import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  BUY_REASON_TEXT,
  buildOptionFulfillmentComparison,
  buildPurchasePlan,
  comparePurchasePlans,
  costToFulfill,
  eligibleOptionsFor,
  previewLineMove,
} from '../../utils/purchasePlanning';
import { convertBasisQuantity, purchaseUnitIdentityForNeed } from '../../utils/packageBasis';
import { expandOptionsAcrossGroup, groupMembersFor } from '../../utils/relatedItemGroups';
import { pluralNoun } from '../../utils/countPresentation';
import { ANSWERED_SOURCES, buyingModePill } from '../../utils/buyingMode';
import { resolveLastUsedOption, lastUsedTitle, PURCHASING_SOURCE_TYPES } from '../../utils/lastUsedOption';
import {
  getShoppingCandidatesForItem,
} from '../../../../supabase/functions/_shared/shoppingCandidates.mjs';
import {
  buildPurchaseScenarioDiscussionPrompt,
  composeDeterministicPurchaseScenarioExplanation,
  unitPriceAmountText,
  validatePurchaseScenarioExplanation,
} from '../../../../supabase/functions/_shared/purchaseScenarioExplanation.mjs';
import { featureEnabled } from '../../config/featureFlags';
import {
  buildVendorExclusionReviewModel,
  directionalComparableText,
  directionalMoneyText,
  directionalQuantityText,
  quantityText as impactQuantityText,
  recommendVendorExclusionScenario,
} from '../../utils/vendorExclusionReview';

/**
 * SavingsReviewWorksheet — the Purchase Planner screen.
 *
 * ONE name, everywhere it is linked to: "Purchase Planner" for the screen,
 * "purchase plan" for one instance of it. It used to answer to Savings
 * Review, Shopping Review, Order Review and Order Plan depending on which
 * button you arrived through, which made four features out of one.
 *
 * The step between "the count says we need these things" and "here are the
 * purchase orders". Not a modal — reviewing the order takes as long as it
 * takes, and this is a worksheet the buyer arranges, leaves, and comes back to.
 *
 * The screen is a live purchasing sandbox with two distinct vendor states.
 * ACTIVE: at least one product in the scenario is assigned to the vendor —
 * only these wear the full order card (aligned subtotal/fee/tax space,
 * projected impact, include/remove control). AVAILABLE: nothing committed,
 * but an approved purchasing relationship could fulfill a line in this plan —
 * these appear only in the quiet Available-vendors strip under the workspace,
 * with their compatible products as evidence, never as an empty $0.00 order
 * card. Participation is decided by product allocation: moving the last item
 * away collapses a card back into the strip, and dropping an item on a tile
 * grows the full card. Between the scenario controls and the cards sits the
 * Counter Intelligence summary — the narrator of the experiment, restating
 * the current scenario in operator words on every change (and describing the
 * untouched plan before any).
 *
 * Below the cards, one list of order items shows what is needed, who it is
 * assigned to, what will be bought and what it costs — the main workspace.
 * Expanding an item reveals its alternate vendor options with the price
 * difference and operational impact of each (pickup stop, fees, vendor
 * minimums), and switching is an inline button — the primary interaction. A
 * sticky summary sidebar keeps the whole plan totals and plain
 * path-to-purchase visible. A group-by-vendor toggle re-exposes the
 * arrangement and keeps drag-and-drop as the advanced gesture, without
 * repeating the scenario controls.
 *
 * A vendor switch is not a re-label: a product bought from Costco is a
 * different package at a different price with a different unit, so on switch
 * the whole row becomes the target vendor's product — description, pack, unit
 * price, line total — and the vendor subtotals, fees and landed total all move
 * with it. Switching to a vendor that cannot supply the product is never
 * offered.
 *
 * Nothing is created while the buyer experiments. The single Done action at
 * the bottom hands the visible scenario to the existing fan-out path, which
 * produces each vendor's PO (delivery) or shopping list (pickup).
 *
 * Props:
 *   plan            purchase_allocations row being reviewed
 *   lines           purchase_allocation_lines for it
 *   optionsByItemId inventory_item_id → purchase_options[]
 *   itemsById       inventory_item_id → inventory_items row (category, name)
 *   vendorsById     vendor id → vendors row (name, fulfillment, minimum, fee)
 *   comparisonByItemId inventory_item_id → stored comparison
 *   draftPoByVendorId vendor id → that finished vendor's unsent draft PO
 *   onCompleteVendor(vendorId, lines) → legacy single-vendor fallback
 *   onCompleteVendors(orders) → creates them all in one pass, asking the
 *                    buying-mode question once for every vendor that owes one
 *   finishInFlight   a finish is under way (question included) — the
 *                    arrangement is frozen until it ends
 *   onReturnDraft(vendorId) → hands that vendor's draft PO back to this sheet
 */

/**
 * A money figure, or the missing placeholder.
 *
 * null and '' are rejected BEFORE coercion. Number(null) is 0, so a price we
 * do not have was rendering as a confident "$0.00" — the cheapest number on
 * the screen, attached to the one option nobody can actually cost. A missing
 * price is unavailable, never free.
 */
const money = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const num = Number(value);
  if (!Number.isFinite(num)) return '—';
  return num.toLocaleString(undefined, { style: 'currency', currency: 'USD' });
};

const signedMoney = (value) => {
  if (value === null || value === undefined || value === '') return 'Unavailable';
  const amount = Number(value);
  if (!Number.isFinite(amount)) return 'Unavailable';
  if (Math.abs(amount) < 0.005) return '$0.00';
  return `${amount > 0 ? '+' : '−'}${money(Math.abs(amount))}`;
};

/**
 * A per-unit price for display. Two-decimal dollars flatten every sub-dime
 * price into an identical "$0.01" — the napkin case, where $0.010625 and
 * $0.0087375 both wore the same figure while the plan claimed a premium.
 * Below a dime the price is shown in tenths of a cent ("1.1¢", "0.9¢");
 * from a dime up it stays ordinary money.
 */
const unitMoney = (value) => {
  const amount = Number(value);
  if (value === null || value === undefined || value === '' || !Number.isFinite(amount)) return money(value);
  if (Math.abs(amount) >= 0.1 || amount === 0) return money(amount);
  const cents = Math.round(Math.abs(amount) * 1000) / 10;
  return `${amount < 0 ? '−' : ''}${cents.toFixed(1)}¢`;
};

const spendChangeText = (value) => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return 'Unavailable';
  const amount = Number(value);
  if (Math.abs(amount) < 0.005) return 'No change';
  return amount < 0 ? `${money(-amount)} less` : `${money(amount)} more`;
};

/**
 * The CASH answer, said as cash: "$3.56 lower", never "save $3.56". A lower
 * purchase total routinely means less product was bought — the word "savings"
 * is reserved for equivalent-quantity price facts (grossPriceImpactText).
 * `value` is scenario − recommended, so negative = the scenario costs less.
 */
const purchaseTotalChangeText = (value) => {
  if (value === null || value === undefined || !Number.isFinite(Number(value))) return 'Unavailable';
  const amount = Number(value);
  if (Math.abs(amount) < 0.005) return 'No change';
  return amount < 0 ? `${money(-amount)} lower` : `${money(amount)} higher`;
};

/**
 * The PRICE answer at equivalent quantities. Positive = genuine gross price
 * savings; negative = a gross price premium. Null = no verified common unit,
 * which is a missing comparison, never zero.
 */
const grossPriceImpactText = (total, complete = true) => {
  if (total === null || total === undefined || !Number.isFinite(Number(total))) return 'Unavailable';
  const amount = Number(total);
  const suffix = complete ? '' : ' · partial';
  if (Math.abs(amount) < 0.005) return `None${suffix}`;
  return amount > 0
    ? `Savings ${money(amount)}${suffix}`
    : `Premium ${money(-amount)}${suffix}`;
};

/** The QUANTITY answer: what changing vendors does to product received. */
const quantityDifferenceText = (comparison) => {
  const changed = Number(comparison?.quantityChangedProductCount) || 0;
  const unknown = Number(comparison?.quantityComparisonUnknownCount) || 0;
  if (changed === 0 && unknown === 0) return 'None';
  // A netted figure is honest only for ONE changed product — across several,
  // opposite-signed changes cancel and "None" would hide real quantity moves.
  if (changed === 1 && unknown === 0
      && comparison?.sharedQuantityUnit && comparison?.quantityDeltaInSharedUnit != null) {
    const delta = Number(comparison.quantityDeltaInSharedUnit);
    if (Math.abs(delta) < 1e-6) return 'None';
    const roundedDelta = Math.round(Math.abs(delta) * 1000) / 1000;
    return `Receives ${roundedDelta} ${comparison.sharedQuantityUnit} ${delta < 0 ? 'less' : 'more'}`;
  }
  const parts = [];
  if (changed > 0) parts.push(`${changed} product${changed === 1 ? '' : 's'} change quantity`);
  if (unknown > 0) parts.push(`${unknown} not comparable`);
  return parts.join(' · ');
};

const countDeltaText = (value, singular, plural = `${singular}s`) => {
  const amount = Number(value) || 0;
  if (!amount) return null;
  const count = Math.abs(amount);
  return `${count} ${amount < 0 ? 'fewer' : 'more'} ${count === 1 ? singular : plural}`;
};

// ONE plural rule for the whole count/purchase vocabulary (utils/
// countPresentation.pluralNoun). This screen and the count card say the same
// nouns out loud — "2 boxes" here and "2 boxes" there — and two copies is how
// one of them ends up saying "boxs". The shared rule also leaves an
// already-plural abbreviation alone, so "pcs" no longer becomes "pcses".
const plural = (n, word) => (word ? pluralNoun(word, n) : word);

const HUMAN_UNITS = Object.freeze({
  PK: 'pack', PKG: 'pack', PACK: 'pack', EA: 'each', EACH: 'each',
  CT: 'count', PC: 'piece', PCS: 'piece', LB: 'lb', LBS: 'lb',
  OZ: 'oz', GAL: 'gal', QT: 'qt', L: 'liter', ML: 'ml',
});

const humanUnit = (unit, quantity = 1) => {
  const raw = String(unit || 'unit').trim();
  const readable = HUMAN_UNITS[raw.toUpperCase()] || raw.toLowerCase();
  if (readable === 'each') return quantity === 1 ? 'each' : 'items';
  if (quantity === 1 || ['count', 'lb', 'oz', 'gal', 'qt', 'ml'].includes(readable)) {
    return readable;
  }
  return plural(quantity, readable);
};

const compactQuantity = (value, unit) => {
  if (value === null || value === undefined || value === '') return null;
  const amount = Number(value);
  if (!Number.isFinite(amount)) return null;
  const rounded = Math.round(amount * 1000) / 1000;
  return `${rounded.toLocaleString()} ${humanUnit(unit, rounded)}`;
};

/**
 * When both vendors' packages hold the same measured quantity, that sameness
 * is the fact that makes a need stated in packages unambiguous across the
 * switch — "1.8 cases" only means one thing when both cases hold 20 lb.
 * Returns "20 lb per case" (falling back to "package" when the two vendors
 * name their package differently), or null when sizes differ or are unknown.
 */
const samePackageSizeText = (change) => {
  const fromPerPkg = Number(change.fromObjectiveQuantityPerPackage);
  const toPerPkg = Number(change.toObjectiveQuantityPerPackage);
  if (!change.comparisonUnit
      || !Number.isFinite(fromPerPkg) || !Number.isFinite(toPerPkg)
      || Math.abs(toPerPkg - fromPerPkg) > 0.0000005) {
    return null;
  }
  const fromNoun = String(change.fromPurchaseUnit || 'package').trim();
  const toNoun = String(change.toPurchaseUnit || 'package').trim();
  const noun = fromNoun.toLowerCase() === toNoun.toLowerCase() ? toNoun : 'package';
  return `${compactQuantity(toPerPkg, change.comparisonUnit)} per ${noun}`;
};

/**
 * Split a purchase-total change into its two honest causes: the unit-price
 * difference (priced across the full delivered quantity, so it always equals
 * the gross price saving/premium stated in the facts below) and the different
 * purchase amount (more or less product, valued at the previous vendor's unit
 * price). Silent unless the two components reconcile with the stated total —
 * a catch-weight billing estimate can put the order cost on another basis,
 * and a split that doesn't add up is worse than none.
 */
const purchaseTotalSplitText = (change, costDelta) => {
  const fromUnit = Number(change.fromComparableUnitCost);
  const fromQty = Number(change.fromObjectiveQuantityReceived);
  const toQty = Number(change.toObjectiveQuantityReceived);
  const gross = Number(change.grossPriceImpact);
  if (change.grossPriceImpact == null
      || ![fromUnit, fromQty, toQty, gross].every(Number.isFinite)) {
    return '';
  }
  const priceEffect = Math.round(-gross * 100) / 100;
  if (Math.abs(priceEffect + (toQty - fromQty) * fromUnit - costDelta) > 0.02) return '';
  const qtyEffect = Math.round((costDelta - priceEffect) * 100) / 100;
  if (Math.abs(priceEffect) < 0.005 && Math.abs(qtyEffect) < 0.005) return '';
  const changeWord = costDelta > 0 ? 'increase' : 'decrease';
  const priceWord = priceEffect > 0 ? 'higher' : 'lower';
  const packSizeDiffers = change.fromObjectiveQuantityPerPackage != null
    && change.toObjectiveQuantityPerPackage != null
    && Math.abs(Number(change.toObjectiveQuantityPerPackage)
      - Number(change.fromObjectiveQuantityPerPackage)) > 0.0000005;
  const amountNoun = `the ${qtyEffect > 0 ? 'larger' : 'smaller'} purchase amount${
    packSizeDiffers ? ' needed for the package size' : ''}`;
  if (Math.abs(qtyEffect) < 0.005) {
    return ` — all of the ${changeWord} is the ${priceWord} unit price`;
  }
  if (Math.abs(priceEffect) < 0.005) {
    return ` — all of the ${changeWord} is ${amountNoun}`;
  }
  if ((priceEffect > 0) === (qtyEffect > 0)) {
    return ` — ${money(Math.abs(priceEffect))} of the ${changeWord} is the ${priceWord} unit price`
      + ` and ${money(Math.abs(qtyEffect))} is ${amountNoun}`;
  }
  // Opposite pulls: name what each side does so the net figure stays checkable.
  const qtyClause = qtyEffect > 0
    ? `${amountNoun} adds ${money(qtyEffect)}`
    : `${amountNoun} takes ${money(-qtyEffect)} off`;
  const priceClause = priceEffect > 0
    ? `the ${priceWord} unit price adds ${money(priceEffect)}`
    : `the ${priceWord} unit price takes ${money(-priceEffect)} off`;
  return ` — ${qtyClause} and ${priceClause}`;
};

/**
 * Explain a moved line without collapsing unlike facts into one signed dollar.
 * All values arrive from comparePurchasePlans; this formatter owns words only.
 * `parInfo` ({ parLevel, countedOnHand, parSource }) is best-effort provenance
 * from the source count — shown when it exists, silently absent otherwise.
 */
const scenarioChangeFacts = (change, parInfo = null) => {
  const facts = [];
  const need = compactQuantity(change.needQuantity, change.needUnit || change.countUnit);
  const samePack = samePackageSizeText(change);
  const parText = parInfo && Number.isFinite(Number(parInfo.parLevel))
    ? compactQuantity(parInfo.parLevel, change.needUnit || change.countUnit)
    : null;
  if (parText && need) {
    const counted = Number.isFinite(Number(parInfo.countedOnHand))
      ? ` and you counted ${compactQuantity(parInfo.countedOnHand, change.needUnit || change.countUnit)}`
      : '';
    const source = parInfo.parSource ? ` (${parInfo.parSource})` : '';
    const packNote = samePack
      ? ` The package size of ${samePack} is the same at both vendors.`
      : '';
    facts.push(`PAR is ${parText}${source}${counted}, so you need ${need} to reach it.${packNote}`);
  } else if (need) {
    facts.push(samePack
      ? `The package size of ${samePack} is the same at both vendors, so the need stays ${need} to reach PAR.`
      : `Need stays ${need} to reach PAR.`);
  }

  if (!change.economicImpactComplete) {
    if (change.reason === 'no_approved_replacement' || change.unassigned) {
      facts.push('No approved replacement currently covers this need.');
    } else if (change.reason === 'price_unavailable') {
      facts.push('A replacement is selected, but its price is unavailable.');
    } else if (change.reason === 'package_quantity_unavailable') {
      facts.push('The replacement package quantity must be confirmed before PAR coverage can be checked.');
    } else if (change.reason === 'below_par') {
      facts.push('The selected replacement does not provide enough quantity to reach PAR.');
    } else {
      facts.push('Fulfillment is unresolved until this item has a safe quantity and price.');
    }
    return facts;
  }

  const costDelta = Number(change.itemCostDelta);
  if (Math.abs(costDelta) < 0.005) {
    facts.push('Purchase total for this item is unchanged.');
  } else {
    const direction = costDelta < 0 ? 'lower' : 'higher';
    facts.push(`Purchase total for this item is ${money(Math.abs(costDelta))} ${direction}${purchaseTotalSplitText(change, costDelta)}.`);
  }

  if (change.comparableUnitCostDelta != null && change.comparisonUnit) {
    const unitDelta = Number(change.comparableUnitCostDelta);
    const estimateLabel = change.comparisonEstimated ? ' (estimated)' : '';
    const perUnit = humanUnit(change.comparisonUnit, 1);
    // Sub-dime deltas in cents language ("about 2/10ths of a cent per
    // napkin") — two-decimal dollars rendered every sub-half-cent change as
    // "increases by $0.00", a claim and its own refutation in one sentence.
    if (Math.abs(unitDelta) < 0.0000005) {
      facts.push(`Unit price is unchanged at ${unitPriceAmountText(change.toComparableUnitCost)} per ${perUnit}${estimateLabel}.`);
    } else {
      facts.push(`Unit price ${unitDelta < 0 ? 'decreases' : 'increases'} by ${unitPriceAmountText(Math.abs(unitDelta))} per ${perUnit}${estimateLabel}.`);
    }
  } else if (change.comparisonUnit
      && change.fromComparableUnitCost != null
      && change.toComparableUnitCost != null
      && !change.unitPriceComparisonTrustworthy) {
    facts.push('Unit-price comparison is estimated below the safe ranking threshold and does not support a winner.');
  }

  const receivedUnit = change.comparisonUnit || change.countUnit;
  const fromReceived = change.comparisonUnit
    ? change.fromObjectiveQuantityReceived : change.fromQuantityReceived;
  const toReceived = change.comparisonUnit
    ? change.toObjectiveQuantityReceived : change.toQuantityReceived;
  const receivedDelta = change.comparisonUnit
    ? change.objectiveQuantityDelta : change.quantityReceivedDelta;
  const fromReceivedText = compactQuantity(fromReceived, receivedUnit);
  const toReceivedText = compactQuantity(toReceived, receivedUnit);
  if (fromReceivedText && toReceivedText && receivedDelta != null) {
    if (Math.abs(Number(receivedDelta)) < 0.0000005) {
      facts.push(`Receives the same ${toReceivedText}${change.comparisonUnit ? ` in the ${change.comparisonEstimated ? 'estimated ' : ''}comparison basis` : ''}.`);
    } else {
      const direction = receivedDelta < 0 ? 'less' : 'more';
      facts.push(`Receives ${toReceivedText} instead of ${fromReceivedText} (${compactQuantity(Math.abs(receivedDelta), receivedUnit)} ${direction}).`);
    }
  }

  // The equivalent-quantity counterfactual — the sentence that separates "we
  // paid less" from "we got a better price". Exists only when both options
  // share a verified comparison unit; a purchase-total difference between
  // unlike quantities makes no price claim here. BOTH sides are priced from
  // unit price × quantity so the three figures always reconcile — toOrderCost
  // can be a catch-weight billing estimate on a different basis.
  const grossImpact = Number(change.grossPriceImpact);
  const equivalentCost = Number(change.equivalentQuantityCostAtPreviousUnitPrice);
  const toUnitCost = Number(change.toComparableUnitCost);
  const toQty = Number(change.toObjectiveQuantityReceived);
  const scenarioCost = Number.isFinite(toUnitCost) && Number.isFinite(toQty)
    ? Math.round(toUnitCost * toQty * 100) / 100
    : NaN;
  if (Number.isFinite(grossImpact) && Number.isFinite(equivalentCost)
      && Number.isFinite(scenarioCost) && toReceivedText && Math.abs(grossImpact) >= 0.005) {
    const fromVendor = change.fromVendorName || 'the previous vendor';
    const toVendor = change.toVendorName || 'the new vendor';
    facts.push(grossImpact > 0
      ? `The same ${toReceivedText} would cost ${money(equivalentCost)} at ${fromVendor}'s unit price versus ${money(scenarioCost)} here — a genuine gross price saving of ${money(grossImpact)} before fees and tax.`
      : `The same ${toReceivedText} would cost ${money(equivalentCost)} at ${fromVendor}'s unit price versus ${money(scenarioCost)} at ${toVendor} — a gross price premium of ${money(-grossImpact)} before fees and tax.`);
  }

  if (change.comparisonUnit
      && change.fromObjectiveQuantityPerPackage != null
      && change.toObjectiveQuantityPerPackage != null
      && Math.abs(change.toObjectiveQuantityPerPackage - change.fromObjectiveQuantityPerPackage) > 0.0000005) {
    facts.push(
      `Package size changes from ${compactQuantity(change.fromObjectiveQuantityPerPackage, change.comparisonUnit)} per ${change.fromPurchaseUnit || 'package'} to ${compactQuantity(change.toObjectiveQuantityPerPackage, change.comparisonUnit)} per ${change.toPurchaseUnit || 'package'}.`,
    );
  }

  if (change.reachesPar === true) {
    const ending = Number(change.endingVsPar);
    const endingText = ending > 0.0000005
      ? `; ${compactQuantity(ending, change.countUnit)} above PAR`
      : '';
    facts.push(`Still reaches PAR${endingText}.`);
  } else if (change.reachesPar === false) {
    const shortage = Number(change.endingVsPar);
    facts.push(shortage < -0.0000005
      ? `${compactQuantity(Math.abs(shortage), change.countUnit)} below PAR.`
      : 'Does not have enough confirmed quantity to prove PAR coverage.');
  }

  return facts;
};

/**
 * A bare "$8.60 higher" beside a "J Italian Food → Prime Meats" route leaves
 * the reader to work out whose number that is. When the caller knows both
 * vendors it gets the plain statement — "Prime Meats is higher than
 * J Italian Food by $8.60" — while compact callers (the row chip) pass no
 * names and keep the short form.
 */
const scenarioChangeHeadline = (change, vendorNames = null) => {
  if (!change.economicImpactComplete || change.itemCostDelta == null) {
    if (change.reason === 'no_approved_replacement' || change.unassigned) {
      return 'Not included in this scenario';
    }
    if (change.reason === 'price_unavailable') return 'Price unavailable';
    if (change.reason === 'package_quantity_unavailable') return 'Package quantity unavailable';
    if (change.reason === 'below_par') return 'Below PAR';
    return 'Incomplete';
  }
  const delta = Number(change.itemCostDelta);
  if (Math.abs(delta) < 0.005) return 'Same purchase total';
  const direction = delta < 0 ? 'lower' : 'higher';
  const fromName = vendorNames?.fromVendorName || null;
  const toName = vendorNames?.toVendorName || null;
  if (fromName && toName) {
    return `${toName} is ${direction} than ${fromName} by ${money(Math.abs(delta))}`;
  }
  return `${money(Math.abs(delta))} ${direction}`;
};

/**
 * Short reason chip for why the assigned vendor was chosen.
 *
 * ONE name per fact. This screen used to say "Best price" and "Best unit
 * price" for the same flag, and "Lowest order cost" and "Lowest cost for this
 * order" for another — three words for two facts, which reads as three
 * competing claims. The two price facts are deliberately different questions
 * and now say which one they are:
 *
 *   Lowest unit price  cheapest per comparable unit (per lb, per fl oz)
 *   Lowest order cost  cheapest way to cover TODAY's need, after packages,
 *                      MOQ and increment rounding
 *
 * 'explicit' has no chip. A row the buyer moved already says so once, in the
 * badge under its name; saying it twice in two colours is not emphasis.
 */
const REASON_CHIP = Object.freeze({
  explicit: null,
  lowest_order_cost: 'Lowest order cost',
  best_price: 'Lowest unit price',
  tied_best_price: 'Lowest unit price',
  best_price_ai_estimate: 'Lowest unit price · pack estimated',
  consolidated: 'Consolidated',
  preferred_fallback: 'Preferred vendor',
  only: 'Only vendor',
  unresolved: 'Needs review',
  substitute_requires_choice: 'Substitute — needs approval',
  no_eligible_option: 'No vendor',
  package_needs_review: 'Package check needed',
});

/**
 * Alternate-source lane vocabulary — Purchase Planner UX, Feature 1.
 *
 * Direction of spend for a lane's mark, said with a glyph so the rail reads
 * at a glance: what would this item's purchase total do at that vendor.
 * "?" is honest ignorance — no comparable price yet, never $0.00.
 */
const SOURCE_DIRECTION_GLYPH = Object.freeze({
  lower: '▼',
  higher: '▲',
  same: '=',
  unknown: '?',
});

/** The mark's full story, for its title/aria-label — words behind the glyph. */
const sourceMarkTitle = (mark) => {
  const facts = [];
  facts.push(mark.costDelta === null
    ? 'no comparable price yet'
    : Math.abs(mark.costDelta) < 0.005
      ? 'same purchase total for this item'
      : `${money(Math.abs(mark.costDelta))} ${mark.costDelta < 0 ? 'lower' : 'higher'} purchase total for this item`);
  if (mark.isSubstitute) facts.push('substitute product');
  if (mark.packState === 'different') facts.push('different pack');
  if (mark.packState === 'unknown' && !mark.isSubstitute) facts.push('pack not compared');
  // Option-scoped evidence, option-scoped claim — never "this vendor was
  // never bought from", which other packs may disprove.
  if (mark.neverBought) facts.push('pack not bought before');
  return `${mark.vendorName}: ${facts.join(' · ')} — open the row to review`;
};

/** One dock row's compact facts line ("+$6.55 on this item · different pack"). */
const dockNoteText = (entry) => {
  const parts = [];
  // With no current assignment to compare against, the lane's own cost is
  // still a real fact — "no comparable price" is only for a genuinely
  // unpriced fulfillment.
  parts.push(entry.costDelta === null
    ? (entry.orderCost != null ? `${money(entry.orderCost)} to buy it here` : 'no comparable price yet')
    : Math.abs(entry.costDelta) < 0.005
      ? 'same purchase total'
      : `${entry.costDelta < 0 ? '−' : '+'}${money(Math.abs(entry.costDelta))} on this item`);
  if (entry.isSubstitute) parts.push('substitute');
  if (entry.packState === 'different') parts.push('different pack');
  if (entry.packState === 'unknown' && !entry.isSubstitute) parts.push('pack not compared');
  // Same scope rule as the rail: the record is about this pack, not the vendor.
  if (entry.neverBought) parts.push('pack not bought before');
  return parts.join(' · ');
};

/**
 * The consequence preview beside a vendor's one include/remove control, as
 * SEPARATED answers: the cash fact (purchase total, neutral), the quantity
 * fact (what is received), and — only when a verified same-unit comparison
 * exists — the price fact. A lower purchase total is never worded or coloured
 * as savings here. Shared by the active vendor card and the excluded
 * available-vendor tile so the two surfaces cannot phrase the same preview
 * differently.
 */
const vendorActionImpactLines = (
  order,
  actionPreview,
  isExcluded,
  exclusionReviewEnabled = false,
) => {
  const subject = `${isExcluded ? 'Including' : exclusionReviewEnabled ? 'Excluding' : 'Removing'} ${order.vendorName}`;
  const newlyBlocked = actionPreview.nextBlockingCount - actionPreview.currentBlockingCount;
  const lines = [];
  if (newlyBlocked > 0) {
    lines.push({ tone: 'warn', text: `${subject} leaves ${newlyBlocked} item${newlyBlocked === 1 ? '' : 's'} needing resolution` });
  } else if (newlyBlocked < 0) {
    const resolved = Math.abs(newlyBlocked);
    lines.push({ tone: 'neutral', text: `${subject} resolves ${resolved} item${resolved === 1 ? '' : 's'}` });
  } else if (actionPreview.changedLineCount === 0) {
    lines.push({ tone: 'neutral', text: `${subject} has no current total impact` });
  } else if (!actionPreview.economicImpactComplete) {
    lines.push({ tone: 'warn', text: `${subject} leaves the cost impact incomplete` });
  } else {
    if (actionPreview.landedDelta < -0.004) {
      lines.push({ tone: 'neutral', text: `${subject} makes the purchase total ${money(-actionPreview.landedDelta)} lower` });
    } else if (actionPreview.landedDelta > 0.004) {
      lines.push({ tone: 'neutral', text: `${subject} makes the purchase total ${money(actionPreview.landedDelta)} higher` });
    } else {
      lines.push({ tone: 'neutral', text: `${subject} keeps the purchase total even` });
    }
    const changedQty = Number(actionPreview.quantityChangedProductCount) || 0;
    const unknownQty = Number(actionPreview.quantityComparisonUnknownCount) || 0;
    // The netted figure is honest only for ONE changed product — see
    // quantityDifferenceText.
    if (changedQty === 1 && unknownQty === 0
        && actionPreview.sharedQuantityUnit && actionPreview.quantityDeltaInSharedUnit != null
        && Math.abs(actionPreview.quantityDeltaInSharedUnit) > 1e-6) {
      const delta = Number(actionPreview.quantityDeltaInSharedUnit);
      const rounded = Math.round(Math.abs(delta) * 1000) / 1000;
      lines.push({ tone: 'neutral', text: `Receives ${rounded} ${actionPreview.sharedQuantityUnit} ${delta < 0 ? 'less' : 'more'}` });
    } else if (changedQty > 0) {
      lines.push({
        tone: 'neutral',
        text: `Changes the quantities purchased for ${changedQty} product${changedQty === 1 ? '' : 's'}${unknownQty > 0 ? ' · some not comparable' : ''}`,
      });
    } else if (unknownQty > 0) {
      lines.push({ tone: 'neutral', text: 'Quantity comparison unavailable for the reassigned products' });
    }
    const gross = actionPreview.grossPriceImpactTotal;
    if (gross != null && Math.abs(gross) >= 0.005) {
      const partial = actionPreview.grossPriceImpactComplete ? '' : ' · partial';
      lines.push(gross > 0
        ? { tone: 'savings', text: `Gross price savings ${money(gross)} at the same quantities${partial}` }
        : { tone: 'warn', text: `Gross price premium ${money(-gross)} at the same quantities${partial}` });
    }
  }
  return lines;
};

/** One vendor-name list, said as prose: "Sysco, US Foods, and Costco". */
const joinVendorNames = (names = []) => {
  if (names.length <= 1) return names[0] || '';
  if (names.length === 2) return `${names[0]} and ${names[1]}`;
  return `${names.slice(0, -1).join(', ')}, and ${names[names.length - 1]}`;
};

/**
 * A vendor order's operational readiness — the one-word answer the sidebar
 * gives for every vendor in the plan.
 */
const vendorReadiness = (order, completedVendorIds) => {
  const vendorKey = order.vendorId == null ? null : String(order.vendorId);
  if (vendorKey != null && completedVendorIds.includes(vendorKey)) {
    return { key: 'finished', label: order.isPickup ? 'Shopping list created' : 'PO created' };
  }
  if ((order.itemCount || 0) === 0) return { key: 'empty', label: 'No items assigned' };
  if (order.vendorLinked === false) return { key: 'unlinked', label: 'Vendor not linked' };
  if (order.feasible === false || order.minimumMet === false) {
    return { key: 'below-minimum', label: 'Below minimum' };
  }
  if (order.isPickup) return { key: 'pickup', label: 'Pickup required' };
  const fees = (Number(order.deliveryFee) || 0) + (Number(order.fuelSurcharge) || 0)
    + (Number(order.belowMinimumFee) || 0);
  if (fees > 0) return { key: 'fee-risk', label: 'Fee risk' };
  return { key: 'ready', label: 'Ready' };
};

/** Strong, always-visible fulfillment badge. Unanswered vendors show nothing. */
const FulfillmentBadge = ({ vendor }) => {
  if (!ANSWERED_SOURCES.includes(vendor?.fulfillment_type_source)) return null;
  const pickup = vendor?.fulfillment_type === 'pickup';
  return (
    <span
      className={pickup ? 'srw-fulfill srw-fulfill-pickup' : 'srw-fulfill srw-fulfill-delivery'}
      data-testid={pickup ? 'savings-review-pickup-badge' : 'savings-review-delivery-badge'}
    >
      {pickup ? 'Pickup' : 'Delivery'}
    </span>
  );
};

/**
 * The repair state for one contradictory vendor product. Its stored facts
 * describe DIFFERENT physical packages (qty_per_package vs the inner-pack
 * structure vs the deterministic comparison), so no order card is shown —
 * a confident "Buy 3 bags · $28.13" built from disputed numbers is exactly
 * the bug this state replaces. The buyer confirms what one order unit
 * contains, and what the stored price applies to; the choices offered are
 * the row's own stored readings, never an invented interpretation, and no
 * divided inner-package price is ever prefilled.
 */
const PackageRepairCard = ({
  line,
  review,
  vendorsById,
  analysis = null,
  onAnalyzePackage,
  onConfirmPackage,
}) => {
  const option = review.option || {};
  const unit = line.countUnit || review.basis?.inventoryUnit || 'unit';
  const purchaseUnit = option.purchase_unit || 'package';
  const vendorName = (option.vendor_id != null && vendorsById[option.vendor_id]?.display_name)
    || option.vendor_name_text || 'Vendor';
  const itemName = line?.description || option.vendor_description || 'this item';
  const hasPackageWording = String(option.pack_description || '').trim().length > 0;

  const storedSemanticRecord = option?.conversion_snapshot?.package_interpretation || {};
  const storedSemantic = storedSemanticRecord.interpretation
    || storedSemanticRecord.semantic_interpretation
    || null;
  const storedSemanticQty = Number(storedSemantic?.quantity_in_item_count_unit);
  const analyzedSemantic = analysis?.interpretation || null;
  const analyzedSemanticQty = Number(analyzedSemantic?.quantity_in_item_count_unit);
  // An on-demand reading is deliberately a suggestion here even when the
  // interpreter could parse it without AI. This row reached the order barrier
  // unresolved, so a person still sees and accepts the plain-language answer.
  const semanticRecord = Number.isFinite(storedSemanticQty) && storedSemanticQty > 0
    ? storedSemanticRecord
    : Number.isFinite(analyzedSemanticQty) && analyzedSemanticQty > 0
      ? {
          state: 'ai_resolved',
          trust_decision: 'confirm_suggestion',
          interpretation: analyzedSemantic,
          price_consistency: analysis?.priceConsistency || null,
        }
      : storedSemanticRecord;
  const semantic = semanticRecord.interpretation || semanticRecord.semantic_interpretation || null;
  const trustDecision = semanticRecord.trust_decision || null;
  const semanticQty = Number(semantic?.quantity_in_item_count_unit);
  const hasSemanticSuggestion = Number.isFinite(semanticQty) && semanticQty > 0;
  const isQuickCheck = trustDecision === 'confirm_suggestion' && hasSemanticSuggestion;
  const priceConsistency = semanticRecord?.price_consistency || {};
  const isConflict = semanticRecord.state === 'conflict'
    || priceConsistency.price_consistency === 'conflict';

  // Candidate contents, each from an actual stored/source reading. Price can
  // challenge one of these candidates, but is never allowed to create one.
  const candidates = [];
  const seen = new Set();
  const pushCandidate = (qty, quantityUnit, source, label = null) => {
    let n = convertBasisQuantity(qty, quantityUnit || unit, unit);
    if (n === null
        && humanUnit(quantityUnit || unit, 1) === humanUnit(unit, 1)) n = Number(qty);
    if (!Number.isFinite(n) || n <= 0) return;
    const key = Math.round(n * 1e6) / 1e6;
    if (seen.has(key)) return;
    seen.add(key);
    candidates.push({
      qty: key,
      source,
      label: label || `1 ${purchaseUnit} = ${key} ${humanUnit(unit, key)}`,
    });
  };
  const purchaseIdentity = purchaseUnitIdentityForNeed(option, unit);
  if (purchaseIdentity?.compatible) {
    pushCandidate(
      purchaseIdentity.quantity,
      purchaseIdentity.unit,
      'the vendor’s stated order unit',
      `1 ${purchaseUnit} = 1 ${humanUnit(unit, 1)}`,
    );
  }
  if (hasSemanticSuggestion) {
    const innerCount = Number(semantic?.inner_pack_count);
    const perInner = Number(semantic?.quantity_per_inner_pack);
    const innerUnit = semantic?.inner_pack_unit || semantic?.item_count_unit || unit;
    const detail = Number.isFinite(innerCount) && innerCount > 0
      ? `1 ${purchaseUnit} = ${innerCount} ${humanUnit(innerUnit, innerCount)}`
        + (Number.isFinite(perInner) && perInner > 0 && semantic?.quantity_unit
          ? `, each ${perInner} ${humanUnit(semantic.quantity_unit, perInner)}` : '')
      : null;
    pushCandidate(
      semanticQty,
      semantic?.item_count_unit || unit,
      'the supplier wording',
      detail,
    );
  }
  const innerCount = Number(option.inner_pack_count);
  const innerSize = Number(option.inner_pack_size);
  if (innerCount > 0 && innerSize > 0) {
    pushCandidate(
      innerCount * innerSize,
      option.inner_pack_size_unit || option.normalized_count_unit || unit,
      'the package structure',
      option.inner_pack_size_unit && convertBasisQuantity(innerCount * innerSize, option.inner_pack_size_unit, unit) !== null
        ? `1 ${purchaseUnit} = ${Math.round(convertBasisQuantity(innerCount * innerSize, option.inner_pack_size_unit, unit) * 1e6) / 1e6} ${humanUnit(unit, convertBasisQuantity(innerCount * innerSize, option.inner_pack_size_unit, unit))}`
        : null,
    );
  }
  const compSnap = option?.conversion_snapshot?.price_comparison;
  if (compSnap && Number(compSnap.quantity_per_purchase_package) > 0
      && (compSnap.source === 'deterministic' || compSnap.source === 'manual_override')) {
    pushCandidate(
      compSnap.quantity_per_purchase_package,
      compSnap.relationship_unit || unit,
      'the pack description',
      null,
    );
  }
  // A legacy qty_per_package=1 EA beside deterministic 50 LB/case evidence is
  // the source of the seven-case bug, not a safe confirmation suggestion.
  const storedCandidateUnsafe = review.warnings?.includes('need_unit_incompatible_with_package_basis')
    && compSnap
    && (compSnap.source === 'deterministic' || compSnap.source === 'manual_override')
    && convertBasisQuantity(compSnap.quantity_per_purchase_package, compSnap.relationship_unit, unit) === null;
  if (!storedCandidateUnsafe) {
    pushCandidate(
      option.qty_per_package,
      option.normalized_count_unit || unit,
      'the stored quantity',
      null,
    );
  }

  const [choice, setChoice] = useState(isQuickCheck ? 0 : null); // index into candidates, or 'custom'
  const [customQty, setCustomQty] = useState('');
  const [price, setPrice] = useState(option.package_price ?? '');
  const [editing, setEditing] = useState(!isQuickCheck);
  // The order-unit WORD is part of the question ("…per case?"), and it can be
  // the thing that's wrong — a price stored against "case" that the vendor
  // really bills per bag. Editable here so the buyer can correct the question
  // itself instead of answering a wrong one.
  const [unitWord, setUnitWord] = useState(String(purchaseUnit || 'package'));
  const [saving, setSaving] = useState(false);

  // Old options may have only the supplier's raw code. Ask the existing
  // server-side interpreter once; while it works, show that Countertop is
  // translating the code instead of flashing the long manual form first.
  useEffect(() => {
    if (hasSemanticSuggestion || !hasPackageWording || analysis?.loading || analysis?.attempted
        || !onAnalyzePackage || option.id == null) return;
    onAnalyzePackage(String(option.id));
  }, [
    analysis?.attempted,
    analysis?.loading,
    hasPackageWording,
    hasSemanticSuggestion,
    onAnalyzePackage,
    option.id,
  ]);

  // A stored suggestion exists on the first render; an on-demand suggestion
  // arrives later. Select the same source-backed candidate in both cases so
  // “Yes, that’s right” always has the exact interpretation to save.
  useEffect(() => {
    if (isQuickCheck && choice == null && candidates.length > 0) {
      setChoice(0);
      setEditing(false);
    }
  }, [candidates.length, choice, isQuickCheck]);

  const effectiveUnitWord = String(unitWord || '').trim() || purchaseUnit;
  const chosenQty = choice === 'custom'
    ? Number(customQty)
    : (choice != null ? candidates[choice]?.qty : null);
  const priceNum = Number(price);
  const priceIsValid = Number.isFinite(priceNum) && priceNum > 0;
  const canSave = Number.isFinite(chosenQty) && chosenQty > 0 && priceIsValid && !saving;

  const save = async () => {
    if (!canSave || !onConfirmPackage) return;
    setSaving(true);
    try {
      await onConfirmPackage(String(option.id), {
        qtyPerPackage: chosenQty,
        packagePrice: priceNum,
        quantityUnit: unit,
        ...(semantic ? { interpretation: semantic } : {}),
        ...(effectiveUnitWord !== purchaseUnit ? { purchaseUnit: effectiveUnitWord } : {}),
      });
    } finally {
      setSaving(false);
    }
  };

  const measuredTotal = Number(semantic?.measured_total_quantity);
  const semanticInnerCount = Number(semantic?.inner_pack_count);
  const semanticPerInner = Number(semantic?.quantity_per_inner_pack);
  const quickReading = hasSemanticSuggestion
    ? (Number.isFinite(semanticInnerCount) && semanticInnerCount > 0
      ? `1 ${purchaseUnit} = ${semanticInnerCount} ${humanUnit(semantic?.inner_pack_unit || semantic?.item_count_unit || unit, semanticInnerCount)}`
        + (Number.isFinite(semanticPerInner) && semanticPerInner > 0 && semantic?.quantity_unit
          ? `, each ${semanticPerInner} ${humanUnit(semantic.quantity_unit, semanticPerInner)}` : '')
      : `1 ${purchaseUnit} = ${semanticQty} ${humanUnit(unit, semanticQty)}`)
    : null;

  const analysisPending = !hasSemanticSuggestion
    && hasPackageWording
    && Boolean(onAnalyzePackage)
    && analysis?.attempted !== true;

  if (analysisPending) {
    return (
      <div className="srw-repair-card srw-repair-card-quick" data-testid="savings-review-repair-card" data-option-id={option.id}>
        <div className="srw-repair-head">
          <span className="srw-badge srw-badge-warn">Reading package details…</span>
          <strong>{vendorName}</strong>
        </div>
        <p className="srw-quick-source">
          {vendorName} lists <strong>{purchaseUnit}</strong>
          {option.pack_description ? <> · <strong>{option.pack_description}</strong></> : null}
          {priceIsValid ? <> · <strong>{money(priceNum)}</strong></> : null}
        </p>
        <p className="srw-quick-reading" data-testid="savings-review-package-reading">
          Turning the supplier&apos;s package code into a plain-language check…
        </p>
      </div>
    );
  }

  // An on-demand suggestion arrives after this component first mounted in
  // manual mode. Treat its not-yet-selected first render as the quick card too
  // so the long form never flashes between “Reading…” and the human sentence.
  if (isQuickCheck && (!editing || choice == null)) {
    return (
      <div className="srw-repair-card srw-repair-card-quick" data-testid="savings-review-repair-card" data-option-id={option.id}>
        <div className="srw-repair-head">
          <span className="srw-badge srw-badge-warn">Quick package check</span>
          <strong>{vendorName}</strong>
        </div>
        <p className="srw-quick-source">
          {vendorName} lists <strong>{purchaseUnit}</strong>
          {option.pack_description ? <> · <strong>{option.pack_description}</strong></> : null}
          {priceIsValid ? <> · <strong>{money(priceNum)}</strong></> : null}
        </p>
        <p className="srw-quick-reading" data-testid="savings-review-package-reading">
          We read that as <strong>{quickReading}</strong>.
        </p>
        {priceIsValid && (
          <p className="srw-repair-preview" data-testid="savings-review-repair-preview">
            {money(priceNum / semanticQty)} per {humanUnit(unit, 1)}
            {Number.isFinite(measuredTotal) && measuredTotal > 0 && semantic?.measured_total_unit
              ? ` · ${money(priceNum / measuredTotal)} per ${humanUnit(semantic.measured_total_unit, 1)}`
              : ''}
          </p>
        )}
        <div className="srw-quick-actions">
          <button type="button" className="srw-btn srw-btn-primary srw-repair-save" disabled={saving} onClick={save}>
            {saving ? 'Saving…' : 'Yes, that’s right'}
          </button>
          <button type="button" className="srw-btn srw-btn-ghost" disabled={saving} onClick={() => setEditing(true)}>
            Edit
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="srw-repair-card" data-testid="savings-review-repair-card" data-option-id={option.id}>
      <div className="srw-repair-head">
        <span className="srw-badge srw-badge-warn">
          {isConflict ? 'Package doesn’t match' : 'We need one detail'}
        </span>
        <strong>{vendorName}</strong>
        {option.vendor_description && <span className="srw-repair-sub">{option.vendor_description}</span>}
      </div>
      <dl className="srw-repair-facts">
        {option.pack_description && (
          <>
            <dt>Vendor description</dt>
            <dd>{option.pack_description}</dd>
          </>
        )}
        <dt>Sold by the</dt>
        <dd>
          <input
            type="text"
            className="srw-calc-input srw-repair-unit-input"
            value={unitWord}
            onChange={(event) => setUnitWord(event.target.value)}
            aria-label="The unit this vendor bills by"
          />
          <em className="srw-repair-unit-hint">edit if the vendor really bills by something else</em>
        </dd>
      </dl>
      <p className="srw-repair-why">
        {isConflict
          ? 'Two source-backed readings disagree. Choose the package the vendor actually sells.'
          : review.warnings?.includes('need_unit_incompatible_with_package_basis')
            ? `This item is needed in ${humanUnit(unit, 2)}, but no verified ${humanUnit(unit, 1)}-per-${purchaseUnit} measurement is stored.`
            : 'Tell us what one vendor package contains.'}
        {review.details?.length
          ? ` — ${review.details[0].message}`
          : '.'}
      </p>
      {priceConsistency.price_consistency === 'conflict'
          && Number.isFinite(Number(priceConsistency.implied_inner_cost))
          && Number.isFinite(Number(priceConsistency.reference_inner_cost)) && (
        <p className="srw-repair-price-warning">
          This reading implies {money(priceConsistency.implied_inner_cost)} per {humanUnit(unit, 1)},
          while independent confirmed history is about {money(priceConsistency.reference_inner_cost)}.
          Price is a warning here—it does not choose the package count.
        </p>
      )}
      <fieldset className="srw-repair-choices">
        {/* The whole question in one sentence — who, what, and what the money
            buys — so the buyer never has to reassemble it from scattered
            facts. The price in the sentence is the live price field below. */}
        <legend data-testid="savings-review-repair-question">
          How many {humanUnit(unit, 2)} are in one {effectiveUnitWord} of {itemName} from {vendorName}
          {priceIsValid ? `, at ${money(priceNum)} per ${effectiveUnitWord}` : ''}?
        </legend>
        {candidates.map((candidate, index) => (
          <label key={candidate.qty}>
            <input
              type="radio"
              name={`srw-repair-${option.id}`}
              checked={choice === index}
              onChange={() => setChoice(index)}
            />
            <span>{candidate.label}</span>
            <em>from {candidate.source}</em>
          </label>
        ))}
        <label>
          <input
            type="radio"
            name={`srw-repair-${option.id}`}
            checked={choice === 'custom'}
            onChange={() => setChoice('custom')}
          />
          <span className="srw-repair-custom">
            A different amount:{' '}
            <input
              type="number"
              min="0"
              step="any"
              className="srw-calc-input"
              value={customQty}
              onFocus={() => setChoice('custom')}
              onChange={(event) => setCustomQty(event.target.value)}
              aria-label={`Quantity of ${unit} in one ${effectiveUnitWord}`}
            />{' '}
            {humanUnit(unit, 2)} per {effectiveUnitWord}
          </span>
        </label>
      </fieldset>
      <div className="srw-repair-price">
        <label>
          The price for ONE {effectiveUnitWord}, as the vendor bills it:{' '}
          <input
            type="number"
            min="0"
            step="any"
            className="srw-calc-input"
            value={price}
            onChange={(event) => setPrice(event.target.value)}
            aria-label={`Price for one ${effectiveUnitWord}`}
          />
        </label>
        <span className="srw-calc-note">
          Currently stored: {money(option.package_price)}. If that figure was really for a
          different package (a whole {effectiveUnitWord}, a single {unit}), correct it here.
        </span>
      </div>
      {/* The consequence, before commitment: what the answer makes one count
          unit cost — the number the buyer compares against the other vendors'
          options before confirming. */}
      {Number.isFinite(chosenQty) && chosenQty > 0 && priceIsValid && (
        <p className="srw-repair-preview" data-testid="savings-review-repair-preview">
          {money(priceNum)} ÷ {chosenQty} {humanUnit(unit, chosenQty)} ={' '}
          <strong>{money(priceNum / chosenQty)} per {humanUnit(unit, 1)}</strong> — check this against the
          other vendors&apos; unit prices before confirming.
        </p>
      )}
      <button
        type="button"
        className="srw-btn srw-btn-ghost srw-repair-save"
        disabled={!canSave}
        onClick={save}
      >
        {saving ? 'Saving…' : 'Confirm package'}
      </button>
    </div>
  );
};

/**
 * The full arithmetic for one vendor option of one line — the old
 * back-of-card comparison, now the "Show full comparison" expansion. Every number
 * is stated, and the package quantity stays correctable in place.
 */
const OptionDetailPanel = ({
  opt,
  line,
  onConfirmPackage,
  onEditOrderingRules,
  locked,
}) => (
  <div className="srw-calc" data-testid="savings-review-calc">
    <div className="srw-calc-head">
      <strong>Price comparison — {opt.vendorName}</strong>
      <span>Need: {line.quantityNeeded} {line.countUnit || 'each'} · from PAR and the completed count</span>
    </div>
    {opt.isSelected && opt.selectionReasonText && (
      <div className="srw-calc-note" data-testid="savings-review-reason">{opt.selectionReasonText}</div>
    )}
    {opt.isSelected && opt.comparisonEstimated && (
      <div className="srw-calc-note" data-testid="savings-review-ai-estimate-note">
        The selected option&apos;s pack size is AI-estimated{Number.isFinite(Number(opt.comparisonConfidence)) ? ` (confidence ${Math.round(Number(opt.comparisonConfidence) * 100)}%)` : ''} — the comparable price is a best reading of the vendor&apos;s description, not a printed figure.
      </div>
    )}
    <div className="srw-calc-row"><span>Need</span><span>{opt.requiredQuantity ?? '—'} {opt.requiredUnit}</span></div>
    {/* The item's own replenishment target, only when it actually moved the
        goalpost past the need. */}
    {opt.purchaseTarget != null && opt.purchaseTarget !== opt.requiredQuantity && (
      <div className="srw-calc-row" data-testid="savings-review-target"><span>Replenish at least</span><span>{opt.purchaseTarget} {opt.requiredUnit}</span></div>
    )}
    <div className="srw-calc-row"><span>Buy</span><span>{opt.packagesRequired ?? '—'} {plural(opt.packagesRequired ?? 1, opt.purchaseUnit)}{
      opt.orderDriver === 'vendor_moq'
        ? ` (package minimum: ${opt.optionMoqPackages})`
        : opt.buyReasonText ? ` (${opt.buyReasonText})` : ''
    }</span></div>
    <div className="srw-calc-row" data-testid="savings-review-minimum"><span>Minimum</span><span>{
      opt.optionMoqPackages > 0
        ? `${opt.optionMoqPackages} ${plural(opt.optionMoqPackages, opt.purchaseUnit)}`
        : 'No stated minimum'
    }</span></div>
    <div className="srw-calc-row" data-testid="savings-review-increment"><span>Ordered in</span><span>{
      opt.orderIncrement != null && opt.orderIncrement !== 1
        ? `${opt.orderIncrement} ${plural(opt.orderIncrement, opt.purchaseUnit)} steps`
        : opt.allowsFractionalOrder && opt.orderIncrement == null
          ? 'Exact quantity (cut to order)'
          : `Whole ${opt.purchaseUnit}s`
    }</span></div>
    <div className="srw-calc-row"><span>Receive</span><span>{opt.quantityReceived ?? '—'} {opt.requiredUnit}</span></div>
    {/* Every vendor answers the SAME need above. What differs is how much of
        it you have to take — so the row names the target it overshoots,
        rather than a bare "Extra" that never said extra to WHAT. */}
    <div className="srw-calc-row"><span>Above PAR</span><span>{opt.overage ? `${opt.overage} ${opt.requiredUnit}` : 'None — exactly reaches PAR'}</span></div>
    <div className="srw-calc-row"><span>Price</span><span>{money(opt.packagePrice)} per {opt.purchaseUnit}</span></div>
    <div className="srw-calc-row srw-calc-strong"><span>Item-order total</span><span>{money(opt.itemOrderTotal)}</span></div>
    {opt.comparableUnitCost != null && opt.comparisonUnit && (
      <div className="srw-calc-row"><span>Comparable price</span><span data-testid="savings-review-comparable">{opt.comparisonEstimated ? '~' : ''}{unitMoney(opt.comparableUnitCost)}/{opt.comparisonUnit}{opt.comparisonEstimated ? ' estimated' : ''}</span></div>
    )}
    {opt.isSubstitute && opt.needsComparisonReview && (
      <div className="srw-calc-note" data-testid="savings-review-substitute-unresolved">
        Substitute — price comparison needs quantity confirmation.
      </div>
    )}
    <div className="srw-opt-facts">
      {opt.isLowestOrderCost && <span>Lowest order cost</span>}
      {opt.isBestUnitPrice && <span>Lowest unit price</span>}
      {opt.isSubstitute && opt.isLowestShoppingUnitPrice && <span>Lowest unit price of any candidate · Substitute</span>}
      {opt.isSubstitute && opt.substituteSavingsPerUnit != null && (
        <span>Substitute · Save {money(opt.substituteSavingsPerUnit)}/{opt.comparisonUnit} vs best same-item choice</span>
      )}
      {!opt.isSelected && opt.itemCostDifferenceFromSelected != null && opt.itemCostDifferenceFromSelected !== 0 && (
        <span>{opt.itemCostDifferenceFromSelected > 0 ? `Purchase total ${money(opt.itemCostDifferenceFromSelected)} higher today` : `Purchase total ${money(-opt.itemCostDifferenceFromSelected)} lower today`}</span>
      )}
      {!opt.isSelected && opt.grossPriceDifferenceFromSelected != null
        && Math.abs(opt.grossPriceDifferenceFromSelected) > 0.004 && (
        <span
          data-testid="savings-review-gross-price"
          className={opt.grossPriceDifferenceFromSelected > 0 ? 'srw-note-ok' : 'srw-note-warn'}
        >
          {opt.grossPriceDifferenceFromSelected > 0
            ? `Gross price savings ${money(opt.grossPriceDifferenceFromSelected)} at the same quantity`
            : `Gross price premium ${money(-opt.grossPriceDifferenceFromSelected)} at the same quantity`}
        </span>
      )}
      {!opt.isSelected && opt.landedOrderDifferenceIfSelected != null && opt.landedOrderDifferenceIfSelected !== opt.itemCostDifferenceFromSelected && (
        <span data-testid="savings-review-order-effect">Whole order {opt.landedOrderDifferenceIfSelected > 0 ? `+${money(opt.landedOrderDifferenceIfSelected)}` : `−${money(-opt.landedOrderDifferenceIfSelected)}`}{opt.feeDifferenceIfSelected ? ` (fees ${opt.feeDifferenceIfSelected > 0 ? '+' : '−'}${money(Math.abs(opt.feeDifferenceIfSelected))})` : ''}</span>
      )}
    </div>
    {onEditOrderingRules && !locked && (
      <button
        type="button"
        className="srw-review-btn"
        data-testid="savings-review-edit-ordering-rules"
        onClick={() => onEditOrderingRules(opt.optionId)}
      >
        Edit ordering rules
      </button>
    )}
    {onConfirmPackage && (
      <details className="srw-pkg-review">
        <summary>Review package calculation</summary>
        <label>
          One {opt.purchaseUnit} holds
          <input
            className="srw-calc-input"
            type="number" min="0" step="any"
            defaultValue={opt.inventoryQuantityPerPackage ?? ''}
            data-testid="savings-review-qty-input"
            onBlur={(e) => {
              const v = Number(e.target.value);
              if (Number.isFinite(v) && v > 0 && v !== Number(opt.inventoryQuantityPerPackage)) {
                onConfirmPackage(opt.optionId, { qtyPerPackage: v });
              }
            }}
          /> {opt.requiredUnit}
        </label>
      </details>
    )}
  </div>
);

const SavingsReviewWorksheet = ({
  plan = null,
  lines = [],
  optionsByItemId = {},
  itemsById = {},
  vendorsById = {},
  comparisonByItemId = {},
  packageAnalysisByOptionId = {},
  completedVendorIds = [],
  draftPoByVendorId = {},
  onCompleteVendor,
  onCompleteVendors,
  finishInFlight = false,
  orderUseByOptionId = {},
  onReturnDraft,
  onReopenDraftAndMove,
  onLineMoved,
  onAnalyzePackage,
  onConfirmPackage,
  onEditOrderingRules,
  onViewOrders,
  onExplainScenario,
  // inventory_item_id → { parLevel, countedOnHand, parSource } from the source
  // count, best effort. Only shown when it agrees with the frozen need.
  parContextByItemId = {},
  busyVendorId = null,
  highlightedLineIds = [],
  workingSetGroups = null,
  shoppingCandidateGraph = null,
  canonicalItemIdByGroupId = {},
  featureFlags = null,
}) => {
  const vendorExclusionReviewEnabled = featureEnabled('vendor_exclusion_review', featureFlags);
  const nextOrderItemsEnabled = featureEnabled('next_order_items', featureFlags);
  // ── Where a line is bought: ONE authority, with an explicit lifecycle ──────
  //
  // The worksheet used to keep an `overrides` map as a rival source of truth.
  // That is how a production review showed Restaurant Depot holding six items
  // and $475 while the shopping list came out with four and $335: the two
  // moves had been REFUSED by the database, the container reloaded the real
  // lines, and nothing could clear the child's override — so the cards stayed
  // where the buyer had dropped them, over the top of the truth.
  //
  // Now there is no rival truth. A line's option is, in order:
  //   1. `pending[lineId]`  — a switch in flight. Optimistic on purpose, and
  //      loudly provisional: the row says "Saving…" and Finish is shut.
  //   2. `confirmed[lineId]` — an option the SERVER answered with, held only
  //      until the `lines` prop catches up (then dropped, below). This is a
  //      cache of the server's own reply, never an independent opinion.
  //   3. `line.selected_purchase_option_id` — persisted truth.
  // A failed write clears 1 and 2, so the row falls back to truth by itself.
  const [pendingMoves, setPendingMoves] = useState({});
  const [confirmedMoves, setConfirmedMoves] = useState({});
  const [moveErrors, setMoveErrors] = useState({});
  // Per-line write queue. Two switches of the same product must not race each
  // other to the database: whichever REPLY arrives last would otherwise decide
  // where the product is bought. Each line's writes run strictly in order, and
  // only the newest intent is ever sent.
  const moveChains = useRef(new Map());
  const moveSeq = useRef(0);
  const latestIntent = useRef(new Map());
  const [dragging, setDragging] = useState(null);
  const [hoverVendor, setHoverVendor] = useState(null);
  const [rejected, setRejected] = useState(null);
  // "Sysco already has a draft PO — reopen it and move this there?"
  const [reopenPrompt, setReopenPrompt] = useState(null);
  const [reopenBusy, setReopenBusy] = useState(false);
  const [justLanded, setJustLanded] = useState(null);
  // Lines the buyer just switched, newest first. A switched row lands at the
  // TOP of the list and pulses so the eye finds it — by the time a switch
  // lands you have forgotten where it went.
  const [promotedLineIds, setPromotedLineIds] = useState([]);
  // Rows opened to their alternate vendor options; option ids whose full
  // arithmetic ("Show full comparison") is showing. Repair rows open on their own —
  // an item that cannot be ordered is not something to discover by clicking.
  const [expandedLineIds, setExpandedLineIds] = useState({});
  const [detailsOptionKeys, setDetailsOptionKeys] = useState({});
  // The Done prompt: up while the buyer is asked whether to create the orders
  // for the vendors still unfinished, with its own busy flag for the bulk run.
  const [donePromptOpen, setDonePromptOpen] = useState(false);
  const [doneBusy, setDoneBusy] = useState(false);
  // Toolbar: search, the pickup filter, the grouping toggle, and "changes you
  // made". All of them narrow the item list; the summary always shows the
  // WHOLE plan, so a filtered list never reads as a smaller order.
  const [searchTerm, setSearchTerm] = useState('');
  const [filterPickup, setFilterPickup] = useState(false);
  const [showChangesOnly, setShowChangesOnly] = useState(false);
  // Vendor view is the DEFAULT: the worksheet opens on the arrangement the
  // buyer will actually order — one section per vendor. Switching views never
  // changes an allocation or a total; it only regroups the same plan.
  const [groupMode, setGroupMode] = useState('vendor'); // 'vendor' | 'item'
  // Which vendor cards have their dock ("N items could land here") expanded.
  // Exploration state only — opening a dock commits nothing.
  const [openDockVendorIds, setOpenDockVendorIds] = useState({});
  // Scenario constraints are local to this purchase plan. They filter the
  // same purchase-option pool the planner already owns; they never edit a
  // vendor or manufacture an alternative. The allowance map is the explicit
  // "keep this vendor for this one impossible item" exception.
  const [avoidedVendorIds, setAvoidedVendorIds] = useState([]);
  const [allowedExcludedVendorIdsByLineId, setAllowedExcludedVendorIdsByLineId] = useState({});
  // Draft-only two-step exclusion. Nothing here enters the active plan until
  // Apply changes copies its constraints and accepted options into the normal
  // arrangement state.
  const [exclusionReview, setExclusionReview] = useState(null);
  const [undoStack, setUndoStack] = useState([]);
  const [whyLineIds, setWhyLineIds] = useState({});
  const [scenarioNarrative, setScenarioNarrative] = useState({
    status: 'idle', explanation: null, source: 'calculated', model: null,
  });
  const scenarioNarrativeRequest = useRef(0);
  const landTimer = useRef(null);

  useEffect(() => () => { if (landTimer.current) clearTimeout(landTimer.current); }, []);

  const plannedLines = useMemo(() => lines.map((line) => ({
    id: line.id,
    inventory_item_id: line.inventory_item_id,
    quantity_needed: line.quantity_needed,
    count_unit: line.count_unit,
    description: itemsById[line.inventory_item_id]?.description || line.description || `Item ${line.inventory_item_id}`,
    selected_purchase_option_id: line.selected_purchase_option_id,
    selection_reason: line.selection_reason ?? null,
    recommended_purchase_option_id: line.recommended_purchase_option_id ?? null,
    recommended_selection_reason: line.recommended_selection_reason ?? null,
  })), [lines, itemsById]);

  /**
   * Freeze the recommendation that opened this plan.
   *
   * New rows carry durable recommended_* columns. For a store still behind
   * that migration, capture the first selected option we see and never replace
   * it when a scenario write reloads the mutable selected_* columns. This is
   * the line between "Countertop recommended" and "what I am trying now".
   */
  const recommendedBaselineRef = useRef({ planId: null, byLineId: {} });
  const planIdentity = String(plan?.id ?? '__preview__');
  if (recommendedBaselineRef.current.planId !== planIdentity) {
    recommendedBaselineRef.current = { planId: planIdentity, byLineId: {} };
  }
  plannedLines.forEach((line) => {
    const key = String(line.id);
    if (recommendedBaselineRef.current.byLineId[key]) return;
    recommendedBaselineRef.current.byLineId[key] = {
      optionId: line.recommended_purchase_option_id
        ?? line.selected_purchase_option_id
        ?? null,
      reason: line.recommended_selection_reason
        ?? line.selection_reason
        ?? null,
    };
  });
  const recommendedBaselineByLineId = recommendedBaselineRef.current.byLineId;

  useEffect(() => {
    setAvoidedVendorIds([]);
    setAllowedExcludedVendorIdsByLineId({});
    setExclusionReview(null);
    setUndoStack([]);
    setWhyLineIds({});
    setOpenDockVendorIds({});
  }, [planIdentity]);

  /**
   * The one selection map every render, every preview and every Finish reads.
   * Pending beats confirmed beats persisted — see the state declaration above.
   */
  const selectionByLineId = useMemo(
    () => ({ ...confirmedMoves, ...pendingMoves }),
    [confirmedMoves, pendingMoves],
  );

  const hasPendingMoves = Object.keys(pendingMoves).length > 0;

  /**
   * THE ARRANGEMENT IS FROZEN while a finish is running.
   *
   * `hasPendingMoves` shuts the door on the way in, but it can only be read at
   * the moment Finish is pressed. Everything after that — the buying-mode
   * question, each vendor's preflight, each reload — happens with this sheet
   * mounted and clickable. A switch started in that window is written by a
   * different code path than the one being finished, so the vendor currently
   * fanning out can preflight clean against a database that has not seen the
   * move yet and email a PO containing the line the screen shows as already
   * gone. Freezing the moves is the only place that can be prevented.
   */
  const arrangementFrozen = finishInFlight || !!busyVendorId;
  // Read by the callbacks below, which must see the freeze as it is NOW rather
  // than as it was when their closure was built.
  const arrangementFrozenRef = useRef(arrangementFrozen);
  arrangementFrozenRef.current = arrangementFrozen;

  // A server answer is only a cache. The moment the authoritative `lines` prop
  // reports the same option, the cache has nothing left to say and is dropped —
  // which is what keeps it from ever becoming a second opinion.
  useEffect(() => {
    setConfirmedMoves((prev) => {
      const keys = Object.keys(prev);
      if (!keys.length) return prev;
      let changed = false;
      const next = { ...prev };
      keys.forEach((lineId) => {
        const line = lines.find((l) => String(l.id) === String(lineId));
        if (line && String(line.selected_purchase_option_id) === String(prev[lineId])) {
          delete next[lineId];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [lines]);

  const planArgs = useMemo(() => ({
    lines: plannedLines,
    optionsByItemId,
    comparisonByItemId,
    vendorsById,
    itemsById,
    storeId: plan?.store_id ?? null,
    groups: workingSetGroups,
    shoppingCandidateGraph,
    canonicalItemIdByGroupId,
  }), [
    plannedLines, optionsByItemId, comparisonByItemId, vendorsById, itemsById,
    plan?.store_id, workingSetGroups, shoppingCandidateGraph, canonicalItemIdByGroupId,
  ]);

  const unconstrainedPlan = useMemo(() => buildPurchasePlan({
    ...planArgs,
    explicitByLineId: selectionByLineId,
  }), [planArgs, selectionByLineId]);

  const plan_ = useMemo(() => buildPurchasePlan({
    ...planArgs,
    explicitByLineId: selectionByLineId,
    excludedVendorIds: avoidedVendorIds,
    allowedExcludedVendorIdsByLineId,
  }), [
    planArgs, selectionByLineId, avoidedVendorIds,
    allowedExcludedVendorIdsByLineId,
  ]);

  const exclusionDraft = useMemo(() => {
    if (!vendorExclusionReviewEnabled || exclusionReview?.step !== 'review') return null;
    const vendorId = String(exclusionReview.vendorId);
    const excludedVendorIds = [...new Set([...avoidedVendorIds.map(String), vendorId])];
    const allowed = {};
    Object.entries(allowedExcludedVendorIdsByLineId).forEach(([lineId, ids]) => {
      const kept = (ids || []).map(String).filter((id) => id !== vendorId);
      if (kept.length) allowed[lineId] = kept;
    });
    (exclusionReview.keptLineIds || []).forEach((lineId) => {
      allowed[String(lineId)] = [...new Set([...(allowed[String(lineId)] || []), vendorId])];
    });
    const proposedPlan = buildPurchasePlan({
      ...planArgs,
      explicitByLineId: {
        ...selectionByLineId,
        ...(exclusionReview.proposedOptionByLineId || {}),
      },
      excludedVendorIds,
      allowedExcludedVendorIdsByLineId: allowed,
    });
    return {
      vendorId,
      excludedVendorIds,
      allowedExcludedVendorIdsByLineId: allowed,
      proposedPlan,
      model: buildVendorExclusionReviewModel({
        currentPlan: plan_,
        proposedPlan,
        vendorId,
        vendorsById,
      }),
    };
  }, [
    vendorExclusionReviewEnabled, exclusionReview, avoidedVendorIds,
    allowedExcludedVendorIdsByLineId, planArgs, selectionByLineId, plan_, vendorsById,
  ]);

  const exclusionApprovedLineIdSet = useMemo(() => new Set(
    (exclusionReview?.approvedLineIds || []).map(String),
  ), [exclusionReview?.approvedLineIds]);
  const exclusionPendingReviewCount = exclusionDraft?.model?.items.filter(
    (item) => !exclusionApprovedLineIdSet.has(String(item.lineId)),
  ).length || 0;

  useEffect(() => {
    if (!exclusionReview) return undefined;
    const closeOnEscape = (event) => {
      if (event.key === 'Escape') setExclusionReview(null);
    };
    document.addEventListener('keydown', closeOnEscape);
    return () => document.removeEventListener('keydown', closeOnEscape);
  }, [exclusionReview]);

  const shoppingCandidatesByItemId = useMemo(() => {
    const result = {};
    plannedLines.forEach((line) => {
      result[String(line.inventory_item_id)] = shoppingCandidateGraph
        ? getShoppingCandidatesForItem(line.inventory_item_id, {
          graph: shoppingCandidateGraph,
          itemsById,
          optionsByItemId,
          canonicalItemIdByGroupId,
        })
        : null;
    });
    return result;
  }, [
    plannedLines, shoppingCandidateGraph, itemsById, optionsByItemId,
    canonicalItemIdByGroupId,
  ]);

  // The alternatives list, drag targets and planner all consume this SAME
  // pool. It is Equivalent-only when no shopping graph is available; otherwise
  // it also contains one-hop confirmed substitute closures, explicitly
  // role-labelled by shoppingCandidatesByItemId.
  const pooledOptionsByItemId = useMemo(
    () => {
      const equivalentPools = expandOptionsAcrossGroup(optionsByItemId, workingSetGroups);
      const result = { ...equivalentPools };
      Object.entries(shoppingCandidatesByItemId).forEach(([itemId, shopping]) => {
        if (shopping) result[itemId] = shopping.purchaseOptions;
      });
      return result;
    },
    [optionsByItemId, workingSetGroups, shoppingCandidatesByItemId],
  );

  /**
   * A quantity in the allocation line's RAW count unit. The worksheet used
   * to divide this through one operational package anchor, even when the row
   * was ordering a different vendor's package. That made a 1.9-can shortage
   * read as 0.079 of a 24-can case beside a selected 12-can case. The package
   * conversion remains part of the order math; this label deliberately does
   * not perform a second conversion.
   */
  const quantityText = useCallback((line, quantity) => {
    const value = Number(quantity);
    if (!Number.isFinite(value)) return null;
    const unit = line?.countUnit || line?.count_unit || 'each';
    const rounded = Math.round(value * 1000) / 1000;
    return `${rounded} ${humanUnit(unit, rounded)}`;
  }, []);

  const candidateContextFor = useCallback((itemId) => {
    const shopping = shoppingCandidatesByItemId[String(itemId)];
    if (!shopping) {
      return {
        groupMembers: groupMembersFor(workingSetGroups, itemId),
        candidateItemIds: null,
        optionRoleById: {},
      };
    }
    return {
      groupMembers: groupMembersFor(workingSetGroups, itemId),
      candidateItemIds: new Set([
        ...(shopping.primaryGroup?.itemIds || []),
        ...shopping.substituteGroups.flatMap((group) => group.itemIds),
      ]),
      optionRoleById: shopping.optionRoleById,
    };
  }, [shoppingCandidatesByItemId, workingSetGroups]);

  /**
   * The immutable recommendation beside the mutable scenario. Explicit ids
   * are intentional here: we are pricing the stored baseline through today's
   * authoritative package/economics functions, not asking a different ranker
   * to invent a new historical recommendation.
   */
  const recommendedByLineId = useMemo(() => Object.fromEntries(
    Object.entries(recommendedBaselineByLineId)
      .filter(([, entry]) => entry.optionId != null)
      .map(([lineId, entry]) => [lineId, String(entry.optionId)]),
  // plannedLines is intentional: recommendedBaselineByLineId is a stable ref
  // whose entries are filled as lines arrive from the plan load.
  ), [recommendedBaselineByLineId, plannedLines]);

  const recommendedPlan = useMemo(() => {
    const bare = plannedLines.map((line) => ({ ...line, selected_purchase_option_id: null }));
    const built = buildPurchasePlan({
      ...planArgs,
      lines: bare,
      explicitByLineId: recommendedByLineId,
    });
    return {
      ...built,
      lines: built.lines.map((line) => ({
        ...line,
        selectionReason: recommendedBaselineByLineId[String(line.lineId)]?.reason
          ?? line.selectionReason,
      })),
    };
  }, [plannedLines, planArgs, recommendedByLineId, recommendedBaselineByLineId]);

  const scenarioComparison = useMemo(
    () => comparePurchasePlans(recommendedPlan, plan_),
    [recommendedPlan, plan_],
  );
  // Financial incompleteness is deliberately separate from order blocking.
  // A known-price gap must suppress savings claims, but an otherwise legal
  // price-unavailable order keeps the existing downstream ordering contract.
  const scenarioFinancialIncomplete = !scenarioComparison.economicImpactComplete;
  const avoidedVendorNames = useMemo(() => avoidedVendorIds.map(
    (id) => vendorsById[id]?.display_name || 'Vendor',
  ), [avoidedVendorIds, vendorsById]);
  const scenarioName = avoidedVendorNames.length > 0
    ? `Without ${avoidedVendorNames.join(' and ')}`
    : 'Current vendor changes';
  const scenarioExplanationFacts = useMemo(() => {
    const vendorNames = (economicPlan) => [...new Set(
      (economicPlan.vendorOrders || [])
        .filter((order) => (order.itemCount || 0) > 0)
        .map((order) => order.vendorName)
        .filter(Boolean),
    )];
    return {
      scenarioName,
      excludedVendors: avoidedVendorNames,
      coverageStatus: scenarioComparison.coverageStatus,
      coveredProductCount: scenarioComparison.coveredProductCount,
      requiredProductCount: scenarioComparison.requiredProductCount,
      coveredProductSpendDelta: scenarioComparison.coveredProductSpendDelta,
      missingBaselineSpend: scenarioComparison.missingBaselineSpend,
      overallSavings: scenarioComparison.overallSavings,
      purchaseTotalDelta: scenarioComparison.landedDelta,
      quantityChangedProductCount: scenarioComparison.quantityChangedProductCount,
      grossPriceImpactTotal: scenarioComparison.grossPriceImpactTotal,
      grossPriceImpactComplete: scenarioComparison.grossPriceImpactComplete,
      comparableScenarioTotal: scenarioComparison.comparableScenarioTotal,
      partialAssignedSubtotal: scenarioComparison.partialAssignedSubtotal,
      landedTotalStatus: scenarioComparison.landedTotalStatus,
      recommendedArrangement: {
        vendors: vendorNames(scenarioComparison.recommended),
        vendorCount: scenarioComparison.recommended.summary?.vendorOrderCount || 0,
        pickupStops: scenarioComparison.recommendedPickupStops,
        landedTotal: scenarioComparison.recommended.landed?.landedTotal ?? null,
      },
      scenarioArrangement: {
        vendors: vendorNames(scenarioComparison.scenario),
        vendorCount: scenarioComparison.scenario.summary?.vendorOrderCount || 0,
        pickupStops: scenarioComparison.scenarioPickupStops,
        landedTotal: scenarioComparison.comparableScenarioTotal,
      },
      changes: scenarioComparison.changedLines.map((change) => ({
        ...change,
        product: change.description || 'Item',
        previousVendor: change.fromVendorName
          || vendorsById[change.fromVendorId]?.display_name
          || 'Unassigned',
        scenarioVendor: change.toVendorName
          || vendorsById[change.toVendorId]?.display_name
          || null,
        previousOrderCost: change.fromOrderCost,
        scenarioOrderCost: change.toOrderCost,
        ...(parContextByItemId[String(change.inventoryItemId)] || {}),
      })),
      unresolvedItems: scenarioComparison.unresolvedItems,
    };
  }, [scenarioComparison, scenarioName, avoidedVendorNames, vendorsById, parContextByItemId]);
  const deterministicScenarioExplanation = useMemo(
    () => composeDeterministicPurchaseScenarioExplanation(scenarioExplanationFacts),
    [scenarioExplanationFacts],
  );
  // The economics objects are rebuilt from immutable facts. Key the async
  // effect by their serialized content so a prose-state render can never
  // retrigger the request merely because an equivalent object got a new ref.
  const scenarioExplanationKey = JSON.stringify(scenarioExplanationFacts);

  useEffect(() => {
    const requestId = scenarioNarrativeRequest.current + 1;
    scenarioNarrativeRequest.current = requestId;
    if (scenarioComparison.changedLineCount === 0) {
      setScenarioNarrative((previous) => (previous.status === 'idle'
        && previous.explanation === null
        ? previous
        : { status: 'idle', explanation: null, source: 'calculated', model: null }));
      return undefined;
    }
    if (typeof onExplainScenario !== 'function') {
      setScenarioNarrative((previous) => (previous.status === 'ready'
        && previous.explanation === null
        && previous.source === 'calculated'
        ? previous
        : { status: 'ready', explanation: null, source: 'calculated', model: null }));
      return undefined;
    }

    // Keep an already-displayed explanation on screen while the refreshed one
    // loads (facts can re-key when PAR provenance arrives moments after a
    // change) — the meta line shows the refresh, the prose does not flash away.
    setScenarioNarrative((previous) => ({
      status: 'loading',
      explanation: previous.status === 'ready' ? previous.explanation : null,
      source: previous.status === 'ready' ? previous.source : 'calculated',
      model: previous.status === 'ready' ? previous.model : null,
    }));
    let active = true;
    // Let rapid vendor-toggle experiments settle before spending an API call.
    const timer = setTimeout(() => {
      Promise.resolve(onExplainScenario(scenarioExplanationFacts))
        .then((response) => {
          if (!active || scenarioNarrativeRequest.current !== requestId) return;
          const explanation = validatePurchaseScenarioExplanation(
            response?.explanation,
            scenarioExplanationFacts,
          );
          setScenarioNarrative({
            status: 'ready',
            explanation,
            source: explanation && response?.source === 'ai' ? 'ai' : 'calculated',
            model: explanation && response?.source === 'ai' ? response?.model || null : null,
          });
        })
        .catch(() => {
          if (!active || scenarioNarrativeRequest.current !== requestId) return;
          setScenarioNarrative({ status: 'ready', explanation: null, source: 'calculated', model: null });
        });
    }, 250);
    return () => {
      active = false;
      clearTimeout(timer);
    };
  // scenarioExplanationKey deliberately replaces the object dependency; see above.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onExplainScenario, scenarioComparison.changedLineCount, scenarioExplanationKey]);
  const changedLineIdSet = useMemo(
    () => new Set(scenarioComparison.changedLines.map((line) => String(line.lineId))),
    [scenarioComparison.changedLines],
  );
  const changedLineIds = useMemo(() => [...changedLineIdSet], [changedLineIdSet]);
  const recommendedLineById = useMemo(
    () => new Map(recommendedPlan.lines.map((line) => [String(line.lineId), line])),
    [recommendedPlan.lines],
  );
  const scenarioChangeByLineId = useMemo(
    () => new Map(scenarioComparison.changedLines.map((line) => [String(line.lineId), line])),
    [scenarioComparison.changedLines],
  );
  const recommendedOrderByLineId = useMemo(() => {
    const map = new Map();
    scenarioComparison.recommended.vendorOrders.forEach((order) => {
      order.lines.forEach((line) => map.set(String(line.lineId), order));
    });
    return map;
  }, [scenarioComparison.recommended.vendorOrders]);

  /**
   * Which package this store ACTUALLY bought last, per line.
   *
   * Not a price claim and not a recommendation: evidence. resolveLastUsedOption
   * is the same rule the Related Products card uses under the same name, so the
   * two screens can never disagree about it, and it counts only real purchasing
   * — a sent purchase order, a completed shopping trip, or an accepted invoice.
   * A store that has only ever imported a price list has no last-used option
   * and correctly sees no badge.
   */
  const lastUsedByLineId = useMemo(() => {
    const map = {};
    plannedLines.forEach((line) => {
      const pool = pooledOptionsByItemId[line.inventory_item_id] || [];
      const resolved = resolveLastUsedOption(pool, { orderUseByOptionId });
      if (resolved) map[String(line.id)] = resolved;
    });
    return map;
  }, [plannedLines, pooledOptionsByItemId, orderUseByOptionId]);

  // Which vendor order each planned line currently belongs to, for the row's
  // fulfillment badge and the issue filter.
  const orderByLineId = useMemo(() => {
    const map = new Map();
    plan_.vendorOrders.forEach((order) => {
      order.lines.forEach((line) => map.set(String(line.lineId), order));
    });
    return map;
  }, [plan_.vendorOrders]);

  const avoidedVendorIdSet = useMemo(
    () => new Set(avoidedVendorIds.map(String)),
    [avoidedVendorIds],
  );
  const optionAllowedInScenario = useCallback((lineId, option) => {
    if (option?.vendor_id == null) return true;
    const vendorId = String(option.vendor_id);
    if (!avoidedVendorIdSet.has(vendorId)) return true;
    return (allowedExcludedVendorIdsByLineId?.[String(lineId)] || [])
      .map(String).includes(vendorId);
  }, [avoidedVendorIdSet, allowedExcludedVendorIdsByLineId]);

  /**
   * Avoiding a vendor may make one product impossible without making it
   * disappear. `plan_` has already evaluated the FULL candidate pool, including
   * approved vendors with no current order section, so an unbuyable result here
   * means no non-excluded safe source survived. The unconstrained line tells us
   * which safe, previously usable vendor can be kept as a line-specific
   * exception; the constrained line tells us whether every remaining option
   * is absent or package-unsafe.
   */
  const blockedAvoidanceLines = useMemo(() => plan_.lines.flatMap((line) => {
    if (!line.isUnbuyable && !line.needsPackageRepair) return [];
    const original = unconstrainedPlan.lines.find(
      (candidate) => String(candidate.lineId) === String(line.lineId),
    );
    const vendorId = original?.vendorId == null ? null : String(original.vendorId);
    if (vendorId == null || !avoidedVendorIdSet.has(vendorId)) return [];
    return [{
      line,
      vendorId,
      vendorName: vendorsById[vendorId]?.display_name || original.vendorName || 'the avoided vendor',
      packageUnsafe: line.needsPackageRepair,
    }];
  }), [plan_.lines, unconstrainedPlan.lines, avoidedVendorIdSet, vendorsById]);

  const scenarioHasBlockingLines = plan_.lines.some(
    (line) => line.isUnbuyable || line.needsPackageRepair,
  );

  /**
   * Every vendor that carries something in this plan, whether or not the
   * current arrangement buys anything from them.
   *
   * A vendor with nothing in it still needs a drop target in vendor-grouped
   * mode, because otherwise there is nowhere to drop: the buyer could never
   * move the first product to Costco without Costco already having a product.
   * Empty sections are the invitation.
   */
  const candidateVendorIds = useMemo(() => {
    const ids = new Set();
    plannedLines.forEach((line) => {
      const candidateContext = candidateContextFor(line.inventory_item_id);
      eligibleOptionsFor(pooledOptionsByItemId[line.inventory_item_id] || [], {
        inventoryItemId: line.inventory_item_id,
        ...candidateContext,
        itemsById,
      }).filter((option) => optionAllowedInScenario(line.id, option)).forEach((option) => {
        if (option.vendor_id != null) ids.add(String(option.vendor_id));
      });
    });
    // Keep every explicitly excluded vendor on the board. A vendor-only item
    // needs to remain attached to the decision that stranded it; removing the
    // whole vendor section pushed the resolution into a disconnected banner
    // and made the line look as though it had disappeared.
    avoidedVendorIds.forEach((vendorId) => ids.add(String(vendorId)));
    return ids;
  }, [
    plannedLines, pooledOptionsByItemId, candidateContextFor, itemsById,
    optionAllowedInScenario, avoidedVendorIds,
  ]);

  const columns = useMemo(() => {
    const used = new Set(plan_.vendorOrders.map((o) => String(o.vendorId)));
    const empty = [...candidateVendorIds]
      .filter((id) => !used.has(id))
      .map((id) => ({
        groupKey: `vendor:${id}`,
        vendorId: id,
        vendorLinked: true,
        vendor: vendorsById[id] || null,
        vendorName: vendorsById[id]?.display_name || 'Vendor',
        fulfillmentType: vendorsById[id]?.fulfillment_type || 'delivery',
        isPickup: (vendorsById[id]?.fulfillment_type || 'delivery') === 'pickup',
        // 'delivery' is this vendor's DEFAULT, not an answer. Until somebody
        // says, the button stays a neutral "Order" and the tag says so.
        fulfillmentAnswered: ANSWERED_SOURCES.includes(vendorsById[id]?.fulfillment_type_source),
        lines: [],
        subtotal: 0,
        itemCount: 0,
        minimumOrderAmount: null,
        minimumMet: null,
        shortfall: null,
        deliveryFee: null,
        fuelSurcharge: null,
      }))
      .sort((a, b) => a.vendorName.localeCompare(b.vendorName));
    return [...plan_.vendorOrders, ...empty];
  }, [plan_.vendorOrders, candidateVendorIds, vendorsById]);

  /**
   * ACTIVE vendors are the ones at least one product is currently assigned
   * to — they are the only vendors that render a full order card. A vendor
   * with nothing committed is not participating in this purchase, so it never
   * wears an order-shaped card with $0.00 money; it appears in the quieter
   * Available vendors strip under the workspace instead (or nowhere, when
   * nothing in this plan can be bought from it). Participation is decided by
   * product allocation, not by a vendor on/off switch.
   */
  const activeOrders = useMemo(
    () => columns.filter((order) => (order.itemCount || 0) > 0),
    [columns],
  );

  /**
   * The consequence printed beside each vendor's one include/remove control.
   * This is another read of the existing purchase-plan engine, not a second
   * price calculator: toggle only that vendor, rebuild, then compare the two
   * complete landed plans. Tax deliberately remains outside this preview
   * because ordering does not calculate it yet.
   */
  const vendorActionPreviewById = useMemo(() => {
    const currentBlockingCount = plan_.lines.filter(
      (line) => line.isUnbuyable || line.needsPackageRepair,
    ).length;
    const previews = {};

    columns.forEach((order) => {
      if (order.vendorId == null) return;
      const vendorId = String(order.vendorId);
      const isExcluded = avoidedVendorIdSet.has(vendorId);
      const nextAvoidedVendorIds = isExcluded
        ? avoidedVendorIds.filter((id) => String(id) !== vendorId)
        : [...avoidedVendorIds, vendorId];
      const nextAllowances = {};
      Object.entries(allowedExcludedVendorIdsByLineId).forEach(([lineId, ids]) => {
        const kept = isExcluded
          ? ids.filter((id) => String(id) !== vendorId)
          : [...ids];
        if (kept.length) nextAllowances[lineId] = kept;
      });
      const previewPlan = buildPurchasePlan({
        ...planArgs,
        explicitByLineId: selectionByLineId,
        excludedVendorIds: nextAvoidedVendorIds,
        allowedExcludedVendorIdsByLineId: nextAllowances,
      });
      const comparison = comparePurchasePlans(plan_, previewPlan);
      previews[vendorId] = {
        isExcluded,
        landedDelta: comparison.landedDelta,
        feeDelta: comparison.feeDelta,
        economicImpactComplete: comparison.economicImpactComplete,
        changedLineCount: comparison.changedLineCount,
        // The three separated answers for the card: cash, quantity, price.
        quantityChangedProductCount: comparison.quantityChangedProductCount,
        quantityComparisonUnknownCount: comparison.quantityComparisonUnknownCount,
        sharedQuantityUnit: comparison.sharedQuantityUnit,
        quantityDeltaInSharedUnit: comparison.quantityDeltaInSharedUnit,
        grossPriceImpactTotal: comparison.grossPriceImpactTotal,
        grossPriceImpactComplete: comparison.grossPriceImpactComplete,
        currentBlockingCount,
        nextBlockingCount: previewPlan.lines.filter(
          (line) => line.isUnbuyable || line.needsPackageRepair,
        ).length,
      };
    });

    return previews;
  }, [
    columns, plan_, planArgs, selectionByLineId, avoidedVendorIdSet,
    avoidedVendorIds, allowedExcludedVendorIdsByLineId,
  ]);

  /** Where else this product can be bought — the legal drop targets. */
  const vendorsCarrying = useCallback((itemId) => {
    const line = plannedLines.find((entry) => entry.inventory_item_id === itemId);
    const eligible = eligibleOptionsFor(pooledOptionsByItemId[itemId] || [], {
      inventoryItemId: itemId,
      ...candidateContextFor(itemId),
      itemsById,
    }).filter((option) => optionAllowedInScenario(line?.id, option));
    const map = new Map();
    eligible.forEach((option) => {
      if (option.vendor_id == null) return;
      const key = String(option.vendor_id);
      const existing = map.get(key);
      // If a vendor sells it more than one way, offer the cheapest way to
      // cover the need — the buyer can refine inside the detail view.
      const cost = costToFulfill(Number(line?.quantity_needed) || 0, option, null, {
        needUnit: line?.count_unit,
        item: itemsById[itemId] || null,
        optionItem: itemsById[String(option.inventory_item_id)] || null,
      });
      if (!existing || (cost?.orderCost ?? Infinity) < (existing.orderCost ?? Infinity)) {
        map.set(key, { optionId: String(option.id), orderCost: cost?.orderCost ?? null });
      }
    });
    return map;
  }, [
    pooledOptionsByItemId, plannedLines, candidateContextFor, itemsById,
    optionAllowedInScenario,
  ]);

  const pushUndo = useCallback((entry) => {
    setUndoStack((prev) => [...prev.slice(-19), entry]);
  }, []);

  const constraintSnapshot = useCallback(() => ({
    type: 'constraints',
    avoidedVendorIds: [...avoidedVendorIds],
    allowedExcludedVendorIdsByLineId: Object.fromEntries(
      Object.entries(allowedExcludedVendorIdsByLineId)
        .map(([lineId, ids]) => [lineId, [...ids]]),
    ),
  }), [avoidedVendorIds, allowedExcludedVendorIdsByLineId]);

  const handleStartVendorExclusion = useCallback((vendorId) => {
    if (arrangementFrozenRef.current || hasPendingMoves || vendorId == null) return;
    const vendorKey = String(vendorId);
    const recommendation = recommendVendorExclusionScenario({
      ...planArgs,
      explicitByLineId: selectionByLineId,
      excludedVendorIds: avoidedVendorIds,
      allowedExcludedVendorIdsByLineId,
    }, vendorKey);
    setExclusionReview({
      step: 'review',
      vendorId: vendorKey,
      proposedOptionByLineId: recommendation.proposedOptionByLineId,
      keptLineIds: [],
      unresolvedLineIds: [],
      approvedLineIds: [],
    });
  }, [
    hasPendingMoves, planArgs, selectionByLineId, avoidedVendorIds,
    allowedExcludedVendorIdsByLineId,
  ]);

  const handleDraftReplacementChange = useCallback((lineId, optionId) => {
    setExclusionReview((current) => {
      if (!current || current.step !== 'review') return current;
      const key = String(lineId);
      return {
        ...current,
        proposedOptionByLineId: {
          ...(current.proposedOptionByLineId || {}),
          [key]: String(optionId),
        },
        keptLineIds: (current.keptLineIds || []).filter((id) => String(id) !== key),
        unresolvedLineIds: (current.unresolvedLineIds || [])
          .filter((id) => String(id) !== key),
        approvedLineIds: (current.approvedLineIds || [])
          .filter((id) => String(id) !== key),
      };
    });
  }, []);

  const handleApproveDraftLine = useCallback((lineId) => {
    setExclusionReview((current) => {
      if (!current || current.step !== 'review') return current;
      const key = String(lineId);
      return {
        ...current,
        approvedLineIds: [...new Set([...(current.approvedLineIds || []).map(String), key])],
      };
    });
  }, []);

  const handleApproveAllDraftReplacements = useCallback(() => {
    if (!exclusionDraft?.model) return;
    const replacementLineIds = exclusionDraft.model.items
      .filter((item) => item.status !== 'no_alternative' && item.selectedOptionId)
      .map((item) => String(item.lineId));
    setExclusionReview((current) => {
      if (!current || current.step !== 'review') return current;
      return {
        ...current,
        approvedLineIds: [...new Set([
          ...(current.approvedLineIds || []).map(String),
          ...replacementLineIds,
        ])],
      };
    });
  }, [exclusionDraft]);

  const handleDraftKeepVendor = useCallback((lineId) => {
    setExclusionReview((current) => {
      if (!current || current.step !== 'review') return current;
      const key = String(lineId);
      const choices = { ...(current.proposedOptionByLineId || {}) };
      delete choices[key];
      return {
        ...current,
        proposedOptionByLineId: choices,
        keptLineIds: [...new Set([...(current.keptLineIds || []).map(String), key])],
        unresolvedLineIds: (current.unresolvedLineIds || [])
          .filter((id) => String(id) !== key),
        approvedLineIds: [...new Set([...(current.approvedLineIds || []).map(String), key])],
      };
    });
  }, []);

  const handleDraftLeaveUnresolved = useCallback((lineId) => {
    setExclusionReview((current) => {
      if (!current || current.step !== 'review') return current;
      const key = String(lineId);
      const choices = { ...(current.proposedOptionByLineId || {}) };
      delete choices[key];
      return {
        ...current,
        proposedOptionByLineId: choices,
        keptLineIds: (current.keptLineIds || []).filter((id) => String(id) !== key),
        unresolvedLineIds: [...new Set([
          ...(current.unresolvedLineIds || []).map(String),
          key,
        ])],
        approvedLineIds: [...new Set([...(current.approvedLineIds || []).map(String), key])],
      };
    });
  }, []);

  const handleAvoidVendor = useCallback((vendorId) => {
    if (arrangementFrozenRef.current || hasPendingMoves || vendorId == null) return;
    const key = String(vendorId);
    if (avoidedVendorIdSet.has(key)) return;
    pushUndo(constraintSnapshot());
    setAvoidedVendorIds((prev) => [...prev, key]);
  }, [avoidedVendorIdSet, constraintSnapshot, pushUndo, hasPendingMoves]);

  const handleRemoveAvoidedVendor = useCallback((vendorId) => {
    if (arrangementFrozenRef.current || hasPendingMoves) return;
    const key = String(vendorId);
    pushUndo(constraintSnapshot());
    setAvoidedVendorIds((prev) => prev.filter((id) => String(id) !== key));
    setAllowedExcludedVendorIdsByLineId((prev) => {
      const next = {};
      Object.entries(prev).forEach(([lineId, ids]) => {
        const kept = ids.filter((id) => String(id) !== key);
        if (kept.length) next[lineId] = kept;
      });
      return next;
    });
  }, [constraintSnapshot, pushUndo, hasPendingMoves]);

  const handleKeepAvoidedVendorForLine = useCallback((lineId, vendorId) => {
    if (arrangementFrozenRef.current || hasPendingMoves || vendorId == null) return;
    const lineKey = String(lineId);
    const vendorKey = String(vendorId);
    pushUndo(constraintSnapshot());
    setAllowedExcludedVendorIdsByLineId((prev) => ({
      ...prev,
      [lineKey]: [...new Set([...(prev[lineKey] || []).map(String), vendorKey])],
    }));
  }, [constraintSnapshot, pushUndo, hasPendingMoves]);

  const handleChooseReplacement = useCallback((lineId) => {
    const key = String(lineId);
    setGroupMode('item');
    setShowChangesOnly(false);
    setFilterPickup(false);
    setSearchTerm('');
    setExpandedLineIds((prev) => ({ ...prev, [key]: true }));
    if (typeof window !== 'undefined') {
      window.requestAnimationFrame?.(() => {
        const row = document.querySelector(
          `[data-testid="savings-review-item"][data-line-id="${key}"]`,
        );
        row?.scrollIntoView?.({ behavior: 'smooth', block: 'center' });
      });
    }
  }, []);

  const handleDiscussScenario = useCallback(() => {
    if (typeof window === 'undefined' || typeof window.CustomEvent !== 'function') return;
    window.dispatchEvent(new CustomEvent('counter-intelligence:prefill', {
      detail: {
        text: buildPurchaseScenarioDiscussionPrompt(scenarioExplanationFacts),
        source: 'purchase_planner',
        sourceLabel: 'Purchase Planner',
      },
    }));
  }, [scenarioExplanationFacts]);

  const handleDragStart = (planLine) => (event) => {
    if (arrangementFrozen) { event.preventDefault(); return; }
    if (completedVendorIds.includes(String(planLine.vendorId))) {
      event.preventDefault();
      return;
    }
    setDragging({
      lineId: planLine.lineId,
      itemId: planLine.inventoryItemId,
      fromVendorId: planLine.vendorId == null ? null : String(planLine.vendorId),
      targets: vendorsCarrying(planLine.inventoryItemId),
    });
    setRejected(null);
    try { event.dataTransfer.effectAllowed = 'move'; } catch (_) {}
  };

  const handleDragEnd = () => { setDragging(null); setHoverVendor(null); };

  /**
   * THE one way a line changes vendor. The drop handler and the row's
   * "Switch to this vendor" button both come here, so the two gestures cannot
   * diverge: same persistence, same promote-and-flash.
   */
  const acceptOption = useCallback((lineId, itemId, optionId, { onFailure } = {}) => {
    if (arrangementFrozenRef.current) return;
    const key = String(lineId);
    const nextOptionId = String(optionId);

    setPromotedLineIds((prev) => [lineId, ...prev.filter((id) => id !== lineId)]);
    setJustLanded(lineId);
    if (landTimer.current) clearTimeout(landTimer.current);
    landTimer.current = setTimeout(() => setJustLanded(null), 2000);
    setDetailsOptionKeys({});
    setMoveErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev }; delete next[key]; return next;
    });

    if (typeof onLineMoved !== 'function') {
      // No persistence wired (preview/standalone): the choice is the answer.
      setConfirmedMoves((prev) => ({ ...prev, [key]: nextOptionId }));
      return;
    }
    const option = (pooledOptionsByItemId[itemId] || [])
      .find((o) => String(o.id) === String(optionId));
    if (!option) return;

    // Show it moved immediately — but as PENDING, which shuts Finish.
    setPendingMoves((prev) => ({ ...prev, [key]: nextOptionId }));
    const seq = (moveSeq.current += 1);
    latestIntent.current.set(key, seq);

    const run = async () => {
      // A newer switch of the same product already superseded this one;
      // sending it would let an old intent win by arriving late.
      if (latestIntent.current.get(key) !== seq) return;
      try {
        const updated = await onLineMoved(lineId, option);
        if (latestIntent.current.get(key) !== seq) return;
        const settled = updated?.selected_purchase_option_id != null
          ? String(updated.selected_purchase_option_id)
          : nextOptionId;
        // Whatever the server actually stored is what the row shows.
        setConfirmedMoves((prev) => ({ ...prev, [key]: settled }));
      } catch (err) {
        if (latestIntent.current.get(key) !== seq) return;
        // The database refused it. Roll the row back to the authoritative
        // vendor and say why, next to the product — never leave the buyer
        // looking at an arrangement that does not exist.
        setConfirmedMoves((prev) => {
          if (!(key in prev)) return prev;
          const next = { ...prev }; delete next[key]; return next;
        });
        setMoveErrors((prev) => ({
          ...prev,
          [key]: err?.message || 'Could not save that change.',
        }));
        if (typeof onFailure === 'function') onFailure(err);
      } finally {
        if (latestIntent.current.get(key) === seq) {
          setPendingMoves((prev) => {
            if (!(key in prev)) return prev;
            const next = { ...prev }; delete next[key]; return next;
          });
        }
      }
    };

    // Serialize per line: the next write starts only when this one has settled.
    const chain = (moveChains.current.get(key) || Promise.resolve()).then(run, run);
    moveChains.current.set(key, chain);
  }, [onLineMoved, pooledOptionsByItemId]);

  const handleApplyVendorExclusion = useCallback(() => {
    if (!exclusionDraft || arrangementFrozenRef.current || hasPendingMoves
        || exclusionPendingReviewCount > 0) return;
    const before = constraintSnapshot();
    pushUndo(before);

    // Apply the reviewed constraint snapshot in one React update. Valid
    // replacements also travel through the ordinary persisted move path; an
    // unresolved line remains visible and blocks fan-out until the buyer
    // chooses Keep or a safe vendor later.
    setAvoidedVendorIds(exclusionDraft.excludedVendorIds);
    setAllowedExcludedVendorIdsByLineId(exclusionDraft.allowedExcludedVendorIdsByLineId);
    const rollbackConstraint = () => {
      // A persisted replacement was refused. Keep any moves the server did
      // accept, but remove the exclusion filter so the failed line falls back
      // to its authoritative vendor instead of looking moved only in-browser.
      setAvoidedVendorIds(before.avoidedVendorIds || []);
      setAllowedExcludedVendorIdsByLineId(before.allowedExcludedVendorIdsByLineId || {});
    };
    exclusionDraft.model.items.forEach((item) => {
      if (!item.selectedOptionId || item.proposedVendorId === exclusionDraft.vendorId) return;
      const current = plan_.lines.find((line) => String(line.lineId) === String(item.lineId));
      if (String(current?.selectedOptionId ?? '') === String(item.selectedOptionId)) return;
      acceptOption(item.lineId, item.inventoryItemId, item.selectedOptionId, {
        onFailure: rollbackConstraint,
      });
    });
    setExclusionReview(null);
  }, [
    exclusionDraft, exclusionPendingReviewCount, hasPendingMoves, pushUndo,
    constraintSnapshot, plan_.lines, acceptOption,
  ]);

  /**
   * A click on "Switch to this vendor". The same gate the drop handler
   * applies: a finished vendor with an unsent draft is offered the reopen as
   * ONE action; a finished vendor whose order was sent is refused with the
   * reason. Anything else lands the switch immediately.
   */
  const requestSwitch = useCallback((line, optionId, vendorId) => {
    if (arrangementFrozenRef.current) return;
    // The SOURCE side of the freeze, enforced at the one choke point every
    // gesture funnels through. The drag path refuses at dragStart and the row
    // hides its switch button, but a caller that forgets (the dock did, once)
    // must not be able to pull a line out of a vendor whose PO already
    // exists — that is the screen-vs-order divergence this screen exists to
    // prevent.
    const sourceKey = line?.vendorId == null ? null : String(line.vendorId);
    if (sourceKey != null && completedVendorIds.includes(sourceKey)) {
      const sourceName = vendorsById[sourceKey]?.display_name || 'That vendor';
      setRejected({
        lineId: line.lineId,
        message: draftPoByVendorId[sourceKey]
          ? `${sourceName} is already finished — return its draft PO to change this item.`
          : `${sourceName}'s order has already been sent — changing it needs a new order or an amendment.`,
      });
      return;
    }
    const key = vendorId == null ? null : String(vendorId);
    if (key != null && completedVendorIds.includes(key)) {
      const targetName = vendorsById[key]?.display_name || 'That vendor';
      const draft = draftPoByVendorId[key];
      if (draft && typeof onReopenDraftAndMove === 'function') {
        setReopenPrompt({
          vendorId: key,
          vendorName: targetName,
          draft,
          lineId: line.lineId,
          itemId: line.inventoryItemId,
          optionId,
          description: line.description || 'this product',
        });
        return;
      }
      setRejected({
        lineId: line.lineId,
        message: draft
          ? `${targetName} is already finished — return its draft PO to change it.`
          : `${targetName}'s order has already been sent — changing it needs a new order or an amendment.`,
      });
      return;
    }
    setRejected(null);
    pushUndo({
      type: 'selection',
      lineId: line.lineId,
      itemId: line.inventoryItemId,
      optionId: line.selectedOptionId ?? null,
    });
    acceptOption(line.lineId, line.inventoryItemId, optionId);
  }, [
    completedVendorIds, draftPoByVendorId, vendorsById, onReopenDraftAndMove,
    acceptOption, pushUndo,
  ]);

  /**
   * Reset to recommended: every line whose assignment the buyer changed is
   * switched back to the frozen baseline, through the same accepted path a
   * manual switch uses — same persistence, same validation, same rollback.
   */
  const handleResetToRecommended = useCallback(() => {
    if (arrangementFrozenRef.current || hasPendingMoves) return;
    pushUndo({
      type: 'reset',
      avoidedVendorIds: [...avoidedVendorIds],
      allowedExcludedVendorIdsByLineId: Object.fromEntries(
        Object.entries(allowedExcludedVendorIdsByLineId)
          .map(([lineId, ids]) => [lineId, [...ids]]),
      ),
      selections: Object.fromEntries(
        unconstrainedPlan.lines.map((line) => [String(line.lineId), line.selectedOptionId ?? null]),
      ),
    });
    setAvoidedVendorIds([]);
    setAllowedExcludedVendorIdsByLineId({});
    unconstrainedPlan.lines.forEach((line) => {
      const recommended = recommendedByLineId[String(line.lineId)];
      if (!recommended) return;
      const current = line.selectedOptionId;
      if (current != null && String(current) === recommended) return;
      const option = (pooledOptionsByItemId[line.inventoryItemId] || [])
        .find((o) => String(o.id) === recommended);
      if (!option) return;
      const vendorKey = option.vendor_id == null ? null : String(option.vendor_id);
      const currentVendorKey = line.vendorId == null ? null : String(line.vendorId);
      // A finished vendor's lines are frozen in both directions — resetting
      // into or out of one is the reopen-a-draft conversation, not a reset.
      if (vendorKey != null && completedVendorIds.includes(vendorKey)) return;
      if (currentVendorKey != null && completedVendorIds.includes(currentVendorKey)) return;
      acceptOption(line.lineId, line.inventoryItemId, recommended);
    });
  }, [
    recommendedByLineId, pooledOptionsByItemId, completedVendorIds, acceptOption,
    unconstrainedPlan.lines, avoidedVendorIds, allowedExcludedVendorIdsByLineId,
    pushUndo, hasPendingMoves,
  ]);

  const handleUndoLastChange = useCallback(() => {
    if (arrangementFrozenRef.current || hasPendingMoves || undoStack.length === 0) return;
    const entry = undoStack[undoStack.length - 1];
    setUndoStack((prev) => prev.slice(0, -1));
    if (entry.type === 'constraints') {
      setAvoidedVendorIds(entry.avoidedVendorIds || []);
      setAllowedExcludedVendorIdsByLineId(entry.allowedExcludedVendorIdsByLineId || {});
      return;
    }
    if (entry.type === 'selection') {
      if (entry.optionId != null) acceptOption(entry.lineId, entry.itemId, entry.optionId);
      return;
    }
    if (entry.type === 'reset') {
      setAvoidedVendorIds(entry.avoidedVendorIds || []);
      setAllowedExcludedVendorIdsByLineId(entry.allowedExcludedVendorIdsByLineId || {});
      Object.entries(entry.selections || {}).forEach(([lineId, optionId]) => {
        if (optionId == null) return;
        const current = unconstrainedPlan.lines.find((line) => String(line.lineId) === String(lineId));
        if (String(current?.selectedOptionId ?? '') === String(optionId)) return;
        if (current) acceptOption(current.lineId, current.inventoryItemId, optionId);
      });
    }
  }, [undoStack, acceptOption, unconstrainedPlan.lines, hasPendingMoves]);

  /**
   * Deterministic preview of a drop: the full plan is rebuilt with the move
   * applied and diffed against the current one — line cost, extra product,
   * vendor-order count, fees, landed total. No AI, same math as landing.
   */
  const dropPreview = useMemo(() => {
    if (!dragging || !hoverVendor) return null;
    const target = dragging.targets.get(hoverVendor);
    if (!target) return null;
    const move = previewLineMove({
      ...planArgs,
      explicitByLineId: selectionByLineId,
      excludedVendorIds: avoidedVendorIds,
      allowedExcludedVendorIdsByLineId,
    }, dragging.lineId, target.optionId);
    const currentLine = plan_.lines.find((l) => l.lineId === dragging.lineId);
    return {
      vendorName: vendorsById[hoverVendor]?.display_name || 'this vendor',
      lineCostDelta: move.itemCostDelta,
      extraQuantityDelta: move.overageDelta,
      countUnit: currentLine?.countUnit || 'each',
      vendorOrdersBefore: move.vendorOrdersBefore,
      vendorOrdersAfter: move.vendorOrdersAfter,
      pickupStopsBefore: move.pickupStopsBefore,
      pickupStopsAfter: move.pickupStopsAfter,
      pickupStopDelta: move.pickupStopDelta,
      feeDelta: move.feeDelta,
      landedDelta: move.landedDelta,
      economicImpactComplete: move.economicImpactComplete,
    };
  }, [
    dragging, hoverVendor, planArgs, selectionByLineId, vendorsById, plan_,
    avoidedVendorIds, allowedExcludedVendorIdsByLineId,
  ]);

  const canDropOn = (vendorId) => {
    if (!dragging) return false;
    const key = vendorId == null ? null : String(vendorId);
    if (key === dragging.fromVendorId) return false;
    if (completedVendorIds.includes(key)) return false;
    return dragging.targets.has(key);
  };

  const handleDrop = (vendorId) => (event) => {
    if (arrangementFrozen) { event.preventDefault(); setDragging(null); return; }
    event.preventDefault();
    setHoverVendor(null);
    if (!dragging) return;
    const key = vendorId == null ? null : String(vendorId);

    if (!canDropOn(vendorId)) {
      const targetName = vendorsById[key]?.display_name || 'That vendor';
      const draft = draftPoByVendorId[key];
      // A finished vendor whose draft is still UNSENT is not a dead end. The
      // buyer's intent ("buy this here instead") is perfectly legal — the only
      // thing in the way is a draft nobody has sent, and undoing that is
      // something the app can do for them. Offer it as one action instead of
      // making them find Return draft, press it, and re-drag.
      if (completedVendorIds.includes(key) && draft && dragging.targets.has(key)
        && typeof onReopenDraftAndMove === 'function') {
        setReopenPrompt({
          vendorId: key,
          vendorName: targetName,
          draft,
          lineId: dragging.lineId,
          itemId: dragging.itemId,
          optionId: dragging.targets.get(key).optionId,
          description: plan_.lines.find((l) => l.lineId === dragging.lineId)?.description || 'this product',
        });
        setDragging(null);
        return;
      }
      // Refused, with the reason. A silently ignored drop reads as a broken
      // interface; "Sysco does not carry this" is a fact the buyer can use.
      let message;
      if (!completedVendorIds.includes(key)) {
        message = `${targetName} does not carry this product.`;
      } else if (!dragging.targets.has(key)) {
        message = `${targetName} does not carry this product.`;
      } else if (draft) {
        // An unsent draft exists but no reopen path was wired.
        message = `${targetName} is already finished — return its draft PO to change it.`;
      } else {
        // Nothing to hand back: the order was SENT, or its shopping list has
        // already been acted on. Neither may be silently rewritten by a drag.
        message = `${targetName}'s order has already been sent — changing it needs a new order or an amendment.`;
      }
      setRejected({ lineId: dragging.lineId, message });
      setDragging(null);
      return;
    }

    const target = dragging.targets.get(key);
    const current = plan_.lines.find((line) => String(line.lineId) === String(dragging.lineId));
    pushUndo({
      type: 'selection',
      lineId: dragging.lineId,
      itemId: dragging.itemId,
      optionId: current?.selectedOptionId ?? null,
    });
    acceptOption(dragging.lineId, dragging.itemId, target.optionId);
    setDragging(null);
  };

  /**
   * Reopen an unsent draft and land the switch that wanted it, as ONE action:
   * return the draft through the existing path, then persist the move. The
   * buyer stays on this sheet throughout — the whole point is that they are
   * still arranging it.
   */
  const confirmReopenAndMove = async () => {
    if (!reopenPrompt || reopenBusy) return;
    setReopenBusy(true);
    try {
      await onReopenDraftAndMove(reopenPrompt.vendorId, {
        lineId: reopenPrompt.lineId,
        itemId: reopenPrompt.itemId,
        optionId: reopenPrompt.optionId,
      });
      setReopenPrompt(null);
    } catch (err) {
      setReopenPrompt(null);
      setRejected({
        lineId: reopenPrompt.lineId,
        message: err?.message || 'Could not reopen that draft.',
      });
    } finally {
      setReopenBusy(false);
    }
  };

  /* ── Totals ─────────────────────────────────────────────────────────────── */

  // Delivery and below-minimum charges come from the SHARED plan model
  // (groupPlanByVendor → vendorOrderCharges), so this screen, the plan list,
  // and any report all bill the same grand total. The worksheet only lists
  // the charges the model already derived.
  const charges = useMemo(() => plan_.vendorOrders.reduce((acc, order) => {
    const fee = Number(order.deliveryFee);
    if (Number.isFinite(fee) && fee > 0) {
      acc.delivery += fee;
      acc.total += fee;
    }
    const fuel = Number(order.fuelSurcharge);
    if (Number.isFinite(fuel) && fuel > 0) {
      acc.fuel += fuel;
      acc.total += fuel;
    }
    const penalty = Number(order.belowMinimumFee);
    if (Number.isFinite(penalty) && penalty > 0) {
      acc.minimum += penalty;
      acc.total += penalty;
    }
    if (order.feasible === false) acc.infeasible.push(order.vendorName);
    return acc;
  }, { total: 0, delivery: 0, fuel: 0, minimum: 0, infeasible: [] }), [plan_.vendorOrders]);

  const allComplete = plan_.vendorOrders.length > 0
    && plan_.vendorOrders.every(
      (o) => o.vendorLinked && completedVendorIds.includes(String(o.vendorId)),
    );

  /* ── Path to purchase ───────────────────────────────────────────────────── */

  const pathSummary = useMemo(() => {
    const withItems = plan_.vendorOrders.filter((o) => (o.itemCount || 0) > 0);
    const isComplete = (o) => o.vendorId != null && completedVendorIds.includes(String(o.vendorId));
    const delivery = withItems.filter((o) => !o.isPickup);
    const pickup = withItems.filter((o) => o.isPickup);
    return {
      deliveryReady: delivery.filter((o) => !isComplete(o) && o.minimumMet !== false && o.feasible !== false).length,
      deliveryTotal: delivery.length,
      pickupStops: pickup.filter((o) => !isComplete(o)).length,
      belowMinimum: withItems.filter((o) => !isComplete(o) && (o.minimumMet === false || o.feasible === false)).length,
      finished: withItems.filter(isComplete).length,
    };
  }, [plan_.vendorOrders, completedVendorIds]);

  /* ── Done: leave the worksheet ────────────────────────────────────────────
     Vendor orders still needing their Finish are the remaining count. An
     unlinked vendor cannot be finished from here, so it is counted but
     excluded from the bulk create — the buyer links it from its vendor card. */
  const remainingOrders = plan_.vendorOrders.filter((o) => (o.itemCount || 0) > 0
    && (o.vendorId == null || !completedVendorIds.includes(String(o.vendorId))));
  const finishableOrders = remainingOrders.filter((o) => o.vendorId != null && o.vendorLinked !== false);

  const handleDone = () => {
    if (scenarioHasBlockingLines) {
      // Take the buyer TO the first blocking line — expanded and centered —
      // instead of silently switching on a filter they then have to find.
      const blocked = plan_.lines.find((line) => line.isUnbuyable || line.needsPackageRepair);
      if (blocked) handleChooseReplacement(blocked.lineId);
      return;
    }
    if (remainingOrders.length === 0) {
      if (onViewOrders) onViewOrders();
      return;
    }
    setDonePromptOpen(true);
  };

  // Finish the remaining vendors — ONE handoff, not a loop of per-vendor
  // Finishes. The container owns the sequence because it also owns the
  // buying-mode question, and that question has to be asked once for every
  // vendor that still owes an answer before any order exists. Looping here
  // let each vendor's question replace the previous one, so one order was
  // created and the rest had to be finished by hand from their cards.
  const handleCreateRemainingOrders = async () => {
    if (doneBusy) return;
    // Same barrier as a single vendor's Finish: nothing becomes an order while
    // an arrangement is still on its way to the database.
    if (hasPendingMoves) return;
    if (!finishableOrders.length) return;
    setDoneBusy(true);
    try {
      if (onCompleteVendors) {
        // The ORDERS, not just their vendor ids: each carries the lines as
        // arranged on screen, which the container has to persist before it
        // preflights anything.
        await onCompleteVendors(finishableOrders);
      } else if (onCompleteVendor) {
        for (const order of finishableOrders) {
          // eslint-disable-next-line no-await-in-loop
          await onCompleteVendor(order.vendorId, order);
        }
        if (onViewOrders) onViewOrders();
      }
    } finally {
      setDoneBusy(false);
      setDonePromptOpen(false);
    }
  };

  /* ── The item list ──────────────────────────────────────────────────────── */

  const visibleLines = useMemo(() => {
    const query = searchTerm.trim().toLowerCase();
    const filtered = plan_.lines.filter((line) => {
      if (query && !String(line.description || '').toLowerCase().includes(query)) return false;
      if (filterPickup) {
        const order = orderByLineId.get(String(line.lineId));
        if (!order?.isPickup) return false;
      }
      if (showChangesOnly && !changedLineIdSet.has(String(line.lineId))) return false;
      return true;
    });
    // Switched rows float to the top, most recent first; untouched lines keep
    // their original order below.
    return [...filtered].sort((a, b) => {
      const ai = promotedLineIds.indexOf(a.lineId);
      const bi = promotedLineIds.indexOf(b.lineId);
      if (ai === -1 && bi === -1) return 0;
      if (ai === -1) return 1;
      if (bi === -1) return -1;
      return ai - bi;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    plan_.lines, searchTerm, filterPickup,
    showChangesOnly, changedLineIdSet, orderByLineId, promotedLineIds,
  ]);

  // Repair rows open on their own: an item that cannot be ordered is not
  // something to discover by clicking a chevron.
  useEffect(() => {
    setExpandedLineIds((prev) => {
      const next = { ...prev };
      let changed = false;
      plan_.lines.forEach((line) => {
        const key = String(line.lineId);
        if ((line.needsPackageRepair || line.substituteReviewOptions?.length) && !next[key]) {
          next[key] = true;
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [plan_.lines]);

  const toggleExpanded = (lineId) => {
    setExpandedLineIds((prev) => ({ ...prev, [String(lineId)]: !prev[String(lineId)] }));
  };

  const toggleDetails = (lineId, optionId) => {
    const key = `${lineId}:${optionId}`;
    setDetailsOptionKeys((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  /** The alternate-vendor list for one expanded row. */
  const alternativesFor = (line) => {
    const candidateContext = candidateContextFor(line.inventoryItemId);
    const scenarioOptions = (pooledOptionsByItemId[line.inventoryItemId] || [])
      .filter((option) => optionAllowedInScenario(line.lineId, option));
    return buildOptionFulfillmentComparison({
      line,
      options: scenarioOptions,
      comparison: comparisonByItemId[line.inventoryItemId] || null,
      item: itemsById[line.inventoryItemId] || null,
      vendorsById,
      groupMembers: candidateContext.groupMembers,
      candidateItemIds: candidateContext.candidateItemIds,
      optionRoleById: candidateContext.optionRoleById,
      itemsById,
      planArgs: {
        ...planArgs,
        explicitByLineId: selectionByLineId,
        excludedVendorIds: avoidedVendorIds,
        allowedExcludedVendorIdsByLineId,
      },
    });
  };

  /**
   * Whether this store has actually PURCHASED through this option — the same
   * evidence bar as the "Last used" badge: a sent purchase order, or an
   * accepted invoice for the package. Catalog housekeeping never counts.
   */
  const optionEverPurchased = useCallback((option) => {
    if (!option) return false;
    const optionId = option.id == null ? null : String(option.id);
    if (optionId && orderUseByOptionId?.[optionId]) return true;
    return PURCHASING_SOURCE_TYPES.has(String(option.last_operational_source_type ?? ''))
      && !!option.last_operational_source_at;
  }, [orderUseByOptionId]);

  /**
   * One alternate lane, classified. `row` is a ranked fulfillment row from
   * the plan engine (it carries the raw option); `line` is the plan line it
   * could serve, `line.selected` the current choice. Confidence is DERIVED
   * from facts the plan already holds — same product vs substitute, provably
   * same pack vs different or uncompared, real purchase history — never
   * invented, and "no comparable price" stays unknown rather than $0.00.
   */
  const classifyAlternateRow = useCallback((row, line) => {
    const selected = line?.selected || null;
    const costDelta = row.orderCost != null && line?.orderCost != null
      ? Math.round((row.orderCost - line.orderCost) * 100) / 100
      : null;
    // Number(null) is 0, so a missing quantity would compare as an equal
    // pack. Absent stays NaN — an uncompared pack is 'unknown', never 'same'.
    const packQty = (value) => (value === null || value === undefined || value === '' ? NaN : Number(value));
    const packState = (() => {
      if (!selected) return 'unknown';
      const compA = packQty(row.comparisonQuantityPerPackage);
      const compB = packQty(selected.comparisonQuantityPerPackage);
      if (Number.isFinite(compA) && Number.isFinite(compB)) {
        return Math.abs(compA - compB) > 1e-6 ? 'different' : 'same';
      }
      const invA = packQty(row.quantityPerPackage);
      const invB = packQty(selected.quantityPerPackage);
      if (Number.isFinite(invA) && Number.isFinite(invB)) {
        return Math.abs(invA - invB) > 1e-6 ? 'different' : 'same';
      }
      return 'unknown';
    })();
    return {
      optionId: String(row.optionId),
      vendorId: row.option?.vendor_id == null ? null : String(row.option.vendor_id),
      vendorName: row.option?.vendor_id != null
        ? (vendorsById[row.option.vendor_id]?.display_name || row.option.vendor_name_text || 'Vendor')
        : (row.option?.vendor_name_text || 'Vendor'),
      orderCost: row.orderCost ?? null,
      costDelta,
      direction: costDelta === null
        ? 'unknown'
        : Math.abs(costDelta) < 0.005 ? 'same' : costDelta < 0 ? 'lower' : 'higher',
      packState,
      isSubstitute: row.isSubstitute === true,
      neverBought: !optionEverPurchased(row.option),
      // Confirmed = the same product in a provably identical pack. Anything
      // else is a lane to open and review, never a one-click move.
      confirmed: row.isSubstitute !== true && packState === 'same',
    };
  }, [vendorsById, optionEverPurchased]);

  /**
   * The source rail for one collapsed row (Feature 1a): one mark per OTHER
   * vendor that could take this line under the current scenario, cheapest way
   * per vendor, cheapest lane first. Read entirely from the ranked options the
   * plan already computed — no extra plan builds, no new fetches.
   */
  const sourceMarksFor = useCallback((line) => {
    if (!line?.selected || line.needsPackageRepair || line.isUnbuyable) return [];
    const currentVendorKey = line.vendorId == null ? null : String(line.vendorId);
    const byVendor = new Map();
    (line.options || []).forEach((row) => {
      const vendorId = row.option?.vendor_id;
      if (vendorId == null) return;
      const key = String(vendorId);
      if (key === currentVendorKey) return;
      const existing = byVendor.get(key);
      if (!existing || (row.orderCost ?? Infinity) < (existing.orderCost ?? Infinity)) {
        byVendor.set(key, row);
      }
    });
    return [...byVendor.values()]
      .map((row) => classifyAlternateRow(row, line))
      .sort((a, b) => (a.costDelta ?? Infinity) - (b.costDelta ?? Infinity));
  }, [classifyAlternateRow]);

  /**
   * Vendor docks (Feature 1b): for every vendor, the plan lines that COULD
   * land there but currently do not. Built from the UNCONSTRAINED plan so an
   * Avoiding vendor still shows its open lane instead of reading as a dead
   * card — exploring an excluded vendor commits nothing. Cost deltas are
   * still measured against the SCENARIO's current choice for each line.
   */
  const dockByVendorId = useMemo(() => {
    const scenarioLineById = new Map(plan_.lines.map((l) => [String(l.lineId), l]));
    const dock = new Map();
    unconstrainedPlan.lines.forEach((uLine) => {
      const line = scenarioLineById.get(String(uLine.lineId)) || uLine;
      const currentVendorKey = line.vendorId == null ? null : String(line.vendorId);
      const byVendor = new Map();
      (uLine.options || []).forEach((row) => {
        const vendorId = row.option?.vendor_id;
        if (vendorId == null) return;
        const key = String(vendorId);
        if (key === currentVendorKey) return;
        const existing = byVendor.get(key);
        if (!existing || (row.orderCost ?? Infinity) < (existing.orderCost ?? Infinity)) {
          byVendor.set(key, row);
        }
      });
      byVendor.forEach((row, vendorKey) => {
        // The tile's detail view compares packages, so the entry carries the
        // raw option (pack description) beside the classified facts.
        const entry = { line, option: row.option || null, ...classifyAlternateRow(row, line) };
        const list = dock.get(vendorKey) || [];
        list.push(entry);
        dock.set(vendorKey, list);
      });
    });
    return dock;
  }, [unconstrainedPlan.lines, plan_.lines, classifyAlternateRow]);

  /**
   * AVAILABLE vendors: zero products committed, but at least one approved
   * purchasing relationship could fulfill a line in this plan — the dock is
   * that product evidence, so a vendor never appears here merely because it
   * exists or sells the same broad category. An explicitly avoided vendor
   * with nothing committed stays listed regardless: the exclusion is a
   * scenario decision, and its Include control is the way back.
   */
  const availableVendors = useMemo(() => columns
    .filter((order) => order.vendorId != null && (order.itemCount || 0) === 0)
    .map((order) => {
      const vendorKey = String(order.vendorId);
      return {
        order,
        vendorKey,
        entries: dockByVendorId.get(vendorKey) || [],
        isExcluded: avoidedVendorIdSet.has(vendorKey),
      };
    })
    .filter(({ entries, isExcluded }) => entries.length > 0 || isExcluded), [
    columns, dockByVendorId, avoidedVendorIdSet,
  ]);

  /**
   * The narrator's opening line for an untouched plan. The scenario
   * explanation (deterministic or AI) narrates CHANGES; before any change
   * exists the summary still has a job — say what the current plan is in
   * operator words, name what is unresolved, and point at the genuine price
   * savings and alternative suppliers on the table. House wording rule holds:
   * "save" only for equivalent-quantity price facts, never for a purchase
   * total.
   */
  const baselinePlanNarration = useMemo(() => {
    const orderedVendors = plan_.vendorOrders.filter((order) => (order.itemCount || 0) > 0);
    const vendorNames = [...new Set(orderedVendors.map((order) => order.vendorName).filter(Boolean))];
    const productCount = plan_.lines.length;
    const stops = scenarioComparison.scenarioPickupStops;
    const sentences = [];
    const vendorPhrase = vendorNames.length === 0
      ? 'no vendors yet'
      : vendorNames.length <= 3
        ? `${vendorNames.length} vendor${vendorNames.length === 1 ? '' : 's'} (${joinVendorNames(vendorNames)})`
        : `${vendorNames.length} vendors`;
    sentences.push(`Your current plan buys ${productCount} product${productCount === 1 ? '' : 's'} from ${vendorPhrase}${stops > 0 ? `, with ${stops} pickup stop${stops === 1 ? '' : 's'}` : ''}.`);
    const blocking = plan_.lines.filter((line) => line.isUnbuyable || line.needsPackageRepair);
    if (blocking.length > 0) {
      const names = blocking.slice(0, 3).map((line) => line.description);
      const more = blocking.length - names.length;
      sentences.push(`${joinVendorNames(names)}${more > 0 ? ` and ${more} more` : ''} still need${blocking.length === 1 ? 's' : ''} a supplier or package check before ordering.`);
    }
    if (Number(plan_.summary.grossPriceSavingsAvailable) > 0) {
      sentences.push(`Switching the flagged items to their lowest unit price would save ${money(plan_.summary.grossPriceSavingsAvailable)} at the same quantities.`);
    }
    if (availableVendors.length > 0) {
      const names = availableVendors.map((entry) => entry.order.vendorName);
      sentences.push(`${joinVendorNames(names.slice(0, 3))}${names.length > 3 ? ` and ${names.length - 3} more` : ''} could also supply items in this plan — see Available vendors under the item list.`);
    }
    return sentences.join(' ');
  }, [plan_, scenarioComparison.scenarioPickupStops, availableVendors]);

  /**
   * Plain-language impact notes for one alternate vendor option, as three
   * separated answers: cash (purchase total, neutral), quantity (what is
   * delivered, neutral), and price (gross savings/premium at equivalent
   * quantities — the only note that may be green). A lower checkout figure
   * that buys less product is stated as cash, never coloured as savings.
   */
  const impactNotesFor = (opt, selectedOpt = null) => {
    const notes = [];
    if (opt.isSubstitute) notes.push({ tone: 'warn', text: 'Substitute product — you approve the change' });
    // Pack and spec are part of identity: a different case count is never a
    // silent swap. Compare in the shared comparison basis when both sides
    // have one, else in the line's own count unit.
    const packPair = (() => {
      if (!selectedOpt || opt.isSubstitute) return null;
      // Number(null) is 0 — an absent quantity must stay absent, or a
      // missing comparison reads as an identical pack.
      const packQty = (value) => (value === null || value === undefined || value === '' ? NaN : Number(value));
      const compA = packQty(opt.comparisonQuantityPerPackage);
      const compB = packQty(selectedOpt.comparisonQuantityPerPackage);
      if (Number.isFinite(compA) && Number.isFinite(compB)) {
        return { a: compA, b: compB, unit: opt.comparisonUnit };
      }
      const invA = packQty(opt.inventoryQuantityPerPackage);
      const invB = packQty(selectedOpt.inventoryQuantityPerPackage);
      if (Number.isFinite(invA) && Number.isFinite(invB)) {
        return { a: invA, b: invB, unit: opt.requiredUnit };
      }
      return null;
    })();
    if (packPair && Math.abs(packPair.a - packPair.b) > 1e-6) {
      const qty = (value) => `${(Math.round(value * 1000) / 1000).toLocaleString()}${packPair.unit ? ` ${humanUnit(packPair.unit, value)}` : ''}`;
      notes.push({
        tone: 'warn',
        text: `Different pack — ${qty(packPair.a)} per ${opt.purchaseUnit || 'package'} instead of ${qty(packPair.b)} per ${selectedOpt.purchaseUnit || 'package'}`,
      });
    }
    // Honest incompleteness: no record ≠ cannot buy. This is an approved,
    // priced option — the note is provenance, not a prohibition. The evidence
    // is OPTION-scoped (this pack, this vendor), so the claim must be too:
    // the store may buy plenty from this vendor through other packs.
    if (!opt.isSelected && !optionEverPurchased(opt.option)) {
      notes.push({ tone: 'neutral', text: 'No purchase on record for this pack yet' });
    }
    if (opt.pickupStopDifferenceIfSelected > 0) notes.push({ tone: 'warn', text: 'Creates a pickup stop' });
    if (opt.pickupStopDifferenceIfSelected < 0) notes.push({ tone: 'ok', text: 'Removes a pickup stop' });
    if (opt.itemCostDifferenceFromSelected != null && opt.itemCostDifferenceFromSelected < -0.004) {
      notes.push({ tone: 'neutral', text: `Purchase total ${money(-opt.itemCostDifferenceFromSelected)} lower on this item` });
    }
    if (opt.itemCostDifferenceFromSelected != null && opt.itemCostDifferenceFromSelected > 0.004) {
      notes.push({ tone: 'neutral', text: `Purchase total ${money(opt.itemCostDifferenceFromSelected)} higher on this item` });
    }
    if (opt.comparableQuantityDifferenceFromSelected != null && opt.comparisonUnit
        && Math.abs(opt.comparableQuantityDifferenceFromSelected) > 1e-6) {
      const qty = Math.round(Math.abs(opt.comparableQuantityDifferenceFromSelected) * 1000) / 1000;
      notes.push({
        tone: 'neutral',
        text: `Delivers ${qty} ${opt.comparisonUnit} ${opt.comparableQuantityDifferenceFromSelected > 0 ? 'more' : 'less'}`,
      });
    }
    if (opt.grossPriceDifferenceFromSelected != null
        && Math.abs(opt.grossPriceDifferenceFromSelected) > 0.004) {
      const saving = opt.grossPriceDifferenceFromSelected > 0;
      notes.push({
        tone: saving ? 'ok' : 'warn',
        text: saving
          ? `Gross price savings ${money(opt.grossPriceDifferenceFromSelected)} at the same quantity`
          : `Gross price premium ${money(-opt.grossPriceDifferenceFromSelected)} at the same quantity`,
      });
    }
    if (opt.feeDifferenceIfSelected != null && opt.feeDifferenceIfSelected < -0.004) {
      notes.push({ tone: 'ok', text: `Avoids ${money(-opt.feeDifferenceIfSelected)} in fees` });
    }
    if (opt.feeDifferenceIfSelected != null && opt.feeDifferenceIfSelected > 0.004) {
      notes.push({ tone: 'warn', text: `Adds ${money(opt.feeDifferenceIfSelected)} in fees` });
    }
    if (opt.vendorCountDifferenceIfSelected != null && opt.vendorCountDifferenceIfSelected < 0) {
      notes.push({ tone: 'ok', text: 'One less vendor order' });
    }
    if (opt.vendorCountDifferenceIfSelected != null && opt.vendorCountDifferenceIfSelected > 0) {
      notes.push({ tone: 'warn', text: 'Adds another vendor order' });
    }
    if (opt.landedOrderDifferenceIfSelected != null
      && Math.abs(opt.landedOrderDifferenceIfSelected) > 0.004) {
      const lower = opt.landedOrderDifferenceIfSelected < 0;
      notes.push({
        tone: 'neutral',
        text: `Whole-plan purchase total ${money(Math.abs(opt.landedOrderDifferenceIfSelected))} ${lower ? 'lower' : 'higher'}`,
      });
    }
    if (opt.moqDrivesOrder) notes.push({ tone: 'warn', text: 'Vendor minimum raises the buy' });
    if (opt.needsComparisonReview) notes.push({ tone: 'warn', text: 'Package comparison needs confirmation' });
    return notes;
  };

  /* ── Row rendering ──────────────────────────────────────────────────────── */

  const renderLineRow = (line, { draggable = false } = {}) => {
    const key = String(line.lineId);
    const order = orderByLineId.get(key) || null;
    const vendorKey = order?.vendorId == null ? null : String(order.vendorId);
    const isComplete = vendorKey != null && completedVendorIds.includes(vendorKey);
    const vendor = vendorKey != null ? vendorsById[vendorKey] : order?.vendor;
    const option = line.selected?.option || {};
    const expanded = !!expandedLineIds[key];
    const isRecommended = recommendedByLineId[key] != null
      && String(line.selectedOptionId ?? '') === recommendedByLineId[key];
    const isChanged = changedLineIdSet.has(key);
    const scenarioChange = scenarioChangeByLineId.get(key) || null;
    const recommendedLine = recommendedLineById.get(key) || null;
    const recommendedOrder = recommendedOrderByLineId.get(key) || null;
    const recommendedVendorName = recommendedOrder?.vendorName
      || vendorsById[scenarioChange?.fromVendorId]?.display_name
      || 'the recommended vendor';
    // The reason the DATABASE recorded when the line was created is the only
    // one that knows why this option was pinned. A scenario replan carries its
    // own planner reason; a direct click carries explicit.
    const storedReason = String(line.selectionReason ?? '') === 'explicit'
      ? (lines.find((l) => String(l.id) === key)?.selection_reason ?? null)
      : line.selectionReason;
    const reasonKey = storedReason;
    const reasonChip = REASON_CHIP[reasonKey] || null;
    const lastUsed = lastUsedByLineId[key] || null;
    const sourceMarks = sourceMarksFor(line);
    const alternatives = expanded && !line.needsPackageRepair ? alternativesFor(line) : [];
    const selectedAlt = alternatives.find((alt) => alt.isSelected) || null;
    const otherAlternatives = alternatives.filter((alt) => !alt.isSelected);
    const elsewhere = (line.options?.length || 0) > 1;
    const whyOpen = !!whyLineIds[key];
    const recommendedAlternatives = whyOpen && recommendedLine
      ? buildOptionFulfillmentComparison({
        line: recommendedLine,
        options: pooledOptionsByItemId[recommendedLine.inventoryItemId] || [],
        comparison: comparisonByItemId[recommendedLine.inventoryItemId] || null,
        item: itemsById[recommendedLine.inventoryItemId] || null,
        vendorsById,
        ...candidateContextFor(recommendedLine.inventoryItemId),
        itemsById,
        planArgs: {
          ...planArgs,
          lines: plannedLines.map((planned) => ({ ...planned, selected_purchase_option_id: null })),
          explicitByLineId: recommendedByLineId,
        },
      })
      : [];
    const recommendedChoice = recommendedAlternatives.find((alt) => alt.isSelected) || null;
    const recommendedAlternates = recommendedAlternatives
      .filter((alt) => !alt.isSelected && alt.landedOrderDifferenceIfSelected != null)
      .sort((a, b) => a.landedOrderDifferenceIfSelected - b.landedOrderDifferenceIfSelected);

    return (
      <li
        key={line.lineId}
        data-testid="savings-review-item"
        data-line-id={line.lineId}
        className={[
          'srw-item',
          expanded ? 'is-open' : '',
          justLanded === line.lineId ? 'just-landed' : '',
          highlightedLineIds.map(String).includes(key) ? 'is-stale-changed' : '',
          isChanged ? 'is-changed' : '',
          dragging?.lineId === line.lineId ? 'is-dragging' : '',
          isComplete ? 'is-locked' : '',
        ].filter(Boolean).join(' ')}
        draggable={draggable && !isComplete && elsewhere && !arrangementFrozen}
        onDragStart={draggable ? handleDragStart(line) : undefined}
        onDragEnd={draggable ? handleDragEnd : undefined}
        title={draggable
          ? (elsewhere ? 'Drag to another vendor to buy it there' : 'Only this vendor carries this product')
          : undefined}
      >
        <div
          className="srw-row"
          role="button"
          tabIndex={0}
          data-testid="savings-review-row-toggle"
          aria-expanded={expanded}
          onClick={() => toggleExpanded(line.lineId)}
          onKeyDown={(event) => {
            if (event.key === 'Enter' || event.key === ' ') {
              event.preventDefault();
              toggleExpanded(line.lineId);
            }
          }}
        >
          <div className="srw-row-name">
            <strong>{line.description}</strong>
            {/* The INVARIANT, said in full. This number is an inventory fact
                — PAR minus what was counted — and it does not move when the
                line is dragged to another vendor. Only the lines below it do. */}
            <span className="srw-row-need" data-testid="savings-review-counted-need">
              Counted result: Need {line.buyDisplay?.needText || quantityText(line, line.quantityNeeded)} to reach PAR
            </span>
            <span className="srw-row-badges">
              {line.selected?.isSubstitute && (
                <span className="srw-badge srw-badge-warn" data-testid="savings-review-selected-substitute">
                  Substitute
                </span>
              )}
              {line.needsPackageRepair && (
                <span className="srw-badge srw-badge-warn">Package check</span>
              )}
              {line.isUnbuyable && (
                <span className="srw-badge srw-badge-warn">No vendor</span>
              )}
              {isChanged && (
                <span className="srw-badge srw-badge-changed" data-testid="savings-review-changed-badge">Scenario choice</span>
              )}
                {!isChanged && isRecommended && line.hasChoice && (
                  <span className="srw-badge srw-badge-rec">Recommended</span>
                )}
                {recommendedLine?.selectedOptionId && (
                  <button
                    type="button"
                    className="srw-why-btn"
                    data-testid="savings-review-why"
                    aria-expanded={whyOpen}
                    onClick={(event) => {
                      event.stopPropagation();
                      setWhyLineIds((prev) => ({ ...prev, [key]: !prev[key] }));
                    }}
                  >
                    Why recommended?
                  </button>
                )}
            </span>
            {/* Source rail: this item's other lanes, graphical first. Solid
                mark = same product, provably same pack; dashed = something
                needs eyes (different pack, substitute, or pack uncompared).
                The glyph is the spend direction; the words live in the title.
                Clicking opens the row — the existing alternates list is the
                popover, so exploring never invents a second surface. */}
            {sourceMarks.length > 0 && (
              <span className="srw-source-rail" data-testid="savings-review-source-rail">
                <span className="srw-source-rail-label">Also at</span>
                {sourceMarks.slice(0, 2).map((mark) => (
                  <button
                    key={mark.vendorId}
                    type="button"
                    className={mark.confirmed ? 'srw-srcmark' : 'srw-srcmark is-unverified'}
                    data-testid="savings-review-source-mark"
                    data-vendor-id={mark.vendorId}
                    title={sourceMarkTitle(mark)}
                    aria-label={sourceMarkTitle(mark)}
                    onClick={(event) => {
                      event.stopPropagation();
                      setExpandedLineIds((prev) => ({ ...prev, [key]: true }));
                    }}
                  >
                    <span className="srw-srcmark-name">{mark.vendorName}</span>
                    <span className="srw-srcmark-dir" aria-hidden="true">
                      {SOURCE_DIRECTION_GLYPH[mark.direction]}
                    </span>
                  </button>
                ))}
                {sourceMarks.length > 2 && (
                  <button
                    type="button"
                    className="srw-srcmark srw-srcmark-more"
                    data-testid="savings-review-source-more"
                    title={`${sourceMarks.length - 2} more vendor${sourceMarks.length - 2 === 1 ? '' : 's'} could supply this — open the row to review`}
                    onClick={(event) => {
                      event.stopPropagation();
                      setExpandedLineIds((prev) => ({ ...prev, [key]: true }));
                    }}
                  >
                    +{sourceMarks.length - 2}
                  </button>
                )}
              </span>
            )}
          </div>
          {!line.needsPackageRepair && !line.isUnbuyable && (
            <>
              <div className="srw-row-vendor">
                <strong>{order?.vendorName || vendor?.display_name || 'Vendor'}</strong>
                {reasonChip && (
                  <span
                    className={`srw-reason${line.selectionReason === 'explicit' ? ' srw-reason-yours' : ''}`}
                    data-testid="savings-review-reason-chip"
                  >
                    {reasonChip}
                  </span>
                )}
              </div>
              {/* Its own cell, on a fixed track, so DELIVERY and PICKUP land on
                  the same x down the whole list — and an unanswered vendor
                  leaves the tab empty rather than collapsing it and shifting
                  everything after it. */}
              <div className="srw-row-fulfill">
                <FulfillmentBadge vendor={vendor || order?.vendor} />
              </div>
              <div className="srw-row-buy">
                {line.buyDisplay?.buyText ? (
                  <>
                    <span className="srw-buy">Vendor order: Buy {line.buyDisplay.buyText}</span>
                    {line.buyDisplay.packText
                      ? <span className="srw-pack-line">{line.buyDisplay.packText}</span> : null}
                    {line.buyDisplay.receiveText
                      ? <span className="srw-pack-line" data-testid="savings-review-delivers">Delivers: {line.buyDisplay.receiveText}</span>
                      : null}
                    {line.buyDisplay.extraText
                      ? <span className="srw-pack-line">{line.buyDisplay.extraText} above PAR</span>
                      : null}
                  </>
                ) : (
                  <>
                    <span className="srw-buy">
                      Vendor order: Buy {line.selected?.packages ?? '—'} {plural(line.selected?.packages ?? 1, option.purchase_unit || 'package')}
                    </span>
                    {/* The auto-resolved package fact, with its source
                        named — this came from the vendor's own text, not
                        an AI guess, and Edit is one click away when the
                        vendor data is wrong. Falls back to the raw pack
                        description when nothing deterministic resolved. */}
                    {(() => {
                      const qty = Number(line.selected?.quantityPerPackage);
                      const source = line.selected?.needConversionSource;
                      const deterministic = ['same_unit', 'deterministic_conversion', 'physical_bridge', 'confirmed_conversion', 'manual_conversion'].includes(source);
                      if (!(qty > 0) || !line.countUnit || !deterministic) {
                        return option.pack_description ? <span className="srw-pack-line">{option.pack_description}</span> : null;
                      }
                      const unitWord = (value) => String(value || '').trim().toLowerCase().replace(/s$/, '');
                      const identity = qty === 1 && unitWord(option.purchase_unit) === unitWord(line.countUnit);
                      return (
                        <span className="srw-pack-line srw-pack-auto" data-testid="savings-review-auto-resolved">
                          {identity
                            ? `1 ${humanUnit(line.countUnit, 1)}`
                            : `${qty} ${humanUnit(line.countUnit, qty)}/${option.purchase_unit || 'package'}`}
                          {option.pack_description
                            ? `${identity ? ' · Sold as' : ' · Read from'} “${option.pack_description}”`
                            : ''}
                          {!isComplete && (
                            <button
                              type="button"
                              className="srw-link-btn"
                              data-testid="savings-review-auto-resolved-edit"
                              onClick={(event) => {
                                event.stopPropagation();
                                setExpandedLineIds((prev) => ({ ...prev, [key]: true }));
                                setDetailsOptionKeys((prev) => ({
                                  ...prev,
                                  [`${line.lineId}:${line.selectedOptionId}`]: true,
                                }));
                              }}
                            >
                              Edit
                            </button>
                          )}
                        </span>
                      );
                    })()}
                    {line.selected?.quantityReceived != null
                      ? <span className="srw-pack-line" data-testid="savings-review-delivers">Delivers: {quantityText(line, line.selected.quantityReceived)}</span>
                      : null}
                    {line.selected?.extraQuantity
                      ? <span className="srw-pack-line">{quantityText(line, line.selected.extraQuantity)} above PAR</span>
                      : null}
                  </>
                )}
                {BUY_REASON_TEXT[line.orderDriver]
                  ? <span className="srw-buy-reason">{BUY_REASON_TEXT[line.orderDriver]}</span>
                  : null}
              </div>
              <div className="srw-row-cost">
                {/* An order we cannot cost says so. It is still a real line
                    with a real package quantity — it just cannot enter a
                    total, win a badge, or claim savings. */}
                {line.orderCost == null ? (
                  <strong data-testid="savings-review-price-unavailable" className="srw-price-unavailable">
                    Price unavailable
                  </strong>
                ) : (
                  <strong>{money(line.orderCost)}</strong>
                )}
                {line.selected?.comparableUnitCost != null && line.comparisonUnit && (
                  <small>{unitMoney(line.selected.comparableUnitCost)}/{line.comparisonUnit}</small>
                )}
                {line.selected?.isBestUnitPrice && <span className="srw-badge srw-badge-unit">Lowest unit price</span>}
                {lastUsed && String(line.selectedOptionId ?? '') === String(lastUsed.optionId) && (
                  <span className="srw-badge srw-badge-used" title={lastUsedTitle(lastUsed)} data-testid="savings-review-last-used">
                    Last used
                  </span>
                )}
              </div>
            </>
          )}
          <span className="srw-row-chevron" aria-hidden="true">{expanded ? '▾' : '▸'}</span>
        </div>

        {scenarioChange && (
          <div className="srw-changed-from" data-testid="savings-review-changed-from">
            Changed from {recommendedVendorName}
            {scenarioChange.economicImpactComplete
              ? ` · ${scenarioChangeHeadline(scenarioChange)} on this item`
              : ' · Cost comparison incomplete'}
          </div>
        )}

        {whyOpen && recommendedLine && (
          <section className="srw-why-panel" data-testid="savings-review-why-panel">
            <strong>Why Countertop chose {recommendedVendorName}</strong>
            <p>
              {recommendedChoice?.selectionReasonText
                || 'This was the selected option in Countertop’s recommended plan.'}
            </p>
            <dl>
              {(() => {
                const parInfo = parContextByItemId[String(line.inventoryItemId)] || null;
                if (!parInfo || !Number.isFinite(Number(parInfo.parLevel))) return null;
                return (
                  <div data-testid="savings-review-why-par">
                    <dt>PAR</dt>
                    <dd>
                      {quantityText(line, parInfo.parLevel)}
                      {parInfo.parSource ? ` · ${parInfo.parSource}` : ''}
                    </dd>
                  </div>
                );
              })()}
              <div><dt>Product cost</dt><dd>{money(recommendedLine.orderCost)}</dd></div>
              <div>
                <dt>Package needed</dt>
                <dd>
                  {recommendedLine.buyDisplay?.buyText
                    || `${recommendedLine.selected?.packages ?? '—'} ${plural(recommendedLine.selected?.packages ?? 1, recommendedChoice?.purchaseUnit || 'package')}`}
                </dd>
              </div>
              <div>
                <dt>Pickup stop</dt>
                <dd>{recommendedOrder?.isPickup ? 'Included in recommended plan' : 'None'}</dd>
              </div>
              <div><dt>Additional vendor fees</dt><dd>{money(recommendedOrder?.charges?.totalCharges || 0)}</dd></div>
            </dl>
            {recommendedAlternates.length > 0 && (
              <ul>
                {recommendedAlternates.slice(0, 3).map((alt) => {
                  // The tradeoff, said whole: cash, quantity, and — when a
                  // verified same-unit comparison exists — the price fact.
                  const clauses = [];
                  const cash = Number(alt.itemCostDifferenceFromSelected);
                  if (Number.isFinite(cash) && Math.abs(cash) > 0.004) {
                    clauses.push(`costs ${money(Math.abs(cash))} ${cash > 0 ? 'more' : 'less'} at checkout`);
                  }
                  const qtyDiff = Number(alt.comparableQuantityDifferenceFromSelected);
                  if (Number.isFinite(qtyDiff) && Math.abs(qtyDiff) > 1e-6 && alt.comparisonUnit) {
                    clauses.push(`delivers ${Math.round(Math.abs(qtyDiff) * 1000) / 1000} ${alt.comparisonUnit} ${qtyDiff > 0 ? 'more' : 'less'}`);
                  }
                  const rateDelta = Number.isFinite(Number(alt.comparableUnitCost))
                    && Number.isFinite(Number(recommendedChoice?.comparableUnitCost))
                    && alt.comparisonTrustworthy && recommendedChoice?.comparisonTrustworthy
                    ? Number(recommendedChoice.comparableUnitCost) - Number(alt.comparableUnitCost)
                    : null;
                  if (rateDelta !== null && Math.abs(rateDelta) > 0.0004 && alt.comparisonUnit) {
                    clauses.push(`${rateDelta > 0 ? 'saves' : 'adds'} ${money(Math.abs(rateDelta))} per ${alt.comparisonUnit}`);
                  }
                  const gross = Number(alt.grossPriceDifferenceFromSelected);
                  const grossClause = Number.isFinite(gross) && Math.abs(gross) > 0.004
                    ? ` Gross price ${gross > 0 ? 'savings' : 'premium'} on that quantity: ${money(Math.abs(gross))}.`
                    : '';
                  const clauseSentence = clauses.length
                    ? ` ${clauses[0]}${clauses.length > 1 ? ` but ${clauses.slice(1).join(' and ')}` : ''}.`
                    : ` would change the landed plan by ${signedMoney(alt.landedOrderDifferenceIfSelected)}.`;
                  return (
                    <li key={alt.optionId}>
                      {alt.vendorName}
                      {clauseSentence}
                      {grossClause}
                      {clauses.length > 0 && alt.landedOrderDifferenceIfSelected != null
                        ? ` Whole plan: ${signedMoney(alt.landedOrderDifferenceIfSelected)}.`
                        : ''}
                    </li>
                  );
                })}
              </ul>
            )}
          </section>
        )}

        {/* The switch's own state, on the row that moved. "Saving…" says the
            arrangement is not yet real; an error says it never became real and
            names the reason where the buyer is already looking. */}
        {pendingMoves[key] && (
          <div className="srw-move-state" data-testid="savings-review-move-pending">
            Saving…
          </div>
        )}
        {moveErrors[key] && (
          <div className="srw-move-error" data-testid="savings-review-move-error">
            {moveErrors[key]}
          </div>
        )}
        {rejected?.lineId === line.lineId && (
          <div className="srw-item-reject" data-testid="savings-review-reject">
            {rejected.message}
          </div>
        )}

        {expanded && (
          <div className="srw-row-detail" data-testid="savings-review-row-detail">
            {line.needsPackageRepair && (
              <>
                <p className="srw-detail-intro">
                  This item stays out of vendor totals until its package is safe to order.
                </p>
                {line.packageReviewOptions.map((review) => (
                  <PackageRepairCard
                    key={review.optionId}
                    line={line}
                    review={review}
                    vendorsById={vendorsById}
                    analysis={packageAnalysisByOptionId[String(review.optionId)] || null}
                    onAnalyzePackage={onAnalyzePackage}
                    onConfirmPackage={onConfirmPackage}
                  />
                ))}
              </>
            )}
            {line.substituteReviewOptions?.map((review) => (
              <React.Fragment key={review.optionId}>
                <div className="srw-calc-note" data-testid="savings-review-substitute-unresolved">
                  Substitute — price comparison needs quantity confirmation.
                </div>
                <PackageRepairCard
                  line={line}
                  review={review}
                  vendorsById={vendorsById}
                  analysis={packageAnalysisByOptionId[String(review.optionId)] || null}
                  onAnalyzePackage={onAnalyzePackage}
                  onConfirmPackage={onConfirmPackage}
                />
              </React.Fragment>
            ))}
            {line.isUnbuyable && (
              <p className="srw-detail-intro">
                No eligible vendor carries this item yet — add a purchase option to order it.
              </p>
            )}
            {!line.needsPackageRepair && alternatives.length > 0 && (
              <>
                {selectedAlt?.selectionReasonText && (
                  <div className="srw-calc-note" data-testid="savings-review-reason">
                    {selectedAlt.selectionReasonText}
                  </div>
                )}
                <div className="srw-alt-list">
                  {[selectedAlt, ...otherAlternatives].filter(Boolean).map((opt) => {
                    const optVendor = opt.vendorId != null ? vendorsById[opt.vendorId] : null;
                    const notes = opt.isSelected ? [] : impactNotesFor(opt, selectedAlt);
                    const detailsKey = `${line.lineId}:${opt.optionId}`;
                    const showingDetails = !!detailsOptionKeys[detailsKey];
                    return (
                      <div
                        key={opt.optionId}
                        className={opt.isSelected ? 'srw-alt is-current' : 'srw-alt'}
                        data-testid="savings-review-back-option"
                      >
                        <div className="srw-alt-main">
                          <div className="srw-alt-vendor">
                            <strong>
                              {opt.isSubstitute ? 'SUBSTITUTE — ' : ''}{opt.vendorName}
                            </strong>
                            <FulfillmentBadge vendor={optVendor} />
                            {opt.isSelected && (
                              <span className="srw-badge srw-badge-rec">Current choice</span>
                            )}
                            {opt.isBestUnitPrice && !opt.isSelected && (
                              <span className="srw-badge srw-badge-unit">Lowest unit price</span>
                            )}
                            {opt.isLowestOrderCost && !opt.isSelected && (
                              <span className="srw-badge srw-badge-cost">Lowest order cost</span>
                            )}
                            {lastUsed && String(opt.optionId) === String(lastUsed.optionId) && !opt.isSelected && (
                              <span className="srw-badge srw-badge-used" title={lastUsedTitle(lastUsed)}>Last used</span>
                            )}
                          </div>
                          <div className="srw-alt-pack">
                            {opt.packageDescription || opt.purchaseUnit}
                            {opt.packagesRequired != null && (
                              <> · Buy {opt.packagesRequired} {plural(opt.packagesRequired, opt.purchaseUnit)}</>
                            )}
                          </div>
                        </div>
                        <div className="srw-alt-side">
                          <strong className="srw-alt-price">{money(opt.itemOrderTotal)}</strong>
                          {!opt.isSelected && opt.itemCostDifferenceFromSelected != null
                            && Math.abs(opt.itemCostDifferenceFromSelected) > 0.004 && (
                            <small className={opt.itemCostDifferenceFromSelected > 0 ? 'srw-diff-up' : 'srw-diff-down'}>
                              {opt.itemCostDifferenceFromSelected > 0 ? '+' : '−'}
                              {money(Math.abs(opt.itemCostDifferenceFromSelected))} vs current
                            </small>
                          )}
                          {opt.comparableUnitCost != null && opt.comparisonUnit && (
                            <small>{unitMoney(opt.comparableUnitCost)}/{opt.comparisonUnit}</small>
                          )}
                        </div>
                        {/* The tradeoff notes span the WHOLE card. They used to
                            live beside the action buttons, whose auto-sized
                            column squeezed them into a one-word-per-line
                            ribbon on narrow cards. */}
                        {notes.length > 0 && (
                          <ul className="srw-alt-notes">
                            {notes.map((note) => (
                              <li
                                key={note.text}
                                className={note.tone === 'ok'
                                  ? 'srw-note-ok'
                                  : note.tone === 'neutral'
                                    ? 'srw-note-neutral'
                                    : 'srw-note-warn'}
                              >
                                {note.text}
                              </li>
                            ))}
                          </ul>
                        )}
                        <div className="srw-alt-actions">
                          {/* The current choice offers no button — you cannot
                              switch to where you already are. */}
                          {!opt.isSelected && !isComplete && (
                            <button
                              type="button"
                              className="srw-review-btn srw-switch-btn"
                              data-testid="savings-review-choose-option"
                              disabled={arrangementFrozen}
                              title={arrangementFrozen
                                ? 'Orders are being created — vendors cannot be changed until that finishes'
                                : undefined}
                              onClick={() => requestSwitch(line, opt.optionId, opt.vendorId)}
                            >
                              Change order to {opt.vendorName}
                            </button>
                          )}
                          <button
                            type="button"
                            className="srw-link-btn"
                            data-testid="savings-review-review-details"
                            aria-expanded={showingDetails}
                            onClick={() => toggleDetails(line.lineId, opt.optionId)}
                          >
                            {showingDetails ? 'Hide full comparison' : 'Show full comparison'}
                          </button>
                        </div>
                        {showingDetails && (
                          <OptionDetailPanel
                            opt={opt}
                            line={line}
                            onConfirmPackage={onConfirmPackage}
                            onEditOrderingRules={onEditOrderingRules}
                            locked={isComplete}
                          />
                        )}
                      </div>
                    );
                  })}
                </div>
              </>
            )}
          </div>
        )}
      </li>
    );
  };

  /* ── Vendor scenario tab ───────────────────────────────────────────────── */

  const renderVendorCard = (order) => {
    const vendorKey = order.vendorId == null ? null : String(order.vendorId);
    const isComplete = vendorKey != null && completedVendorIds.includes(vendorKey);
    const draftPo = vendorKey ? draftPoByVendorId[vendorKey] : null;
    const readiness = vendorReadiness(order, completedVendorIds);
    const pill = buyingModePill(vendorsById[order.vendorId]);
    const isExcluded = vendorKey != null && avoidedVendorIdSet.has(vendorKey);
    // The dock: what could land here but currently does not. A finished
    // vendor's arrangement is frozen, so its dock is not offered.
    const dockEntries = vendorKey != null && !isComplete
      ? (dockByVendorId.get(vendorKey) || [])
      : [];
    const dockConfirmedCount = dockEntries.filter((entry) => entry.confirmed).length;
    const dockReviewCount = dockEntries.length - dockConfirmedCount;
    const dockOpen = vendorKey != null && !!openDockVendorIds[vendorKey];
    const actionPreview = vendorKey && !isComplete ? vendorActionPreviewById[vendorKey] : null;
    const recordedFeeParts = [order.deliveryFee, order.fuelSurcharge, order.belowMinimumFee]
      .filter((value) => value !== null && value !== undefined && Number.isFinite(Number(value)));
    const hasRecordedFees = order.deliveryFee !== null && order.deliveryFee !== undefined
      || order.fuelSurcharge !== null && order.fuelSurcharge !== undefined
      || Number(order.belowMinimumFee) > 0;
    const feeTotal = recordedFeeParts.reduce((sum, value) => sum + Number(value), 0);
    const feeLabel = order.isPickup
      ? 'Pickup fees'
      : order.fulfillmentAnswered
        ? 'Delivery + fees'
        : 'Order fees';
    // The card's consequence preview — see vendorActionImpactLines for the
    // separated cash/quantity/price wording contract.
    const actionImpactLines = actionPreview
      ? vendorActionImpactLines(
        order,
        actionPreview,
        isExcluded,
        vendorExclusionReviewEnabled,
      )
      : null;
    return (
      <section
        key={order.groupKey || (vendorKey ? `vendor:${vendorKey}` : '__unassigned__')}
        role="listitem"
        data-testid="savings-review-vendor"
        data-vendor-id={vendorKey || ''}
        className={[
          'srw-vendor-card',
          isComplete ? 'is-complete' : '',
          isExcluded ? 'is-excluded' : '',
          readiness.key === 'pickup' ? 'is-pickup' : '',
          readiness.key === 'below-minimum' ? 'is-below-min' : '',
        ].filter(Boolean).join(' ')}
      >
        <div className="srw-vendor-card-top">
          <div className="srw-vendor-head">
            <strong>{order.vendorName}</strong>
            {order.vendorLinked === false ? (
              <span
                className="srw-tag srw-tag-unset"
                title="This imported vendor name is not linked to a vendor record yet"
              >
                Not linked
              </span>
            ) : pill && (
              <span
                className={pill.tone === 'info' ? 'srw-tag srw-tag-pickup' : 'srw-tag'}
                title={pill.title}
              >
                {pill.text}
              </span>
            )}
            {isExcluded && <span className="srw-tag srw-tag-unset">Avoiding</span>}
          </div>
          {!isComplete && vendorKey != null && (
            <button
              type="button"
              data-testid={isExcluded ? 'savings-review-include-vendor' : 'savings-review-avoid-vendor'}
              className={isExcluded
                ? 'srw-vendor-toggle is-include'
                : 'srw-vendor-toggle is-remove'}
              disabled={arrangementFrozen || hasPendingMoves}
              onClick={() => (isExcluded
                ? handleRemoveAvoidedVendor(vendorKey)
                : vendorExclusionReviewEnabled
                  ? handleStartVendorExclusion(vendorKey)
                  : handleAvoidVendor(vendorKey))}
              aria-label={isExcluded
                ? `Include ${order.vendorName}`
                : vendorExclusionReviewEnabled
                  ? `Exclude ${order.vendorName} from this order`
                  : `Remove ${order.vendorName}`}
            >
              {isExcluded
                ? 'Include'
                : vendorExclusionReviewEnabled ? 'Exclude from this order' : 'Remove'}
            </button>
          )}
        </div>
        <div className="srw-vendor-sub">
          {order.itemCount} item{order.itemCount === 1 ? '' : 's'} in this order
        </div>
        {/* The open lane (dock). A vendor with nothing committed but items
            that could land here is a lane to open, not an empty $0.00 card —
            and an Avoiding vendor keeps its dock so exploring it stays one
            click away without committing anything. */}
        {dockEntries.length > 0 && (
          <div className="srw-vendor-dock">
            <button
              type="button"
              className="srw-vendor-dock-toggle"
              data-testid="savings-review-vendor-dock"
              aria-expanded={dockOpen}
              onClick={() => setOpenDockVendorIds((prev) => ({
                ...prev,
                [vendorKey]: !prev[vendorKey],
              }))}
            >
              {dockEntries.length} item{dockEntries.length === 1 ? '' : 's'} could land here
              {dockConfirmedCount > 0 ? ` · ${dockConfirmedCount} confirmed` : ''}
              {dockReviewCount > 0 ? ` · ${dockReviewCount} to review` : ''}
            </button>
            {dockOpen && (
              <ul className="srw-vendor-dock-list" data-testid="savings-review-vendor-dock-list">
                {dockEntries.map((entry) => {
                  // A line whose current vendor is FINISHED already sits on a
                  // created PO or shopping list. The lane is still real, but
                  // one-click moving it would diverge the screen from that
                  // order — return the draft first, like every other gesture.
                  const sourceFinished = entry.line.vendorId != null
                    && completedVendorIds.includes(String(entry.line.vendorId));
                  return (
                  <li key={entry.line.lineId} data-testid="savings-review-vendor-dock-item">
                    <span className="srw-dock-name">{entry.line.description}</span>
                    <span className="srw-dock-note">
                      {dockNoteText(entry)}
                      {sourceFinished ? ' · in a finished order — return its draft to change it' : ''}
                    </span>
                    {/* An excluded vendor's dock is exploration only — the
                        card's Include button is the path back in. A confirmed
                        same-pack lane moves in one click through the same
                        switch path every other gesture uses; anything less
                        than confirmed goes to the row for review, never a
                        silent swap. */}
                    {!isExcluded && !sourceFinished && (entry.confirmed ? (
                      <button
                        type="button"
                        className="srw-link-btn"
                        data-testid="savings-review-dock-move"
                        disabled={arrangementFrozen}
                        onClick={() => requestSwitch(entry.line, entry.optionId, order.vendorId)}
                      >
                        Move here
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="srw-link-btn"
                        data-testid="savings-review-dock-review"
                        onClick={() => handleChooseReplacement(entry.line.lineId)}
                      >
                        Review
                      </button>
                    ))}
                  </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
        <dl className="srw-vendor-money" data-testid="savings-review-vendor-money">
          <div>
            <dt>Product subtotal</dt>
            <dd>{money(order.subtotal)}</dd>
          </div>
          <div>
            <dt>{feeLabel}</dt>
            <dd>{hasRecordedFees ? money(feeTotal) : '—'}</dd>
          </div>
          <div>
            <dt>Tax</dt>
            <dd className="srw-tax-placeholder">Not estimated</dd>
          </div>
          <div className="srw-vendor-money-total">
            <dt>Estimated order</dt>
            <dd>{money(order.landedTotal ?? order.subtotal)}</dd>
          </div>
        </dl>
        {actionImpactLines && actionImpactLines.length > 0 && (
          <div className="srw-vendor-impact" data-testid="savings-review-vendor-impact">
            {actionImpactLines.map((line) => (
              <span
                key={line.text}
                className={line.tone === 'savings'
                  ? 'srw-impact-savings'
                  : line.tone === 'warn'
                    ? 'srw-impact-warn'
                    : 'srw-impact-neutral'}
              >
                {line.text}
              </span>
            ))}
          </div>
        )}
        <div className="srw-vendor-flags">
          <span className={`srw-readiness srw-readiness-${readiness.key}`} data-testid="savings-review-readiness">
            {readiness.label}
          </span>
          {order.itemCount > 0 && order.minimumMet === false && (
            <span className="srw-flag srw-flag-warn">
              {money(order.shortfall)} under {money(order.minimumOrderAmount)} minimum
            </span>
          )}
          {order.itemCount > 0 && order.minimumMet === true && (
            <span className="srw-flag srw-flag-ok">Minimum met</span>
          )}
          {order.itemCount > 0 && order.minimumMet === null && (
            <span className="srw-flag">No minimum on record</span>
          )}
          {Number(order.deliveryFee) > 0 && (
            <span className="srw-flag">+{money(order.deliveryFee)} delivery</span>
          )}
          {Number(order.deliveryFee) === 0 && order.vendor?.delivery_fee > 0 && (
            <span className="srw-flag srw-flag-ok">Free delivery</span>
          )}
          {Number(order.fuelSurcharge) > 0 && (
            <span className="srw-flag">+{money(order.fuelSurcharge)} fuel</span>
          )}
        </div>
        {(order.itemCount || 0) > 0 && order.vendorLinked === false && (
          <p className="srw-vendor-blocked">Link this vendor before Done can create its order.</p>
        )}
        {isComplete && (
          <div className="srw-total-done">
            {order.isPickup ? 'Shopping list created' : 'Purchase order created'}
          </div>
        )}
        {/* Finishing is not final while the draft is still unsent. Handing it
            back unlocks exactly this vendor — a buyer who spots a better price
            a minute later should not have to delete the draft and rebuild the
            arrangement. */}
        {isComplete && draftPo && onReturnDraft && (
          <button
            type="button"
            data-testid="savings-review-return-draft"
            className="srw-btn srw-btn-undo"
            disabled={!!busyVendorId}
            onClick={() => onReturnDraft(order.vendorId)}
            title={`Delete draft ${draftPo.po_number || ''} and put its items back in this purchase plan`.trim()}
          >
            {busyVendorId != null && busyVendorId === vendorKey ? 'Working…' : '↩ Return draft to this plan'}
          </button>
        )}
      </section>
    );
  };

  /* ── Available vendors ─────────────────────────────────────────────────── */

  /**
   * One quiet tile per vendor that could supply something but currently
   * supplies nothing. Much smaller than an active card on purpose: no money
   * grid, no $0.00, no order accounting — just "you could source some of this
   * order here", the specific products as evidence, and the same
   * drag/move/review gestures every other surface uses. Dropping a product
   * here (or pressing Move) makes the vendor active, which grows its full
   * card and removes this tile — the plan rebuild does both.
   */
  const renderAvailableVendor = ({ order, vendorKey, entries, isExcluded }) => {
    const pill = buyingModePill(vendorsById[order.vendorId]);
    const expanded = !!openDockVendorIds[vendorKey];
    const isHover = hoverVendor === vendorKey && canDropOn(order.vendorId);
    const isBlocked = !!dragging && !canDropOn(order.vendorId);
    const actionPreview = vendorActionPreviewById[vendorKey] || null;
    // Only the excluded tile previews its one action ("Including X …"); a
    // plain available vendor has no items, so a remove preview says nothing.
    const impactLines = isExcluded && actionPreview
      ? vendorActionImpactLines(order, actionPreview, true)
      : null;
    const productNames = [...new Set(entries.map((entry) => entry.line.description))];
    const namesShown = productNames.slice(0, 4);
    return (
      <section
        key={`available:${vendorKey}`}
        role="listitem"
        data-testid="savings-review-available-vendor"
        data-vendor-id={vendorKey}
        className={[
          'srw-available-vendor',
          isExcluded ? 'is-excluded' : '',
          isHover ? 'is-target' : '',
          isBlocked ? 'is-blocked' : '',
        ].filter(Boolean).join(' ')}
        onDragOver={(event) => {
          if (!canDropOn(order.vendorId)) return;
          event.preventDefault();
          setHoverVendor(vendorKey);
        }}
        onDragLeave={() => setHoverVendor((cur) => (cur === vendorKey ? null : cur))}
        onDrop={handleDrop(order.vendorId)}
      >
        <div className="srw-available-top">
          <div className="srw-available-name">
            <strong>{order.vendorName}</strong>
            {pill && (
              <span
                className={pill.tone === 'info' ? 'srw-tag srw-tag-pickup' : 'srw-tag'}
                title={pill.title}
              >
                {pill.text}
              </span>
            )}
            {isExcluded && <span className="srw-tag srw-tag-unset">Avoiding</span>}
          </div>
          {isExcluded && (
            <button
              type="button"
              data-testid="savings-review-include-vendor"
              className="srw-vendor-toggle is-include"
              disabled={arrangementFrozen || hasPendingMoves}
              onClick={() => handleRemoveAvoidedVendor(vendorKey)}
              aria-label={`Include ${order.vendorName}`}
            >
              Include
            </button>
          )}
        </div>
        {entries.length > 0 ? (
          <button
            type="button"
            className="srw-available-toggle"
            data-testid="savings-review-available-toggle"
            aria-expanded={expanded}
            onClick={() => setOpenDockVendorIds((prev) => ({
              ...prev,
              [vendorKey]: !prev[vendorKey],
            }))}
          >
            {entries.length} compatible product{entries.length === 1 ? '' : 's'}
          </button>
        ) : (
          <span className="srw-available-none">No compatible products in this plan</span>
        )}
        {!expanded && namesShown.length > 0 && (
          <p className="srw-available-products" data-testid="savings-review-available-products">
            {namesShown.join(' · ')}
            {productNames.length > namesShown.length
              ? ` · +${productNames.length - namesShown.length} more`
              : ''}
          </p>
        )}
        {expanded && entries.length > 0 && (
          <ul className="srw-available-detail" data-testid="savings-review-available-detail">
            {entries.map((entry) => {
              // A line inside a finished vendor's order sits on a created PO
              // or shopping list — the lane is real, but moving it starts
              // with returning that draft, like every other gesture.
              const sourceFinished = entry.line.vendorId != null
                && completedVendorIds.includes(String(entry.line.vendorId));
              const currentPack = entry.line.selected?.option?.pack_description || null;
              const targetPack = entry.option?.pack_description || null;
              const currentVendorName = entry.line.vendorId != null
                ? (vendorsById[entry.line.vendorId]?.display_name
                  || entry.line.selected?.option?.vendor_name_text || 'Vendor')
                : (entry.line.selected?.option?.vendor_name_text || null);
              return (
                <li key={entry.line.lineId} data-testid="savings-review-available-item">
                  <strong className="srw-dock-name">{entry.line.description}</strong>
                  <span className="srw-available-offer">
                    {order.vendorName}: {entry.orderCost != null ? money(entry.orderCost) : 'price unavailable'}
                    {targetPack ? ` · ${targetPack}` : ''}
                  </span>
                  <span className="srw-available-current">
                    {currentVendorName
                      ? `Current: ${currentVendorName}${entry.line.orderCost != null ? ` · ${money(entry.line.orderCost)}` : ''}${currentPack ? ` · ${currentPack}` : ''}`
                      : 'Currently unassigned'}
                  </span>
                  <span className="srw-dock-note">
                    {dockNoteText(entry)}
                    {sourceFinished ? ' · in a finished order — return its draft to change it' : ''}
                  </span>
                  {/* Same confidence rule as every lane: only a confirmed
                      same-product, same-pack option moves in one click, and
                      through the same requestSwitch path as every other
                      gesture. Anything less goes to the row for review. An
                      excluded vendor's tile is exploration only — Include is
                      the path back in. */}
                  {!isExcluded && !sourceFinished && (entry.confirmed ? (
                    <button
                      type="button"
                      className="srw-link-btn"
                      data-testid="savings-review-dock-move"
                      disabled={arrangementFrozen}
                      onClick={() => requestSwitch(entry.line, entry.optionId, order.vendorId)}
                    >
                      Move to {order.vendorName}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="srw-link-btn"
                      data-testid="savings-review-dock-review"
                      onClick={() => handleChooseReplacement(entry.line.lineId)}
                    >
                      Review
                    </button>
                  ))}
                </li>
              );
            })}
          </ul>
        )}
        {expanded && !isExcluded && (
          <button
            type="button"
            className="srw-link-btn srw-available-avoid"
            data-testid="savings-review-avoid-vendor"
            disabled={arrangementFrozen || hasPendingMoves}
            onClick={() => handleAvoidVendor(vendorKey)}
            aria-label={`Remove ${order.vendorName}`}
          >
            Avoid in this scenario
          </button>
        )}
        {impactLines && impactLines.length > 0 && (
          <div className="srw-vendor-impact" data-testid="savings-review-vendor-impact">
            {impactLines.map((line) => (
              <span
                key={line.text}
                className={line.tone === 'savings'
                  ? 'srw-impact-savings'
                  : line.tone === 'warn'
                    ? 'srw-impact-warn'
                    : 'srw-impact-neutral'}
              >
                {line.text}
              </span>
            ))}
          </div>
        )}
      </section>
    );
  };

  const filterActive = filterPickup || showChangesOnly
    || searchTerm.trim().length > 0;
  const statusParts = changedLineIds.length > 0
    ? [
      `${changedLineIds.length} change${changedLineIds.length === 1 ? '' : 's'}`,
      scenarioComparison.unresolvedLineCount > 0
        ? `${scenarioComparison.unresolvedLineCount} needs resolution`
        : scenarioFinancialIncomplete
          ? 'cost comparison incomplete'
        : Math.abs(scenarioComparison.landedDelta) < 0.005
          ? 'landed total unchanged'
          : `landed total ${money(Math.abs(scenarioComparison.landedDelta))} ${scenarioComparison.landedDelta < 0 ? 'lower' : 'higher'}`,
      countDeltaText(scenarioComparison.pickupStopDelta, 'pickup stop'),
      countDeltaText(scenarioComparison.vendorOrderDelta, 'vendor'),
    ].filter(Boolean)
    : ['No changes from recommended'];
  const scenarioStatus = statusParts.join(' · ');
  const reviewVendorId = exclusionReview?.vendorId == null
    ? null : String(exclusionReview.vendorId);
  const reviewVendorName = reviewVendorId == null
    ? 'Vendor'
    : vendorsById[reviewVendorId]?.display_name
      || plan_.vendorOrders.find((order) => String(order.vendorId) === reviewVendorId)?.vendorName
      || 'Vendor';
  const replacementReviewCount = exclusionDraft?.model?.items.filter(
    (item) => item.status !== 'no_alternative' && item.selectedOptionId,
  ).length || 0;
  const approvedReplacementCount = exclusionDraft?.model?.items.filter(
    (item) => item.status !== 'no_alternative'
      && item.selectedOptionId
      && exclusionApprovedLineIdSet.has(String(item.lineId)),
  ).length || 0;

  const overageImpactText = (value, unit) => {
    if (value === null || value === undefined) return '—';
    if (Math.abs(Number(value)) < 1e-6) return 'Exactly reaches PAR';
    return `${impactQuantityText(value, unit)} ${Number(value) > 0 ? 'above' : 'below'} PAR`;
  };

  const renderExclusionReviewItem = (item) => {
    const kept = item.proposedVendorId === reviewVendorId;
    const unresolved = !item.selectedOptionId;
    const explicitlyUnresolved = (exclusionReview?.unresolvedLineIds || [])
      .map(String).includes(String(item.lineId));
    const approved = exclusionApprovedLineIdSet.has(String(item.lineId));
    const needsDecision = item.status === 'no_alternative' && !approved;
    return (
      <article
        key={item.lineId}
        className={[
          'srw-exclusion-item',
          unresolved ? 'is-unresolved' : '',
          approved ? 'is-approved' : 'is-pending',
        ].filter(Boolean).join(' ')}
        data-testid="savings-review-exclusion-item"
        data-line-id={item.lineId}
      >
        <header>
          <div>
            <h4>{item.description}</h4>
            <p>
              {item.currentVendorName} → {needsDecision
                ? 'Decision required'
                : unresolved ? 'Needs attention' : item.proposedVendorName}
            </p>
          </div>
          {needsDecision && <span className="srw-tag srw-tag-unset">Decision required</span>}
          {explicitlyUnresolved && <span className="srw-tag srw-tag-unset">Unpurchased</span>}
          {kept && <span className="srw-tag">Vendor remains</span>}
          {!needsDecision && !unresolved && (
            <span className={approved ? 'srw-tag srw-tag-approved' : 'srw-tag'}>
              {approved ? 'Approved' : 'Pending review'}
            </span>
          )}
        </header>

        {item.status === 'no_alternative' ? (
          <div className="srw-exclusion-no-alt">
            <p>{item.explanation}</p>
            <div className="srw-exclusion-choice-row" role="group" aria-label={`Outcome for ${item.description}`}>
              <button
                type="button"
                className={kept ? 'srw-chip is-on' : 'srw-chip'}
                aria-pressed={kept}
                data-testid="savings-review-exclusion-keep"
                onClick={() => handleDraftKeepVendor(item.lineId)}
              >
                Accept — keep with {reviewVendorName}
              </button>
              <button
                type="button"
                className={explicitlyUnresolved ? 'srw-chip is-on' : 'srw-chip'}
                aria-pressed={explicitlyUnresolved}
                data-testid="savings-review-exclusion-unresolved"
                onClick={() => handleDraftLeaveUnresolved(item.lineId)}
              >
                Leave unresolved
              </button>
              {nextOrderItemsEnabled && (
                <span className="srw-next-order-disabled">
                  <button type="button" className="srw-chip" disabled>Move to Next Order</button>
                  <small>Save this item to reconsider during your next ordering session.</small>
                </span>
              )}
            </div>
          </div>
        ) : (
          <>
            <dl className="srw-exclusion-facts">
              <div><dt>Package</dt><dd>{item.currentPackage} → {item.proposedPackage || '—'}</dd></div>
              <div>
                <dt>Quantity delivered</dt>
                <dd>
                  {impactQuantityText(item.currentQuantityReceived, item.countUnit)} →{' '}
                  {impactQuantityText(item.proposedQuantityReceived, item.countUnit)}
                </dd>
              </div>
              <div><dt>Coverage</dt><dd>{item.coverage ? 'PAR remains covered' : 'PAR is no longer covered'}</dd></div>
              <div>
                <dt>Above or below PAR</dt>
                <dd>
                  {overageImpactText(item.currentOverage, item.countUnit)} →{' '}
                  {overageImpactText(item.proposedOverage, item.countUnit)}
                </dd>
              </div>
              <div><dt>Purchase total</dt><dd>{directionalMoneyText(item.purchaseSpendDelta)}</dd></div>
              <div>
                <dt>Quantity difference</dt>
                <dd>{directionalQuantityText(item.quantityDifference.value, item.quantityDifference.unit)}</dd>
              </div>
              <div>
                <dt>Comparable unit price</dt>
                <dd>
                  {item.currentComparableUnitCost === null || !item.comparisonUnit
                    ? '—'
                    : `${money(item.currentComparableUnitCost)} / ${item.comparisonUnit}`}
                  {' → '}
                  {item.proposedComparableUnitCost === null || !item.comparisonUnit
                    ? '—'
                    : `${money(item.proposedComparableUnitCost)} / ${item.comparisonUnit}`}
                </dd>
              </div>
              <div>
                <dt>Comparable price impact</dt>
                <dd>{directionalComparableText(item.comparablePriceDelta, item.comparisonUnit)}</dd>
              </div>
            </dl>
            {item.operationalConsequences.length > 0 && (
              <ul className="srw-exclusion-consequences">
                {item.operationalConsequences.map((consequence) => <li key={consequence}>{consequence}</li>)}
              </ul>
            )}
            <p className="srw-exclusion-explanation">{item.explanation}</p>
            {item.alternatives.length > 1 && (
              <details className="srw-exclusion-change" data-testid="savings-review-exclusion-change">
                <summary>Change</summary>
                <div role="group" aria-label={`Choose a replacement vendor for ${item.description}`}>
                  {item.alternatives.map((alternative) => (
                    <button
                      type="button"
                      key={alternative.optionId}
                      className={alternative.optionId === item.selectedOptionId ? 'srw-chip is-on' : 'srw-chip'}
                      aria-pressed={alternative.optionId === item.selectedOptionId}
                      onClick={() => handleDraftReplacementChange(item.lineId, alternative.optionId)}
                    >
                      {alternative.vendorName} · {alternative.packageDescription} · {money(alternative.orderCost)}
                    </button>
                  ))}
                </div>
              </details>
            )}
            <div className="srw-exclusion-item-approval">
              <button
                type="button"
                className={approved ? 'srw-chip is-on' : 'srw-chip'}
                aria-pressed={approved}
                data-testid="savings-review-exclusion-approve-item"
                onClick={() => handleApproveDraftLine(item.lineId)}
              >
                {approved ? 'Replacement approved' : 'Approve replacement'}
              </button>
            </div>
          </>
        )}
      </article>
    );
  };

  return (
    <div className="srw">
      <div className="srw-head">
        <div>
          <h1>Purchase Planner</h1>
          <p>
            This is your purchase plan. Counter Intelligence built the best current
            shopping path for it — switch any item&apos;s vendor and the totals, fees,
            minimums and pickup stops update immediately.
          </p>
        </div>
      </div>

      {exclusionReview && (
        <div
          className="srw-done-overlay srw-exclusion-overlay"
          role="presentation"
          onClick={() => setExclusionReview(null)}
        >
          <section
            className="srw-done-dialog srw-exclusion-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="srw-exclusion-title"
            data-testid="savings-review-exclusion-dialog"
            onClick={(event) => event.stopPropagation()}
          >
            {exclusionDraft?.model ? (
              <>
                <header className="srw-exclusion-dialog-head">
                  <div>
                    <span className="srw-eyebrow">Exclude from this order</span>
                    <h2 id="srw-exclusion-title">Review replacements for {reviewVendorName}</h2>
                    <p>Review items one at a time or approve every proposed replacement together.</p>
                    <p className="srw-exclusion-scope">
                      No changes are applied until you select Apply changes. This affects only the
                      current purchase plan; {reviewVendorName} stays in your vendor list.
                    </p>
                  </div>
                  <button
                    type="button"
                    className="srw-link-btn"
                    aria-label="Cancel vendor exclusion review"
                    onClick={() => setExclusionReview(null)}
                  >
                    Cancel
                  </button>
                </header>

                <div className="srw-exclusion-review-toolbar">
                  <span data-testid="savings-review-exclusion-progress">
                    {exclusionDraft.model.items.length - exclusionPendingReviewCount} of{' '}
                    {exclusionDraft.model.items.length} item{exclusionDraft.model.items.length === 1 ? '' : 's'} reviewed
                  </span>
                  {replacementReviewCount > 0 && (
                    <button
                      type="button"
                      className="srw-chip"
                      data-testid="savings-review-exclusion-approve-all"
                      disabled={approvedReplacementCount === replacementReviewCount}
                      autoFocus
                      onClick={handleApproveAllDraftReplacements}
                    >
                      {approvedReplacementCount === replacementReviewCount
                        ? 'All replacements approved'
                        : 'Approve all replacements'}
                    </button>
                  )}
                </div>

                <div className="srw-exclusion-items">
                  {exclusionDraft.model.groups.oneAlternative.length > 0 && (
                    <section className="srw-exclusion-group">
                      <h3>One valid alternative</h3>
                      <div className="srw-exclusion-grid">
                        {exclusionDraft.model.groups.oneAlternative.map(renderExclusionReviewItem)}
                      </div>
                    </section>
                  )}
                  {exclusionDraft.model.groups.severalAlternatives.length > 0 && (
                    <section className="srw-exclusion-group">
                      <h3>Several valid alternatives</h3>
                      <div className="srw-exclusion-grid">
                        {exclusionDraft.model.groups.severalAlternatives.map(renderExclusionReviewItem)}
                      </div>
                    </section>
                  )}
                  {exclusionDraft.model.groups.noAlternative.length > 0 && (
                    <section
                      className="srw-exclusion-group"
                      data-testid="savings-review-exclusion-needs-attention"
                    >
                      <h3>Needs attention · No alternative vendor</h3>
                      <div className="srw-exclusion-grid">
                        {exclusionDraft.model.groups.noAlternative.map(renderExclusionReviewItem)}
                      </div>
                    </section>
                  )}
                </div>

                <section className="srw-exclusion-summary" data-testid="savings-review-exclusion-summary">
                  <h3>
                    {exclusionDraft.model.summary.fullyExcluded
                      ? `Excluding ${reviewVendorName} will:`
                      : `Applying these changes will keep ${reviewVendorName} in this order and:`}
                  </h3>
                  <ul>
                    {exclusionDraft.model.summary.moves.map((move) => (
                      <li key={move.vendorName}>Move {move.count} item{move.count === 1 ? '' : 's'} to {move.vendorName}</li>
                    ))}
                    {exclusionDraft.model.summary.keptCount > 0 && (
                      <li>Keep {exclusionDraft.model.summary.keptCount} item{exclusionDraft.model.summary.keptCount === 1 ? '' : 's'} with {reviewVendorName}</li>
                    )}
                    {exclusionDraft.model.summary.unresolvedCount > 0 && (
                      <li>Leave {exclusionDraft.model.summary.unresolvedCount} item{exclusionDraft.model.summary.unresolvedCount === 1 ? '' : 's'} needing attention</li>
                    )}
                    <li>
                      {Math.abs(Number(exclusionDraft.model.summary.purchaseTotalDelta) || 0) < 0.005
                        ? exclusionDraft.model.summary.economicImpactComplete
                          ? 'Leave the purchase total effectively unchanged'
                          : 'Leave the covered-item purchase total effectively unchanged'
                        : `${exclusionDraft.model.summary.purchaseTotalDelta < 0 ? 'Lower' : 'Raise'} the purchase total${exclusionDraft.model.summary.economicImpactComplete ? '' : ' for covered items'} by ${money(Math.abs(exclusionDraft.model.summary.purchaseTotalDelta))}`}
                    </li>
                    {Math.abs(Number(exclusionDraft.model.summary.feeDelta) || 0) < 0.005 ? (
                      <li>Add no known fees</li>
                    ) : (
                      <li>{exclusionDraft.model.summary.feeDelta < 0 ? 'Remove' : 'Add'} {money(Math.abs(exclusionDraft.model.summary.feeDelta))} in known fees</li>
                    )}
                    <li>
                      {exclusionDraft.model.summary.pickupStopDelta === 0
                        ? 'Add no pickup stops'
                        : `${exclusionDraft.model.summary.pickupStopDelta > 0 ? 'Add' : 'Remove'} ${Math.abs(exclusionDraft.model.summary.pickupStopDelta)} pickup stop${Math.abs(exclusionDraft.model.summary.pickupStopDelta) === 1 ? '' : 's'}`}
                    </li>
                    <li>
                      Landed total: {exclusionDraft.model.summary.landedDelta == null
                        ? 'Incomplete'
                        : directionalMoneyText(exclusionDraft.model.summary.landedDelta)}
                    </li>
                  </ul>
                </section>

                <div className="srw-done-actions srw-exclusion-apply-actions">
                  {exclusionPendingReviewCount > 0 && (
                    <span
                      className="srw-exclusion-pending-note"
                      data-testid="savings-review-exclusion-pending"
                    >
                      Review {exclusionPendingReviewCount} more item{exclusionPendingReviewCount === 1 ? '' : 's'} before applying.
                    </span>
                  )}
                  <button
                    type="button"
                    className="srw-btn srw-btn-primary"
                    data-testid="savings-review-exclusion-apply"
                    disabled={arrangementFrozen || hasPendingMoves || exclusionPendingReviewCount > 0}
                    onClick={handleApplyVendorExclusion}
                  >
                    {exclusionDraft.model.summary.fullyExcluded ? 'Apply changes' : 'Apply partial changes'}
                  </button>
                  <button
                    type="button"
                    className="srw-btn srw-btn-ghost"
                    data-testid="savings-review-exclusion-cancel"
                    onClick={() => setExclusionReview(null)}
                  >
                    Cancel
                  </button>
                </div>
              </>
            ) : null}
          </section>
        </div>
      )}

      {reopenPrompt && (
        <div className="srw-done-overlay" role="presentation" onClick={() => !reopenBusy && setReopenPrompt(null)}>
          <section
            className="srw-done-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="srw-reopen-title"
            data-testid="savings-review-reopen-prompt"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="srw-reopen-title">{reopenPrompt.vendorName} already has a draft PO</h2>
            <p>
              Reopen the draft{reopenPrompt.draft?.po_number ? ` (${reopenPrompt.draft.po_number})` : ''} and
              move {reopenPrompt.description} to {reopenPrompt.vendorName}?
            </p>
            <p className="srw-done-note">
              The draft has not been sent. Reopening puts its items back in this
              review so you can keep arranging, then finish it again.
            </p>
            <div className="srw-done-actions">
              <button
                type="button"
                className="srw-btn srw-btn-primary"
                data-testid="savings-review-reopen-confirm"
                disabled={reopenBusy}
                onClick={confirmReopenAndMove}
              >
                {reopenBusy ? 'Working…' : 'Reopen & move'}
              </button>
              <button
                type="button"
                className="srw-btn srw-btn-undo"
                data-testid="savings-review-reopen-cancel"
                disabled={reopenBusy}
                onClick={() => setReopenPrompt(null)}
              >
                Cancel
              </button>
            </div>
          </section>
        </div>
      )}

      {donePromptOpen && (
        <div className="srw-done-overlay" role="presentation" onClick={() => !doneBusy && setDonePromptOpen(false)}>
          <section
            className="srw-done-dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="srw-done-title"
            data-testid="savings-review-done-prompt"
            onClick={(event) => event.stopPropagation()}
          >
            <h2 id="srw-done-title">
              {remainingOrders.length} vendor order{remainingOrders.length === 1 ? '' : 's'} remaining
            </h2>
            <p>
              {finishableOrders.length > 0
                ? 'Create the orders for the remaining vendors now, or keep working on this purchase plan?'
                : 'These vendors need to be linked before their orders can be created.'}
            </p>
            {finishableOrders.length > 0 && finishableOrders.length < remainingOrders.length && (
              <p className="srw-done-note">
                {remainingOrders.length - finishableOrders.length} vendor{remainingOrders.length - finishableOrders.length === 1 ? '' : 's'} must
                be linked first — link them from their vendor card, then finish.
              </p>
            )}
            <div className="srw-done-actions">
              {finishableOrders.length > 0 && (
                <button
                  type="button"
                  className="srw-btn srw-btn-primary"
                  data-testid="savings-review-done-create"
                  disabled={doneBusy}
                  onClick={handleCreateRemainingOrders}
                >
                  {doneBusy ? 'Creating orders…' : 'Create orders'}
                </button>
              )}
              <button
                type="button"
                className="srw-btn srw-btn-ghost"
                data-testid="savings-review-done-continue"
                disabled={doneBusy}
                onClick={() => setDonePromptOpen(false)}
              >
                Continue reviewing
              </button>
            </div>
          </section>
        </div>
      )}

      {/* Toolbar: find items, narrow to what needs attention, and choose the
          item or vendor view. Scenario actions live in the bar below. */}
      <div className="srw-toolbar" data-testid="savings-review-toolbar">
        <input
          type="search"
          className="srw-search"
          placeholder="Search items…"
          aria-label="Search items"
          data-testid="savings-review-search"
          value={searchTerm}
          onChange={(event) => setSearchTerm(event.target.value)}
        />
        <span className="srw-toolbar-label">Show</span>
        <button
          type="button"
          className={filterPickup ? 'srw-chip is-on' : 'srw-chip'}
          data-testid="savings-review-filter-pickup"
          aria-pressed={filterPickup}
          onClick={() => setFilterPickup((v) => !v)}
        >
          Pickup stops
        </button>
        <span className="srw-toolbar-gap" />
        <span className="srw-toolbar-label">Organize</span>
        <div className="srw-group-toggle" role="group" aria-label="Group items">
          <button
            type="button"
            className={groupMode === 'vendor' ? 'srw-chip is-on' : 'srw-chip'}
            data-testid="savings-review-group-vendor"
            aria-pressed={groupMode === 'vendor'}
            onClick={() => setGroupMode('vendor')}
          >
            Group by vendor
          </button>
          <button
            type="button"
            className={groupMode === 'item' ? 'srw-chip is-on' : 'srw-chip'}
            data-testid="savings-review-group-item"
            aria-pressed={groupMode === 'item'}
            onClick={() => setGroupMode('item')}
          >
            Group by item
          </button>
        </div>
      </div>

      <section className="srw-vendor-planner" data-testid="savings-review-vendor-planner">
        <header className="srw-vendor-planner-head" data-testid="savings-review-scenario-bar">
          <div>
            <span className="srw-eyebrow">Vendor scenario</span>
            <h2>Active vendors in this order</h2>
            <p>
              {vendorExclusionReviewEnabled
                ? 'Exclude a vendor from this order here. You’ll review replacements before item allocations, totals, fees, minimums or pickup stops change.'
                : 'A vendor is active while at least one product is assigned to it. Move products below to change who supplies them — subtotals, known fees, minimums and pickup stops recalculate immediately.'}
            </p>
          </div>
          <div className="srw-vendor-planner-actions">
            <button
              type="button"
              className={showChangesOnly ? 'srw-chip is-on' : 'srw-chip'}
              data-testid="savings-review-show-changes"
              aria-pressed={showChangesOnly}
              disabled={changedLineIds.length === 0}
              onClick={() => setShowChangesOnly((v) => !v)}
            >
              {scenarioStatus}
            </button>
            <button
              type="button"
              className="srw-chip"
              data-testid="savings-review-undo"
              disabled={undoStack.length === 0 || hasPendingMoves}
              onClick={handleUndoLastChange}
            >
              Undo last change
            </button>
          </div>
        </header>

        {/* The narrator of the purchasing experiment, above the vendor cards:
            one plain-language reading of the current scenario, updated with
            every move, vendor change, resolution, or total change. With no
            changes yet it narrates the plan as it stands. */}
        {plan_.lines.length > 0 && (
          <div className="srw-scenario-explanation" data-testid="savings-review-scenario-explanation">
            <span className="srw-eyebrow">Counter Intelligence summary</span>
            <p>
              {changedLineIds.length > 0
                ? (scenarioNarrative.explanation || deterministicScenarioExplanation)
                : baselinePlanNarration}
            </p>
            <div className="srw-scenario-explanation-meta">
              {scenarioNarrative.status === 'loading' ? (
                <span data-testid="savings-review-explanation-loading" role="status">
                  <i aria-hidden="true" /> Refining this explanation from the calculated order facts…
                </span>
              ) : (
                <span>
                  {changedLineIds.length > 0 && scenarioNarrative.source === 'ai'
                    ? 'AI wording · amounts and coverage calculated by the purchase planner'
                    : 'Calculated purchase-planner summary'}
                </span>
              )}
              <button
                type="button"
                className="srw-link-btn"
                data-testid="savings-review-discuss-scenario"
                onClick={handleDiscussScenario}
              >
                Discuss this arrangement
              </button>
            </div>
          </div>
        )}

        {avoidedVendorIds.length > 0 && (
          <div className="srw-constraints" data-testid="savings-review-constraints">
            <strong>{vendorExclusionReviewEnabled ? 'Excluded from this order' : 'Scenario · Avoiding'}</strong>
            {avoidedVendorNames.map((name) => (
              <span key={name} className="srw-constraint">{name}</span>
            ))}
          </div>
        )}

        <div className="srw-vendor-tabs" role="list" aria-label="Vendor scenario controls">
          {activeOrders.length === 0 && (
            <p className="srw-side-empty">No vendors assigned yet.</p>
          )}
          {activeOrders.map((order) => renderVendorCard(order))}
        </div>

        {blockedAvoidanceLines.length > 0 && (
          <section className="srw-vendor-resolution" data-testid="savings-review-vendor-resolution">
            <h3>
              {blockedAvoidanceLines.length} item{blockedAvoidanceLines.length === 1 ? '' : 's'} cannot be safely reassigned
            </h3>
            {blockedAvoidanceLines.map(({ line, vendorId, vendorName, packageUnsafe }) => (
              <div key={`${vendorId}:${line.lineId}`}>
                <span>
                  <strong>{line.description}</strong>
                  {' — '}{packageUnsafe
                    ? 'the remaining package option needs confirmation.'
                    : 'no other approved purchasing option is available.'}
                </span>
                <button
                  type="button"
                  className="srw-vendor-toggle is-include"
                  disabled={arrangementFrozen || hasPendingMoves}
                  onClick={() => handleKeepAvoidedVendorForLine(line.lineId, vendorId)}
                >
                  Keep {vendorName} for this item
                </button>
              </div>
            ))}
          </section>
        )}

        {changedLineIds.length > 0 && (
          <section className="srw-scenario-breakdown" data-testid="savings-review-scenario-breakdown">
            <h3>Item allocation changes</h3>
            {avoidedVendorIds.length > 0 && (
              <p>Without {avoidedVendorNames.join(' and ')}</p>
            )}
            {scenarioComparison.changedLines.map((change) => {
              const unresolvedItem = scenarioComparison.unresolvedItems.find(
                (item) => String(item.lineId) === String(change.lineId),
              );
              const knownFromVendorName = change.fromVendorName
                || vendorsById[change.fromVendorId]?.display_name
                || null;
              const fromVendorName = knownFromVendorName || 'Unassigned';
              const toVendorName = change.toVendorName
                || vendorsById[change.toVendorId]?.display_name
                || null;
              const noReplacement = change.reason === 'no_approved_replacement' || change.unassigned;
              return (
                <div key={change.lineId} data-testid="savings-review-scenario-change">
                  <span className="srw-scenario-change-name">{change.description || 'Item'}</span>
                  <span className="srw-scenario-change-route">
                    {fromVendorName} →{' '}
                    {noReplacement ? 'No replacement found' : toVendorName || 'Unresolved'}
                  </span>
                  <strong>
                    {scenarioChangeHeadline(change, {
                      fromVendorName: knownFromVendorName,
                      toVendorName: noReplacement ? null : toVendorName,
                    })}
                  </strong>
                  <ul className="srw-scenario-change-facts">
                    {scenarioChangeFacts(
                      change,
                      parContextByItemId[String(change.inventoryItemId)] || null,
                    ).map((fact) => <li key={fact}>{fact}</li>)}
                  </ul>
                  {unresolvedItem && (
                    <div className="srw-scenario-unresolved" data-testid="savings-review-unresolved-change">
                      <p>
                        {unresolvedItem.product} was {money(unresolvedItem.previousOrderCost)} in the
                        recommended plan. That amount is missing from this scenario because the item
                        is not being purchased. <strong>It is not savings.</strong>
                      </p>
                      <span>Not covered · Action required</span>
                      <div>
                        <button
                          type="button"
                          className="srw-btn srw-btn-undo"
                          data-testid="savings-review-choose-replacement"
                          onClick={() => handleChooseReplacement(change.lineId)}
                        >
                          Choose a replacement
                        </button>
                        {change.fromVendorId != null
                          && avoidedVendorIdSet.has(String(change.fromVendorId)) && (
                          <button
                            type="button"
                            className="srw-btn srw-btn-undo"
                            data-testid="savings-review-restore-vendor-item"
                            onClick={() => handleKeepAvoidedVendorForLine(
                              change.lineId,
                              change.fromVendorId,
                            )}
                          >
                            Restore {fromVendorName} for this item
                          </button>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </section>
        )}

        {/* The scenario ledger. Its metrics are DELTAS against the recommended
            plan, so with no changes there is nothing to tabulate — a single
            anchored sentence says you are on the recommended plan and what it
            lands at. The one absolute fact that must survive both states is
            the "Missing from order" product list: an UNTOUCHED plan can hold
            unresolved lines, and their names never hide behind a change. The
            metric grid appears only once the scenario has actually diverged,
            under a header that names the anchor. */}
        <footer className="srw-vendor-planner-impact" data-testid="savings-review-scenario-impact">
          {changedLineIds.length === 0 ? (
            <div className="srw-impact-baseline" data-testid="savings-review-impact-baseline">
              <span className="srw-eyebrow">Scenario impact</span>
              <p>
                No changes from recommended — this is the plan as recommended.
                {scenarioComparison.coveredProductCount < scenarioComparison.requiredProductCount && (
                  <span className="srw-impact-warn">
                    {' '}{scenarioComparison.coveredProductCount} of {scenarioComparison.requiredProductCount} products covered.
                  </span>
                )}
                {scenarioComparison.landedTotalStatus === 'incomplete'
                  ? ' The landed total is incomplete until every item is priced and covered.'
                  : <> Estimated landed total <strong>{money(scenarioComparison.comparableScenarioTotal)}</strong>.</>}
              </p>
              {scenarioComparison.unresolvedItems.length > 0 && (
                <dl>
                  <div className="srw-impact-missing">
                    <dt>Missing from order</dt>
                    <dd>
                      <ul className="srw-impact-missing-list" data-testid="savings-review-missing-products">
                        {scenarioComparison.unresolvedItems.map((item) => (
                          <li key={item.lineId}>
                            <span className="srw-impact-missing-name" title={item.product}>{item.product}</span>
                            <span className="srw-impact-missing-cost"> · {money(item.previousOrderCost)} in recommended plan</span>
                          </li>
                        ))}
                      </ul>
                    </dd>
                  </div>
                </dl>
              )}
            </div>
          ) : (
            <>
              <div className="srw-impact-head">
                <div>
                  <span className="srw-eyebrow">Scenario impact</span>
                  <h3>Impact of your changes</h3>
                </div>
                <span className="srw-impact-status">
                  Compared with the recommended plan · {scenarioStatus}
                </span>
              </div>
              <dl>
            <div>
              <dt>Coverage</dt>
              <dd>
                {scenarioComparison.coveredProductCount} of {scenarioComparison.requiredProductCount} products covered
              </dd>
            </div>
            <div>
              <dt>Purchase total</dt>
              <dd>
                {spendChangeText(scenarioComparison.coveredProductSpendDelta)}
                {scenarioFinancialIncomplete ? ' for covered items' : ''}
              </dd>
            </div>
            {scenarioComparison.unresolvedItems.length > 0 && (
              <div className="srw-impact-missing">
                <dt>Missing from order</dt>
                <dd>
                  <ul className="srw-impact-missing-list" data-testid="savings-review-missing-products">
                    {scenarioComparison.unresolvedItems.map((item) => (
                      <li key={item.lineId}>
                        <span className="srw-impact-missing-name" title={item.product}>{item.product}</span>
                        <span className="srw-impact-missing-cost"> · {money(item.previousOrderCost)} in recommended plan</span>
                      </li>
                    ))}
                  </ul>
                </dd>
              </div>
            )}
            <div>
              <dt>Quantity difference</dt>
              <dd>{quantityDifferenceText(scenarioComparison)}</dd>
            </div>
            <div
              title="Compares both choices using the same quantity, before fees and tax."
            >
              <dt>Comparable price impact</dt>
              <dd className={scenarioComparison.grossPriceImpactTotal > 0.004
                ? 'srw-impact-savings'
                : scenarioComparison.grossPriceImpactTotal < -0.004
                  ? 'srw-impact-warn'
                  : undefined}
              >
                {grossPriceImpactText(
                  scenarioComparison.grossPriceImpactTotal,
                  scenarioComparison.grossPriceImpactComplete,
                )}
              </dd>
            </div>
            <div>
              <dt>Landed total</dt>
              <dd>{scenarioComparison.landedTotalStatus === 'incomplete'
                ? 'Incomplete'
                : purchaseTotalChangeText(scenarioComparison.landedDelta)}</dd>
            </div>
              </dl>
            </>
          )}
        </footer>
      </section>

      {charges.infeasible.length > 0 && (
        <div className="srw-note" data-testid="savings-review-infeasible" style={{ borderColor: '#e8b9ae', background: '#fdecea', color: '#a33b2e' }}>
          {charges.infeasible.join(', ')} {charges.infeasible.length === 1 ? 'is' : 'are'} below a
          hard delivery minimum — this plan cannot be ordered as-is. Move items in or accept the
          shortfall explicitly.
        </div>
      )}

      {plan_.summary.unresolvedCount > 0 && (
        <div className="srw-note">
          {plan_.summary.unresolvedCount} item{plan_.summary.unresolvedCount === 1 ? '' : 's'} do not
          have a shared comparison unit yet — their vendors are all shown, with no cheapest called.
          Open the item and use Show full comparison to see and correct the package numbers.
        </div>
      )}

      {plan_.summary.packageRepairCount > 0 && (
        <div className="srw-note" data-testid="savings-review-package-repair">
          {plan_.summary.packageRepairCount} item{plan_.summary.packageRepairCount === 1 ? '' : 's'} need
          {plan_.summary.packageRepairCount === 1 ? 's' : ''} a package check before ordering — they are
          open below and stay out of vendor totals until the package is safe to order.
          <button
            type="button"
            className="srw-link-btn"
            onClick={() => {
              const repair = plan_.lines.find((line) => line.needsPackageRepair);
              if (repair) handleChooseReplacement(repair.lineId);
            }}
          >
            Go to the first item
          </button>
        </div>
      )}

      {dropPreview && (
        <div className="srw-preview" data-testid="savings-review-drop-preview">
          <strong>Move to {dropPreview.vendorName}?</strong>
          <span>Line cost {dropPreview.lineCostDelta == null
            ? 'Unavailable'
            : `${dropPreview.lineCostDelta >= 0 ? '+' : '−'}${money(Math.abs(dropPreview.lineCostDelta))}`}</span>
          {dropPreview.extraQuantityDelta != null && dropPreview.extraQuantityDelta !== 0 && (
            <span>{dropPreview.extraQuantityDelta > 0 ? '+' : '−'}{Math.abs(dropPreview.extraQuantityDelta)} {dropPreview.countUnit} above PAR</span>
          )}
          <span>Vendor orders {dropPreview.vendorOrdersBefore} → {dropPreview.vendorOrdersAfter}</span>
          {dropPreview.pickupStopDelta !== 0 && (
            <span>Pickup stops {dropPreview.pickupStopsBefore} → {dropPreview.pickupStopsAfter}</span>
          )}
          {dropPreview.feeDelta != null && dropPreview.feeDelta !== 0 && (
            <span>Fees {dropPreview.feeDelta > 0 ? '+' : '−'}{money(Math.abs(dropPreview.feeDelta))}</span>
          )}
          <span className="srw-preview-total">
            {dropPreview.economicImpactComplete && dropPreview.landedDelta != null
              ? `Estimated landed total ${dropPreview.landedDelta >= 0 ? '+' : '−'}${money(Math.abs(dropPreview.landedDelta))}`
              : 'Estimated landed total unavailable until coverage and pricing are complete'}
          </span>
        </div>
      )}

      <div className="srw-layout">
        <div className="srw-main">
          {visibleLines.length === 0 && (
            <div className="srw-empty-list" data-testid="savings-review-empty-list">
              {filterActive
                ? 'No items match these filters.'
                : 'Nothing to order from this count.'}
            </div>
          )}

          {groupMode === 'item' ? (
            <ul className="srw-list" data-testid="savings-review-list">
              {visibleLines.map((line) => renderLineRow(line))}
            </ul>
          ) : (
            <div className="srw-vendor-groups" data-testid="savings-review-vendor-groups">
              {/* Lines no vendor order holds — package checks, unbuyable
                  items, unassigned products. Vendor view is the default, so
                  these must not become invisible for lack of a vendor. */}
              {(() => {
                const ghostedLineIds = new Set(
                  blockedAvoidanceLines.map(({ line }) => String(line.lineId)),
                );
                const unassignedLines = visibleLines.filter((line) => (
                  !orderByLineId.has(String(line.lineId))
                  && !ghostedLineIds.has(String(line.lineId))
                ));
                if (unassignedLines.length === 0) return null;
                return (
                  <section
                    className="srw-vendor-section is-unassigned"
                    data-testid="savings-review-unassigned-section"
                  >
                    <header className="srw-vendor-section-head">
                      <strong className="srw-vendor-section-name">Needs resolution</strong>
                      <span className="srw-vendor-count-pill">
                        {unassignedLines.length} item{unassignedLines.length === 1 ? '' : 's'}
                      </span>
                      <span className="srw-vendor-section-note">without an orderable option</span>
                    </header>
                    <ul className="srw-list">
                      {unassignedLines.map((line) => renderLineRow(line))}
                    </ul>
                  </section>
                );
              })()}
              {columns.filter((order) => {
                // A vendor with nothing committed renders no workspace
                // section — the Available vendors strip below is its surface
                // and its drop target. An excluded vendor keeps its section
                // while a stranded item still needs the "keep this vendor"
                // resolution shown beside where it used to live.
                if ((order.itemCount || 0) > 0) return true;
                const vendorKey = order.vendorId == null ? null : String(order.vendorId);
                return blockedAvoidanceLines.some(({ vendorId }) => String(vendorId) === vendorKey);
              }).map((order) => {
                const vendorKey = order.vendorId == null ? null : String(order.vendorId);
                const sectionKey = order.groupKey || (vendorKey ? `vendor:${vendorKey}` : '__unassigned__');
                const isComplete = vendorKey != null && completedVendorIds.includes(vendorKey);
                const isExcluded = vendorKey != null && avoidedVendorIdSet.has(vendorKey);
                const isHover = hoverVendor === vendorKey && canDropOn(order.vendorId);
                const isBlocked = !!dragging && !canDropOn(order.vendorId)
                  && dragging.fromVendorId !== vendorKey;
                const sectionLines = order.lines
                  .filter((line) => visibleLines.some((v) => v.lineId === line.lineId));
                const ghostLines = blockedAvoidanceLines.filter(({ line, vendorId }) => (
                  String(vendorId) === vendorKey
                  && visibleLines.some((visible) => String(visible.lineId) === String(line.lineId))
                ));
                return (
                  <section
                    key={sectionKey}
                    data-testid="savings-review-vendor-section"
                    data-vendor-id={vendorKey || ''}
                    className={[
                      'srw-vendor-section',
                      isHover ? 'is-target' : '',
                      isBlocked ? 'is-blocked' : '',
                      isComplete ? 'is-complete' : '',
                      isExcluded ? 'is-excluded' : '',
                    ].filter(Boolean).join(' ')}
                    onDragOver={(event) => {
                      if (!canDropOn(order.vendorId)) return;
                      event.preventDefault();
                      setHoverVendor(vendorKey);
                    }}
                    onDragLeave={() => setHoverVendor((cur) => (cur === vendorKey ? null : cur))}
                    onDrop={handleDrop(order.vendorId)}
                  >
                    {/* A banded header, not a floating caption: vendor name
                        leads, the item count wears a pill, and the subtotal
                        anchors the right edge over the items it totals. */}
                    <header className="srw-vendor-section-head">
                      <strong className="srw-vendor-section-name">{order.vendorName}</strong>
                      <FulfillmentBadge vendor={order.vendor || vendorsById[order.vendorId]} />
                      <span className="srw-vendor-count-pill">
                        {order.itemCount} item{order.itemCount === 1 ? '' : 's'}
                      </span>
                      {isComplete && <span className="srw-tag srw-tag-done">Finished</span>}
                      {isExcluded && (
                        <span
                          className="srw-tag srw-tag-unset"
                          data-testid="savings-review-excluded-vendor-tag"
                        >
                          {order.itemCount > 0
                            ? 'Line exception'
                            : ghostLines.length > 0
                              ? `Excluded · ${ghostLines.length} needs resolution`
                              : 'Excluded'}
                        </span>
                      )}
                      <span className="srw-vendor-section-total">{money(order.subtotal)}</span>
                    </header>
                    {sectionLines.length === 0 && ghostLines.length === 0 && (
                      <div className="srw-empty">
                        {isExcluded
                          ? `${order.vendorName} is excluded from this scenario.`
                          : order.itemCount === 0
                          ? `Drag a product here to buy it from ${order.vendorName}.`
                          : 'No items match the current filters.'}
                      </div>
                    )}
                    <ul className="srw-list">
                      {sectionLines.map((line) => renderLineRow(line, { draggable: true }))}
                      {ghostLines.map(({ line, vendorId, vendorName, packageUnsafe }) => (
                        <li
                          key={`excluded:${vendorId}:${line.lineId}`}
                          className="srw-excluded-ghost"
                          data-testid="savings-review-excluded-vendor-ghost"
                          data-line-id={line.lineId}
                        >
                          <span
                            className="srw-excluded-ghost-caution"
                            role="img"
                            aria-label="Needs resolution"
                            data-testid="savings-review-excluded-vendor-caution"
                          >
                            ⚠
                          </span>
                          <span className="srw-excluded-ghost-copy">
                            <strong>{line.description}</strong>
                            <span>
                              {packageUnsafe
                                ? `Excluding ${vendorName} leaves no order-safe option for ${line.description}; the remaining package option needs confirmation.`
                                : `Excluding ${vendorName} leaves no approved purchasing option for ${line.description}.`}
                            </span>
                          </span>
                          <button
                            type="button"
                            className="srw-btn srw-btn-undo"
                            data-testid="savings-review-excluded-vendor-keep"
                            disabled={arrangementFrozen || hasPendingMoves}
                            onClick={() => handleKeepAvoidedVendorForLine(line.lineId, vendorId)}
                          >
                            Keep {vendorName} for this item
                          </button>
                        </li>
                      ))}
                    </ul>
                  </section>
                );
              })}
            </div>
          )}

          {/* Vendors that could supply part of this order but currently
              supply none of it. Deliberately quieter than the active cards:
              product evidence and gestures, never $0.00 accounting. */}
          {availableVendors.length > 0 && (
            <section className="srw-available" data-testid="savings-review-available-vendors">
              <header className="srw-available-head">
                <h3>Available vendors</h3>
                <p>Other suppliers with products that can fulfill items in this plan.</p>
              </header>
              <div className="srw-available-list" role="list" aria-label="Available vendors">
                {availableVendors.map((entry) => renderAvailableVendor(entry))}
              </div>
            </section>
          )}
        </div>

        {/* The sticky summary: always visible, always the WHOLE plan — the
            item list may be filtered, the money never is. */}
        <aside className="srw-side" data-testid="savings-review-summary">
          <section className="srw-side-block">
            <h4>{changedLineIds.length > 0 ? 'Your scenario' : 'Recommended plan'}</h4>
            <div className="srw-plan-facts">
              <span>{plan_.lines.length} product{plan_.lines.length === 1 ? '' : 's'}</span>
              <span>{plan_.summary.vendorOrderCount} vendor{plan_.summary.vendorOrderCount === 1 ? '' : 's'}</span>
              <span>{scenarioComparison.scenarioPickupStops} pickup stop{scenarioComparison.scenarioPickupStops === 1 ? '' : 's'}</span>
            </div>
            <div className="srw-total-row">
              <span>{scenarioFinancialIncomplete
                ? 'Partial subtotal for assigned products'
                : 'Product subtotal'}</span>
              <span>{money(scenarioComparison.scenario.landed.productSubtotal)}</span>
            </div>
            <div className="srw-total-row srw-total-charge">
              <span>{scenarioFinancialIncomplete ? 'Fees in partial arrangement' : 'Fees'}</span>
              <span>{money(scenarioComparison.scenario.landed.fees)}</span>
            </div>
            <div className="srw-total-row srw-total-final" data-testid="savings-review-landed-total">
              <span>{scenarioFinancialIncomplete ? 'Landed total' : 'Estimated landed total'}</span>
              <span>{scenarioFinancialIncomplete
                ? 'Incomplete'
                : money(scenarioComparison.scenario.landed.landedTotal)}</span>
            </div>
            {changedLineIds.length > 0 ? (
              <div className="srw-compared" data-testid="savings-review-compared">
                <strong>Compared with recommended</strong>
                <div>
                  <span>Landed total</span>
                  <span>{purchaseTotalChangeText(scenarioComparison.landedDelta)}</span>
                </div>
                <div>
                  <span>Quantity difference</span>
                  <span>{quantityDifferenceText(scenarioComparison)}</span>
                </div>
                <div title="Compares both choices using the same quantity, before fees and tax.">
                  <span>Comparable price impact</span>
                  <span className={scenarioComparison.grossPriceImpactTotal > 0.004
                    ? 'srw-impact-savings'
                    : scenarioComparison.grossPriceImpactTotal < -0.004
                      ? 'srw-impact-warn'
                      : undefined}
                  >
                    {grossPriceImpactText(
                      scenarioComparison.grossPriceImpactTotal,
                      scenarioComparison.grossPriceImpactComplete,
                    )}
                  </span>
                </div>
                {scenarioComparison.missingBaselineSpend > 0 && (
                  <div>
                    <span>Missing from order · not savings</span>
                    <span>{money(scenarioComparison.missingBaselineSpend)}</span>
                  </div>
                )}
                {scenarioComparison.pickupStopDelta !== 0 && (
                  <div><span>Pickup stops</span><span>{countDeltaText(scenarioComparison.pickupStopDelta, 'stop')}</span></div>
                )}
                {scenarioComparison.vendorOrderDelta !== 0 && (
                  <div><span>Vendors</span><span>{countDeltaText(scenarioComparison.vendorOrderDelta, 'vendor')}</span></div>
                )}
                <button
                  type="button"
                  className="srw-btn srw-btn-undo"
                  data-testid="savings-review-reset-recommended"
                  disabled={hasPendingMoves}
                  onClick={handleResetToRecommended}
                >
                  Reset to recommended
                </button>
              </div>
            ) : (
              <>
                {/* Genuine price savings still on the table, at the chosen
                    quantities — the ONLY figure here allowed to wear green. */}
                {plan_.summary.grossPriceSavingsAvailable > 0 && (
                  <div
                    className="srw-total-row srw-savings-row"
                    data-testid="savings-review-savings"
                    title="What switching the flagged items to their lowest unit price would save at the same quantities. Compares both choices using the same quantity, before fees and tax."
                  >
                    <span>Additional gross price savings available</span>
                    <span>{money(plan_.summary.grossPriceSavingsAvailable)}</span>
                  </div>
                )}
                {/* The spend-reduction ceiling is a purchase-total fact: the
                    cheaper packages usually deliver DIFFERENT quantities, so
                    it is worded and coloured as cash, never as savings. */}
                {plan_.summary.productSavings > 0 && (
                  <div
                    className="srw-total-row srw-total-charge"
                    data-testid="savings-review-lower-spend"
                    title="Choosing each item's lowest-cost package. Quantities delivered may change, and fees can offset this."
                  >
                    <span>Lower-spend packs available</span>
                    <span>up to {money(plan_.summary.productSavings)} less</span>
                  </div>
                )}
              </>
            )}
            {/* The quantity/target summary: covered means the delivered
                quantity actually reaches the need, not merely assigned. */}
            <div className="srw-coverage-row" data-testid="savings-review-coverage-summary">
              {plan_.summary.needsMetCount} of {plan_.summary.itemCount} need{plan_.summary.itemCount === 1 ? '' : 's'} met
              {plan_.summary.aboveTargetCount > 0
                ? ` · ${plan_.summary.aboveTargetCount} product${plan_.summary.aboveTargetCount === 1 ? '' : 's'} above target`
                : ''}
            </div>
            {allComplete && (
              <div className="srw-total-done">All vendor orders created.</div>
            )}
          </section>

          <section className="srw-side-block" data-testid="savings-review-path">
            <h4>Your shopping path</h4>
            <ul className="srw-path-list">
              <li>
                {pathSummary.deliveryReady} delivery order{pathSummary.deliveryReady === 1 ? '' : 's'} ready
                {pathSummary.deliveryTotal > pathSummary.deliveryReady
                  ? ` of ${pathSummary.deliveryTotal}` : ''}
              </li>
              {pathSummary.belowMinimum > 0 && (
                <li className="srw-note-warn">
                  {pathSummary.belowMinimum} vendor{pathSummary.belowMinimum === 1 ? '' : 's'} below minimum
                </li>
              )}
              {pathSummary.pickupStops > 0 && (
                <li className="srw-path-pickup">
                  {pathSummary.pickupStops} pickup stop{pathSummary.pickupStops === 1 ? '' : 's'} required
                </li>
              )}
              {pathSummary.finished > 0 && (
                <li className="srw-note-ok">
                  {pathSummary.finished} vendor order{pathSummary.finished === 1 ? '' : 's'} created
                </li>
              )}
            </ul>
          </section>

        </aside>
      </div>

      <footer className="srw-finish" data-testid="savings-review-finish-bar">
        <div>
          <strong>Ready with this scenario?</strong>
          <span>
            Done creates the current purchase orders and pickup lists, then opens Purchasing.
          </span>
          {scenarioHasBlockingLines && (
            <span className="srw-note-warn">Resolve every unassigned or package-check item first.</span>
          )}
        </div>
        <button
          type="button"
          className="srw-btn srw-btn-primary srw-btn-done"
          data-testid="savings-review-done"
          disabled={doneBusy || hasPendingMoves || !!busyVendorId || scenarioHasBlockingLines}
          onClick={handleDone}
        >
          {hasPendingMoves ? 'Saving changes…' : busyVendorId || doneBusy ? 'Creating orders…' : 'Done'}
        </button>
      </footer>

      <style>{`
        .srw { box-sizing: border-box; max-width: 100%; overflow-x: clip; font-family: Montserrat, sans-serif; color: #3D2518; padding: 0 env(safe-area-inset-right, 0px) calc(2rem + env(safe-area-inset-bottom, 0px)) env(safe-area-inset-left, 0px); }
        .srw *, .srw *::before, .srw *::after { box-sizing: border-box; }
        .srw-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; padding: 1rem 1.1rem 0.5rem; flex-wrap: wrap; }
        .srw-head h1 { margin: 0; font-size: 1.35rem; font-weight: 700; }
        .srw-head p { margin: 0.25rem 0 0; color: #7a6e66; font-size: 0.85rem; max-width: 56ch; line-height: 1.5; }
        .srw-note { margin: 0.6rem 1.1rem 0; background: #FFF8E6; border: 1px solid #F0E0B0; border-radius: 8px; color: #8a6d1a; font-size: 0.82rem; padding: 0.55rem 0.7rem; }

        .srw-toolbar { display: flex; flex-wrap: wrap; align-items: center; gap: 0.4rem; padding: 0.6rem 1.1rem; background: #FAF6F2; border-top: 1px solid #EFE6DC; border-bottom: 1px solid #EFE6DC; }
        .srw-search { flex: 0 1 220px; border: 1px solid #D4C4B0; border-radius: 999px; padding: 0.35rem 0.8rem; font-size: 0.8rem; font-family: inherit; background: #fff; color: #3D2518; }
        .srw-chip { border: 1px solid #D4C4B0; background: #FFFDF9; color: #5A4A42; border-radius: 999px; padding: 0.3rem 0.7rem; font-size: 0.72rem; font-weight: 700; cursor: pointer; font-family: inherit; }
        .srw-chip.is-on { background: #3A2418; border-color: #3A2418; color: #fff; }
        .srw-chip:disabled { opacity: 0.45; cursor: not-allowed; }
        .srw-chip-reset { border-color: #8B5E3C; color: #8B5E3C; }
        .srw-toolbar-gap { flex: 1; }
        .srw-group-toggle { display: inline-flex; gap: 0.3rem; }
        .srw-toolbar-label { color: #8b7b73; font-size: 0.66rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; margin-left: 0.25rem; }
        .srw-vendor-planner { margin: 0.75rem 1.1rem 0; border: 1px solid #DECDBC; border-radius: 16px; background: #FFFDF9; box-shadow: 0 6px 22px rgba(61,37,24,0.06); overflow: hidden; }
        .srw-vendor-planner-head { display: flex; align-items: flex-start; justify-content: space-between; gap: 1rem; padding: 0.85rem 0.9rem 0.75rem; background: linear-gradient(135deg, #FAF6F2 0%, #FFFDF9 68%); border-bottom: 1px solid #EFE6DC; }
        .srw-eyebrow { display: block; margin-bottom: 0.15rem; color: #8B5E3C; font-size: 0.64rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.08em; }
        .srw-vendor-planner-head h2 { margin: 0; font-size: 1rem; line-height: 1.3; }
        .srw-vendor-planner-head p { max-width: 68ch; margin: 0.22rem 0 0; color: #6B5A50; font-size: 0.76rem; line-height: 1.45; }
        .srw-vendor-planner-actions { display: flex; flex-wrap: wrap; justify-content: flex-end; gap: 0.4rem; flex: 0 1 auto; }
        .srw-constraints { display: flex; align-items: center; flex-wrap: wrap; gap: 0.35rem; padding: 0.55rem 0.9rem 0; font-size: 0.72rem; color: #5A4A42; }
        .srw-constraint { border: 1px solid #D5B08C; border-radius: 999px; background: #FFF4E7; color: #7A4925; padding: 0.22rem 0.55rem; font: inherit; font-weight: 700; }
        .srw-vendor-tabs { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 330px), 1fr)); gap: 0.65rem; padding: 0.75rem 0.9rem; align-items: stretch; }
        .srw-vendor-resolution { margin: 0 0.9rem 0.75rem; border: 1px solid #E8B9AE; border-radius: 10px; background: #FDECEA; color: #8A3026; padding: 0.65rem 0.75rem; }
        .srw-vendor-resolution h3 { margin: 0 0 0.4rem; font-size: 0.8rem; }
        .srw-vendor-resolution > div { display: flex; align-items: center; justify-content: space-between; gap: 0.75rem; padding-top: 0.4rem; border-top: 1px solid rgba(163,59,46,0.16); font-size: 0.74rem; line-height: 1.4; }
        .srw-scenario-breakdown { margin: 0 0.9rem 0.75rem; border: 1px solid #E2D3C3; border-radius: 10px; background: #fff; padding: 0.65rem 0.75rem; font-size: 0.76rem; }
        .srw-scenario-breakdown h3 { margin: 0 0 0.12rem; color: #3D2518; font-size: 0.8rem; }
        .srw-scenario-breakdown > p { margin: 0 0 0.35rem; color: #7A6E66; font-size: 0.7rem; }
        .srw-scenario-breakdown > div { display: grid; grid-template-columns: minmax(0, 1.1fr) minmax(0, 1fr) 6.5rem; align-items: baseline; gap: 0.5rem; padding: 0.28rem 0; border-top: 1px solid #F2EBE5; }
        .srw-scenario-breakdown > div > strong { justify-self: end; font-variant-numeric: tabular-nums; }
        /* The narrator card: directly under the scenario controls, above the
           active vendor cards — compact, visually distinct, same palette. */
        .srw-scenario-explanation { display: block; margin: 0.7rem 0.9rem 0; border: 1px solid #D7E0D3; border-radius: 9px; background: #F8FBF7; padding: 0.65rem 0.7rem; }
        .srw-scenario-explanation > p { margin: 0.18rem 0 0; color: #3D2518; font-size: 0.76rem; line-height: 1.55; }
        .srw-scenario-explanation-meta { display: flex; align-items: center; justify-content: space-between; gap: 0.7rem; margin-top: 0.45rem; color: #6B5A50; font-size: 0.66rem; }
        .srw-scenario-explanation-meta > span { display: inline-flex; align-items: center; gap: 0.35rem; }
        .srw-scenario-explanation-meta i { width: 0.75rem; height: 0.75rem; border: 2px solid #CBD7C7; border-top-color: #4D714E; border-radius: 50%; animation: srw-spin 0.8s linear infinite; }
        @keyframes srw-spin { to { transform: rotate(360deg); } }
        .srw-scenario-change-facts { grid-column: 1 / -1; display: grid; gap: 0.16rem; margin: 0; padding: 0 0 0 1rem; color: #6B5A50; font-size: 0.7rem; line-height: 1.4; }
        .srw-scenario-change-facts li::marker { color: #B58B68; }
        .srw-scenario-unresolved { grid-column: 1 / -1; border-left: 3px solid #B84A3A; border-radius: 6px; background: #FDECEA; color: #7F2D24; padding: 0.5rem 0.6rem; }
        .srw-scenario-unresolved p { margin: 0; font-size: 0.72rem; line-height: 1.5; }
        .srw-scenario-unresolved > span { display: block; margin-top: 0.32rem; font-size: 0.67rem; font-weight: 800; text-transform: uppercase; letter-spacing: 0.035em; }
        .srw-scenario-unresolved > div { display: flex; flex-wrap: wrap; gap: 0.35rem; margin-top: 0.45rem; }
        .srw-scenario-unresolved .srw-btn { width: auto; min-height: 36px; }
        /* The scenario ledger: a titled band, then the metric grid under it.
           The header sits at the top-left like every other section header —
           never vertically centred against the numbers. */
        .srw-vendor-planner-impact { display: flex; flex-direction: column; gap: 0.55rem; padding: 0.75rem 0.9rem; border-top: 1px solid #E4D9CD; background: #F7F4F0; }
        .srw-impact-baseline p { margin: 0.05rem 0 0; color: #5A4A42; font-size: 0.78rem; line-height: 1.5; max-width: 72ch; }
        .srw-impact-head { display: flex; align-items: baseline; justify-content: space-between; gap: 0.3rem 1rem; flex-wrap: wrap; }
        .srw-impact-head h3 { margin: 0.05rem 0 0; font-size: 0.88rem; color: #3D2518; }
        .srw-impact-status { color: #6B5A50; font-size: 0.7rem; line-height: 1.4; text-align: right; max-width: 34rem; }
        .srw-vendor-planner-impact dl { display: grid; grid-template-columns: repeat(auto-fit, minmax(8.5rem, 1fr)); gap: 0.5rem 1rem; margin: 0; }
        .srw-vendor-planner-impact dl > div { display: flex; flex-direction: column; align-items: flex-start; gap: 0.08rem; }
        .srw-vendor-planner-impact dt { color: #7A6E66; font-size: 0.62rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; }
        .srw-vendor-planner-impact dd { margin: 0; font-size: 0.78rem; font-weight: 800; font-variant-numeric: tabular-nums; }
        .srw-vendor-planner-impact dd > span { display: block; }
        /* The missing list is product names, not a metric: it spans the whole
           footer, one product per row, left-aligned — with a row cap that
           scrolls and per-name ellipsis so a long catalog or a long name can
           never turn the impact grid into a wall of text. */
        .srw-vendor-planner-impact dl > .srw-impact-missing { grid-column: 1 / -1; align-items: stretch; }
        .srw-impact-missing dd { font-weight: 600; min-width: 0; }
        .srw-impact-missing-list { list-style: none; margin: 0; padding: 0.1rem 0 0; max-height: 8.4rem; overflow-y: auto; display: flex; flex-direction: column; gap: 0.12rem; }
        .srw-impact-missing-list li { display: flex; align-items: baseline; min-width: 0; font-size: 0.72rem; line-height: 1.35; }
        .srw-impact-missing-name { min-width: 0; overflow: hidden; text-overflow: ellipsis; white-space: nowrap; color: #3D2518; }
        .srw-impact-missing-cost { white-space: nowrap; color: #6B5A50; font-weight: 600; font-variant-numeric: tabular-nums; }
        .srw-layout { display: flex; align-items: flex-start; gap: 0.9rem; padding: 0.85rem 1.1rem; }
        .srw-main { flex: 1 1 auto; min-width: 0; }
        .srw-side { flex: 0 0 320px; max-width: 320px; position: sticky; top: 0.5rem; display: flex; flex-direction: column; gap: 0.6rem; }
        .srw-side-block { background: #fff; border: 1px solid #EFE6DC; border-radius: 12px; padding: 0.65rem 0.75rem; }
        .srw-side-block h4 { margin: 0 0 0.45rem; font-size: 0.72rem; text-transform: uppercase; letter-spacing: 0.05em; color: #8b7b73; }
        .srw-side-empty { margin: 0; font-size: 0.78rem; color: #8b7b73; }
        .srw-plan-facts { display: flex; gap: 0.35rem; flex-wrap: wrap; margin-bottom: 0.55rem; }
        .srw-plan-facts span { border-radius: 999px; background: #F2ECE5; color: #5A4A42; padding: 0.18rem 0.42rem; font-size: 0.68rem; font-weight: 700; }
        .srw-compared { border-top: 1px solid #E4D9CD; margin-top: 0.55rem; padding-top: 0.5rem; font-size: 0.76rem; }
        .srw-compared > strong { display: block; margin-bottom: 0.25rem; }
        .srw-compared > div { display: flex; justify-content: space-between; gap: 0.6rem; padding: 0.12rem 0; color: #5A4A42; }

        .srw-path-list { list-style: none; margin: 0; padding: 0; font-size: 0.82rem; color: #3D2518; display: flex; flex-direction: column; gap: 0.25rem; }
        .srw-path-list li { padding-left: 1rem; position: relative; }
        .srw-path-list li::before { content: '•'; position: absolute; left: 0.2rem; color: #C8B5A0; }
        .srw-path-pickup { color: #40606f; font-weight: 700; }
        .srw-note-ok { color: #3f6a44; }
        .srw-note-warn { color: #a35c2e; }
        .srw-note-neutral { color: #6b5a50; }
        .srw-coverage-row { margin-top: 0.3rem; font-size: 0.72rem; color: #6b5a50; }

        .srw-vendor-card { min-width: 0; border: 1px solid #E6D9CB; border-radius: 12px; padding: 0.7rem; background: #fff; display: flex; flex-direction: column; }
        .srw-vendor-card.is-pickup { border-color: #B9CDD9; background: #F4F8FB; }
        .srw-vendor-card.is-below-min { border-color: #E8C9A8; background: #FDF6EE; }
        .srw-vendor-card.is-complete { background: #F7F4F0; border-color: #D9CDBF; }
        .srw-vendor-card.is-excluded { border-style: dashed; border-color: #D5B08C; background: #FFFCF7; }
        .srw-vendor-card-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 0.55rem; }
        .srw-vendor-head { min-width: 0; display: flex; align-items: center; gap: 0.35rem; flex-wrap: wrap; font-size: 0.88rem; }
        .srw-vendor-head strong { min-width: 0; overflow-wrap: anywhere; }
        .srw-vendor-sub { margin-top: 0.15rem; font-size: 0.78rem; color: #5A4A42; }
        /* The dock: a faint stamped outline, not a second order card. Same
           ochre family as the Avoiding treatment — an opening, not a claim. */
        .srw-vendor-dock { margin-top: 0.4rem; }
        .srw-vendor-dock-toggle { display: block; width: 100%; text-align: left; border: 1px dashed #D5B08C; border-radius: 8px; background: #FFF8F0; color: #7A4925; font: inherit; font-size: 0.68rem; font-weight: 700; padding: 0.3rem 0.45rem; cursor: pointer; }
        .srw-vendor-dock-toggle[aria-expanded="true"] { border-bottom-left-radius: 0; border-bottom-right-radius: 0; }
        .srw-vendor-dock-list { list-style: none; margin: 0; padding: 0.25rem 0.45rem 0.35rem; border: 1px dashed #D5B08C; border-top: 0; border-bottom-left-radius: 8px; border-bottom-right-radius: 8px; background: #FFFCF7; display: flex; flex-direction: column; gap: 0.28rem; }
        .srw-vendor-dock-list li { display: flex; align-items: baseline; gap: 0.4rem; font-size: 0.68rem; line-height: 1.35; }
        .srw-dock-name { color: #3D2518; font-weight: 700; min-width: 0; }
        .srw-dock-note { color: #6B5A50; flex: 1; min-width: 0; }
        .srw-vendor-dock-list .srw-link-btn { white-space: nowrap; }
        /* Available vendors: could supply part of this order, currently
           supply none of it. A quiet dashed strip in the same ochre family as
           the dock — an opening, not a claim, with no order accounting. */
        .srw-available { margin-top: 0.85rem; border: 1px dashed #D9C6AF; border-radius: 12px; background: #FDFAF6; padding: 0.6rem 0.7rem; }
        .srw-available-head h3 { margin: 0; font-size: 0.8rem; color: #3D2518; }
        .srw-available-head p { margin: 0.12rem 0 0.5rem; color: #7A6E66; font-size: 0.7rem; line-height: 1.4; }
        .srw-available-list { display: grid; grid-template-columns: repeat(auto-fill, minmax(min(100%, 250px), 1fr)); gap: 0.5rem; align-items: start; }
        .srw-available-vendor { min-width: 0; border: 1px dashed #D5B08C; border-radius: 10px; background: #FFFCF7; padding: 0.5rem 0.6rem; display: flex; flex-direction: column; gap: 0.28rem; }
        .srw-available-vendor.is-excluded { background: #FFF8F0; }
        .srw-available-vendor.is-target { border-style: solid; border-color: #7A9B6E; background: #EDF3EC; }
        .srw-available-vendor.is-blocked { opacity: 0.45; }
        .srw-available-top { display: flex; align-items: flex-start; justify-content: space-between; gap: 0.45rem; }
        .srw-available-name { min-width: 0; display: flex; align-items: center; flex-wrap: wrap; gap: 0.3rem; font-size: 0.8rem; }
        .srw-available-name strong { min-width: 0; overflow-wrap: anywhere; }
        .srw-available-toggle { align-self: flex-start; border: 0; background: none; padding: 0; color: #7A4925; font: inherit; font-size: 0.68rem; font-weight: 700; cursor: pointer; text-decoration: underline; text-decoration-style: dotted; text-underline-offset: 2px; }
        .srw-available-none { color: #8B7B73; font-size: 0.68rem; }
        .srw-available-products { margin: 0; color: #6B5A50; font-size: 0.68rem; line-height: 1.4; }
        .srw-available-detail { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.4rem; }
        .srw-available-detail li { display: flex; flex-direction: column; gap: 0.1rem; border-top: 1px dashed #EAD9C6; padding-top: 0.35rem; font-size: 0.68rem; line-height: 1.4; }
        .srw-available-detail li:first-child { border-top: 0; padding-top: 0; }
        .srw-available-offer { color: #3D2518; font-weight: 700; }
        .srw-available-current { color: #6B5A50; }
        .srw-available-detail .srw-link-btn { align-self: flex-start; padding-left: 0; }
        .srw-available-avoid { align-self: flex-start; color: #8B5E3C; padding-left: 0; }
        .srw-available-vendor .srw-vendor-impact { margin-top: 0.1rem; padding-top: 0.3rem; }
        .srw-vendor-toggle { flex: 0 0 auto; min-height: 2rem; border: 1px solid #D5B08C; border-radius: 999px; background: #FFF8F0; color: #7A4925; padding: 0.28rem 0.65rem; font-family: inherit; font-size: 0.7rem; font-weight: 800; cursor: pointer; }
        .srw-vendor-toggle.is-include { border-color: #7A9B6E; background: #EDF3EC; color: #315E39; }
        .srw-vendor-toggle:disabled { opacity: 0.45; cursor: not-allowed; }
        .srw-vendor-money { display: grid; grid-template-columns: repeat(4, minmax(0, 1fr)); margin: 0.58rem 0 0; border: 1px solid #EFE6DC; border-radius: 9px; overflow: hidden; background: rgba(255,255,255,0.72); }
        .srw-vendor-money > div { min-width: 0; padding: 0.42rem 0.46rem; border-left: 1px solid #EFE6DC; }
        .srw-vendor-money > div:first-child { border-left: 0; }
        .srw-vendor-money dt { min-height: 1.8em; color: #7A6E66; font-size: 0.6rem; line-height: 1.15; }
        .srw-vendor-money dd { margin: 0.18rem 0 0; color: #3D2518; font-size: 0.76rem; font-weight: 800; text-align: right; font-variant-numeric: tabular-nums; white-space: nowrap; }
        .srw-vendor-money .srw-tax-placeholder { color: #8B7B73; font-size: 0.62rem; font-weight: 600; white-space: normal; }
        .srw-vendor-money-total { background: #FAF6F2; }
        .srw-vendor-impact { margin-top: auto; padding-top: 0.52rem; color: #6B5A50; font-size: 0.68rem; font-weight: 700; line-height: 1.35; display: flex; flex-direction: column; gap: 0.12rem; }
        /* Cash and quantity lines stay NEUTRAL — green belongs only to a
           genuine equivalent-quantity price saving. */
        .srw-impact-neutral { color: #6B5A50; }
        .srw-impact-savings { color: #2F6F4F; }
        .srw-impact-warn { color: #A35C2E; }
        .srw-vendor-flags { display: flex; flex-wrap: wrap; gap: 0.25rem 0.4rem; margin: 0.35rem 0 0; }
        .srw-vendor-blocked { margin: 0.35rem 0 0; color: #a35c2e; font-size: 0.7rem; line-height: 1.4; }
        .srw-readiness { font-size: 0.66rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.04em; border-radius: 4px; padding: 0.1rem 0.35rem; background: #EDF3EC; color: #3f6a44; }
        .srw-readiness-pickup { background: #E8EEF3; color: #40606f; }
        .srw-readiness-below-minimum { background: #FBEEE4; color: #a35c2e; }
        .srw-readiness-fee-risk { background: #FFF8E6; color: #8a6d1a; }
        .srw-readiness-finished { background: #E4EDE2; color: #3f6a44; }
        .srw-readiness-unlinked { background: #FDF3E3; color: #8a5a00; }
        .srw-readiness-empty { background: #F2ECE5; color: #6b5a50; }

        .srw-list { list-style: none; margin: 0; padding: 0; display: flex; flex-direction: column; gap: 0.45rem; }
        .srw-item { border: 1px solid #EFE6DC; border-radius: 10px; background: #fff; transition: box-shadow 140ms ease, transform 140ms ease, background-color 400ms ease; }
        .srw-item:hover { box-shadow: 0 2px 8px rgba(0,0,0,0.07); }
        .srw-item.is-open { border-color: #C8B5A0; }
        .srw-item.is-dragging { opacity: 0.4; }
        .srw-item.is-locked { background: #FBF9F7; }
        .srw-item.is-stale-changed { border-color: #D08A46; background: #FFF4E7; box-shadow: 0 0 0 3px rgba(208,138,70,0.2); }
        .srw-item.is-changed { border-left: 3px solid #8B5E3C; }
        /* The row has just become a different vendor's product: flash it so the
           changed pack and price are noticed rather than discovered later. */
        .srw-item.just-landed { animation: srw-land 2000ms ease; }
        @keyframes srw-land {
          0%, 28%, 56% { background: #E4EDE2; transform: scale(1.005); box-shadow: 0 4px 14px rgba(63,106,68,0.2); }
          14%, 42%, 72% { background: #fff; transform: scale(1); box-shadow: none; }
          100% { background: #fff; transform: scale(1); }
        }
        @media (prefers-reduced-motion: reduce) {
          .srw-item.just-landed { animation: none; background: #F3F8F2; }
          .srw-scenario-explanation-meta i { animation: none; }
        }

        /* ── Row grid ─────────────────────────────────────────────────────
           Six tracks, every one fixed or fr. Never put an auto or
           max-content track here: each <li> is an INDEPENDENT grid, so a
           content-sized track resolves to a different width per card and the
           list reads ragged — that was the bug, not a preference. Cells are
           placed explicitly rather than by source order, because a
           package-repair or no-vendor row renders only the name and chevron
           and would otherwise pull them leftwards. */
        .srw-row {
          display: grid;
          grid-template-columns:
            minmax(0, 1.7fr)    /* 1 · item     — name / need / flags     */
            minmax(0, 0.9fr)    /* 2 · vendor   — name / reason chip      */
            5.25rem             /* 3 · fulfil   — delivery | pickup tab   */
            minmax(0, 1.25fr)   /* 4 · buy      — packages / pack / PAR   */
            7.25rem             /* 5 · cost     — price / unit / badges   */
            18px;               /* 6 · chevron                            */
          gap: 0.5rem 0.9rem;
          align-items: baseline;
          padding: 0.55rem 0.7rem;
          cursor: pointer;
        }
        .srw-row-name    { grid-column: 1; grid-row: 1; min-width: 0; }
        .srw-row-vendor  { grid-column: 2; grid-row: 1; min-width: 0; }
        .srw-row-fulfill { grid-column: 3; grid-row: 1; }
        .srw-row-buy     { grid-column: 4; grid-row: 1; min-width: 0; }
        .srw-row-cost    { grid-column: 5; grid-row: 1; }
        .srw-row-chevron { grid-column: -2 / -1; grid-row: 1; justify-self: end; }

        /* Column 1 — the flags come OFF the "Need …" line and sit under the
           item name, so the first column stops being the tall one. */
        .srw-row-name strong { display: block; font-size: 0.88rem; font-weight: 600; line-height: 1.3; }
        .srw-row-need { display: block; color: #8b7b73; font-size: 0.74rem; line-height: 1.35; }
        .srw-row-badges { display: flex; flex-wrap: wrap; gap: 0.25rem; margin: 0.22rem 0 0; }
        /* Source rail: small stamped marks, not sentences. Solid border =
           same product, provably same pack; dashed = review first. The glyph
           carries spend direction, so colour is never the only signal. */
        .srw-source-rail { display: flex; flex-wrap: wrap; align-items: center; gap: 0.25rem; margin-top: 0.24rem; }
        .srw-source-rail-label { font-size: 0.62rem; color: #8b7b73; text-transform: uppercase; letter-spacing: 0.04em; }
        .srw-srcmark { display: inline-flex; align-items: center; gap: 0.22rem; border: 1px solid #D4C4B0; border-radius: 999px; background: #FAF6F2; color: #5A4A42; font: inherit; font-size: 0.64rem; font-weight: 700; padding: 0.08rem 0.42rem; cursor: pointer; max-width: 11rem; }
        .srw-srcmark:hover { border-color: #8B5E3C; color: #3D2518; }
        .srw-srcmark.is-unverified { border-style: dashed; border-color: #D5B08C; background: #FFF8F0; color: #7A4925; }
        .srw-srcmark-name { overflow: hidden; text-overflow: ellipsis; white-space: nowrap; min-width: 0; }
        .srw-srcmark-dir { font-size: 0.58rem; color: #6B5A50; }
        .srw-srcmark-more { max-width: none; }
        .srw-row-badges:empty { display: none; }
        .srw-why-btn { border: 0; background: transparent; color: #8B5E3C; padding: 0; font: inherit; font-size: 0.66rem; font-weight: 700; text-decoration: underline; cursor: pointer; text-transform: none; letter-spacing: 0; }
        .srw-changed-from { border-top: 1px solid #F6E9DA; padding: 0.3rem 0.7rem; background: #FFFAF3; color: #7A4925; font-size: 0.7rem; font-weight: 600; }
        .srw-why-panel { margin: 0 0.7rem 0.55rem; border: 1px solid #D7E1D4; border-radius: 8px; background: #F8FBF7; padding: 0.55rem 0.65rem; font-size: 0.74rem; }
        .srw-why-panel > p { margin: 0.2rem 0 0.45rem; color: #5A4A42; line-height: 1.45; }
        .srw-why-panel dl { margin: 0; }
        .srw-why-panel dl > div { display: flex; justify-content: space-between; gap: 0.7rem; padding: 0.12rem 0; }
        .srw-why-panel dt { color: #7a6e66; }
        .srw-why-panel dd { margin: 0; text-align: right; font-weight: 700; }
        .srw-why-panel ul { margin: 0.45rem 0 0; padding: 0.4rem 0 0 1.05rem; border-top: 1px solid #D7E1D4; color: #5A4A42; }

        .srw-row-vendor { display: flex; flex-direction: column; align-items: flex-start; gap: 0.18rem; font-size: 0.82rem; line-height: 1.3; }
        .srw-row-vendor strong { font-weight: 600; }

        .srw-row-fulfill { display: flex; align-items: baseline; }
        .srw-row-fulfill .srw-fulfill { white-space: nowrap; }

        .srw-row-buy { font-size: 0.74rem; color: #7a6e66; line-height: 1.35; }
        .srw-row-cost { text-align: right; }
        .srw-row-cost strong { display: block; font-size: 0.88rem; line-height: 1.3; }
        .srw-row-cost small { color: #8b7b73; font-size: 0.7rem; display: block; }
        .srw-row-cost .srw-badge { display: inline-block; margin-top: 0.15rem; }
        .srw-price-unavailable { color: #8b7b73; font-weight: 700; font-size: 0.72rem; }
        .srw-row-chevron { color: #C8B5A0; font-size: 0.8rem; }
        .srw-reason { font-size: 0.66rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; border-radius: 4px; padding: 0.1rem 0.3rem; background: #F2ECE5; color: #6b5a50; }
        .srw-reason-yours { background: #FDF3E3; color: #8a5a00; }

        .srw-fulfill { font-size: 0.64rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.05em; border-radius: 4px; padding: 0.12rem 0.4rem; }
        .srw-fulfill-pickup { background: #40606f; color: #fff; }
        .srw-fulfill-delivery { background: #E4EDE2; color: #3f6a44; }

        .srw-badge { font-size: 0.62rem; font-weight: 700; text-transform: uppercase; letter-spacing: 0.03em; border-radius: 4px; padding: 0.1rem 0.3rem; background: #F2ECE5; color: #6b5a50; }
        .srw-badge-cost { background: #E4EDE2; color: #3f6a44; }
        .srw-badge-unit { background: #E8EEF3; color: #40606f; }
        .srw-badge-warn { background: #FBEEE4; color: #a35c2e; }
        .srw-badge-rec { background: #E4EDE2; color: #3f6a44; }
        .srw-badge-changed { background: #FDF3E3; color: #8a5a00; }
        .srw-badge-used { background: #EFE7DC; color: #6b5540; }

        .srw-row-detail { border-top: 1px solid #F2EBE5; padding: 0.6rem 0.7rem 0.7rem; }
        .srw-detail-intro { margin: 0 0 0.5rem; font-size: 0.78rem; color: #6b5a50; }
        .srw-alt-list { display: flex; flex-direction: column; gap: 0.4rem; }
        /* One option card. Row 1 is the only two-column row (vendor | price);
           the notes, the actions and the full comparison each span the WHOLE
           card — an auto-sized button column must never decide how wide a
           sentence gets to be. */
        .srw-alt { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 0.3rem 0.8rem; border: 1px solid #EFE6DC; border-radius: 8px; padding: 0.45rem 0.55rem; }
        .srw-alt.is-current { border-color: #C8B5A0; background: #FAF6F2; }
        .srw-alt-vendor { display: flex; align-items: center; gap: 0.35rem; flex-wrap: wrap; font-size: 0.82rem; }
        .srw-alt-pack { color: #7a6e66; font-size: 0.74rem; margin-top: 0.1rem; }
        .srw-alt-notes { grid-column: 1 / -1; list-style: none; margin: 0.15rem 0 0; padding: 0.35rem 0 0; border-top: 1px dashed #F2EBE5; display: grid; grid-template-columns: repeat(auto-fill, minmax(min(100%, 15rem), 1fr)); gap: 0.14rem 1.1rem; font-size: 0.72rem; line-height: 1.4; }
        .srw-alt-side { text-align: right; display: flex; flex-direction: column; gap: 0.1rem; align-items: flex-end; }
        .srw-alt-price { font-size: 0.86rem; }
        .srw-alt-side small { color: #8b7b73; font-size: 0.7rem; }
        .srw-diff-up { color: #a35c2e !important; }
        /* A lower checkout figure is a cash fact between DIFFERENT delivered
           quantities — neutral, never the savings green. */
        .srw-diff-down { color: #6b5a50 !important; }
        .srw-alt-actions { grid-column: 1 / -1; display: flex; flex-wrap: wrap; gap: 0.35rem 0.55rem; align-items: center; margin-top: 0.1rem; }
        /* The full comparison is an inset panel, not more prose: opening and
           closing it must be unmistakable. */
        .srw-alt .srw-calc { grid-column: 1 / -1; margin-top: 0.2rem; border: 1px solid #E4D9CD; border-radius: 8px; background: #FCFAF7; padding: 0.5rem 0.6rem; }
        .srw-alt.is-current .srw-calc { background: #fff; }

        .srw-vendor-groups { display: grid; grid-template-columns: repeat(auto-fit, minmax(280px, 1fr)); gap: 0.75rem; align-items: start; }
        .srw-vendor-section { border: 2px solid #EFE6DC; border-radius: 12px; background: #fff; padding: 0.6rem 0.7rem; min-width: 0; transition: border-color 140ms ease, box-shadow 140ms ease; }
        .srw-vendor-section.is-target { border-color: #7a9b6e; box-shadow: 0 0 0 4px rgba(122,155,110,0.16); }
        .srw-vendor-section.is-blocked { opacity: 0.45; }
        .srw-vendor-section.is-complete { background: #F7F4F0; border-color: #D9CDBF; }
        .srw-vendor-section.is-excluded { border-style: dashed; border-color: #D5B08C; background: #FFFCF7; }
        /* A banded header across the whole section, visually distinct from
           the item rows below it — the vendor label is a header, not a
           caption floating between cards. */
        .srw-vendor-section-head { display: flex; align-items: center; gap: 0.45rem; flex-wrap: wrap; font-size: 0.92rem; margin: -0.6rem -0.7rem 0.55rem; padding: 0.5rem 0.7rem; background: #FAF6F2; border-bottom: 1px solid #EFE6DC; border-radius: 10px 10px 0 0; }
        .srw-vendor-section-name { font-size: 0.95rem; min-width: 0; overflow-wrap: anywhere; }
        /* Bordered like every other chip on this screen, so it stays a pill
           on ANY band tint — the is-complete band is nearly the same colour
           as the old borderless fill. */
        .srw-vendor-count-pill { border: 1px solid #D4C4B0; border-radius: 999px; background: #FFFDF9; color: #5A4A42; padding: 0.12rem 0.5rem; font-size: 0.66rem; font-weight: 700; white-space: nowrap; }
        .srw-vendor-section-note { color: #7A6E66; font-size: 0.72rem; }
        .srw-vendor-section-total { margin-left: auto; font-weight: 800; font-variant-numeric: tabular-nums; color: #3D2518; white-space: nowrap; }
        .srw-vendor-section.is-complete .srw-vendor-section-head { background: #F2EDE7; }
        .srw-vendor-section.is-excluded .srw-vendor-section-head { background: #FFF8F0; border-bottom-style: dashed; }
        .srw-excluded-ghost { list-style: none; display: grid; grid-template-columns: auto minmax(0, 1fr) auto; align-items: center; gap: 0.55rem; border: 1px dashed #D5B08C; border-radius: 9px; background: #FFF8E6; color: #6B5540; padding: 0.6rem; opacity: 0.82; }
        .srw-excluded-ghost-caution { color: #A35C2E; font-size: 1rem; align-self: start; line-height: 1.2; }
        .srw-excluded-ghost-copy { min-width: 0; display: flex; flex-direction: column; gap: 0.18rem; font-size: 0.72rem; line-height: 1.4; }
        .srw-excluded-ghost-copy strong { color: #3D2518; font-size: 0.8rem; }
        .srw-excluded-ghost .srw-btn { width: auto; margin: 0; white-space: nowrap; }
        .srw-vendor-section .srw-row { grid-template-columns: minmax(0, 1fr) 6rem 18px; gap: 0.3rem 0.55rem; }
        .srw-vendor-section .srw-row-name { grid-column: 1; grid-row: 1; }
        .srw-vendor-section .srw-row-cost { grid-column: 2; grid-row: 1; }
        .srw-vendor-section .srw-row-chevron { grid-column: 3; grid-row: 1; }
        .srw-vendor-section .srw-row-vendor { display: none; }
        .srw-vendor-section .srw-row-fulfill { display: none; }
        .srw-vendor-section .srw-row-buy { grid-column: 1 / -1; grid-row: 2; }
        .srw-empty { border: 1px dashed #DDD0C2; border-radius: 8px; padding: 0.8rem 0.6rem; text-align: center; color: #9c8d84; font-size: 0.75rem; line-height: 1.4; }
        .srw-empty-list { border: 1px dashed #DDD0C2; border-radius: 10px; padding: 1.4rem 1rem; text-align: center; color: #9c8d84; font-size: 0.82rem; }

        .srw-buy { font-weight: 700; color: #3D2518; display: block; }
        .srw-pack-line { display: block; }
        /* The auto-resolved package fact: the derived count leads, the source
           text follows, and Edit sits inline — a statement with a door, not a
           blocking question. */
        .srw-pack-auto { color: #5A4A42; }
        .srw-buy-reason { display: block; font-size: 0.68rem; color: #8b7b73; font-style: italic; }
        .srw-move-state { font-size: 0.68rem; color: #8b7b73; font-style: italic; padding: 0.15rem 0.7rem 0.3rem; }
        .srw-move-error { font-size: 0.7rem; color: #a33b2e; background: #fdecea; border: 1px solid #f5c6c0; border-radius: 6px; padding: 0.25rem 0.4rem; margin: 0.25rem 0.7rem 0.5rem; }
        .srw-item-reject { margin: 0.25rem 0.7rem 0.5rem; font-size: 0.72rem; color: #a33b2e; }
        .srw-calc-head { display: flex; flex-direction: column; gap: 0.1rem; margin-bottom: 0.2rem; }
        .srw-calc-head span { color: #8b7b73; font-size: 0.7rem; }
        /* Neutral by default: these are facts, and several of them are cash
           facts. Green arrives only via an explicit srw-note-ok on a genuine
           equivalent-quantity price saving. */
        .srw-opt-facts { display: flex; flex-direction: column; gap: 0.1rem; margin-top: 0.25rem; font-size: 0.7rem; color: #5A4A42; }
        .srw-pkg-review { margin-top: 0.3rem; font-size: 0.72rem; }
        .srw-pkg-review summary { cursor: pointer; color: #6b5a50; }
        .srw-pkg-review label { display: flex; align-items: center; gap: 0.3rem; margin-top: 0.25rem; }
        .srw-calc { display: flex; flex-direction: column; gap: 0.22rem; font-size: 0.74rem; color: #5A4A42; }
        .srw-calc-row { display: flex; justify-content: space-between; gap: 0.6rem; }
        .srw-calc-row > span:last-child { font-variant-numeric: tabular-nums; text-align: right; }
        .srw-calc-strong { font-weight: 700; color: #3D2518; }
        .srw-calc-note { font-size: 0.7rem; color: #8b7b73; font-style: italic; margin-top: 0.15rem; }
        .srw-calc-input { width: 4.5rem; border: 1px solid #D4C4B0; border-radius: 6px; padding: 0.1rem 0.3rem; font-size: 0.74rem; font-family: inherit; text-align: right; }
        .srw-review-btn { border: 1px solid #D4C4B0; background: #FFFDF9; color: #5A4A42; border-radius: 999px; padding: 0.2rem 0.6rem; font-size: 0.7rem; font-weight: 700; cursor: pointer; font-family: inherit; }
        .srw-switch-btn { background: #3A2418; border-color: #5A3A28; color: #fff; }
        .srw-link-btn { border: none; background: none; color: #8B5E3C; text-decoration: underline; cursor: pointer; font-family: inherit; font-size: 0.7rem; font-weight: 700; padding: 0; }

        .srw-tag { font-size: 0.66rem; text-transform: uppercase; letter-spacing: 0.04em; background: #F2ECE5; color: #6b5a50; border-radius: 4px; padding: 0.1rem 0.35rem; font-weight: 700; }
        .srw-tag-pickup { background: #E8EEF3; color: #40606f; }
        .srw-tag-unset { background: #FDF3E3; color: #8a5a00; }
        .srw-tag-done { background: #E4EDE2; color: #3f6a44; }
        .srw-flag { font-size: 0.7rem; color: #8b7b73; background: #FAF6F2; border-radius: 4px; padding: 0.08rem 0.3rem; }
        .srw-flag-warn { color: #a35c2e; background: #FBEEE4; }
        .srw-flag-ok { color: #3f6a44; background: #EDF3EC; }

        .srw-preview { position: sticky; top: env(safe-area-inset-top, 0px); z-index: 5; display: flex; flex-wrap: wrap; align-items: baseline; gap: 0.4rem 1rem; margin: 0.5rem 1.1rem 0; background: #1f2a24; color: #dfe9df; border-radius: 10px; padding: 0.55rem 0.8rem; font-size: 0.8rem; }
        .srw-preview strong { color: #fff; }
        .srw-preview-total { font-weight: 700; color: #a9d3a9; }

        .srw-btn { border-radius: 999px; padding: 0.5rem 0.9rem; font-size: 0.8rem; font-weight: 700; cursor: pointer; font-family: inherit; width: 100%; }
        .srw-btn-primary { background: #3A2418; border: 2px solid #5A3A28; color: #fff; }
        .srw-btn-primary:disabled { opacity: 0.45; cursor: not-allowed; }
        .srw-btn-ghost { background: transparent; border: 2px solid #C8B5A0; color: #5A4A42; width: auto; }
        .srw-btn-undo { background: transparent; border: 2px solid #C8B5A0; color: #5A4A42; margin-top: 0.4rem; font-size: 0.74rem; padding: 0.4rem 0.7rem; }
        .srw-btn-undo:disabled { opacity: 0.45; cursor: not-allowed; }
        .srw-head-actions { display: flex; gap: 0.5rem; align-items: center; }
        .srw-btn-done { width: auto; }

        .srw-total-row { display: flex; justify-content: space-between; gap: 0.75rem; font-size: 0.8rem; padding: 0.12rem 0; color: #5A4A42; }
        .srw-total-charge { color: #8b7b73; font-size: 0.76rem; }
        .srw-total-final { margin-top: 0.35rem; padding-top: 0.35rem; border-top: 1px solid #E4D9CD; font-weight: 700; color: #3D2518; font-size: 0.92rem; }
        .srw-savings-row { color: #2f6f4f; font-weight: 700; }
        .srw-total-done { margin-top: 0.4rem; font-size: 0.76rem; color: #3f6a44; }

        .srw-done-overlay { position: fixed; inset: 0; z-index: 20; display: flex; align-items: center; justify-content: center; padding: max(1.5rem, env(safe-area-inset-top, 0px)) max(1.5rem, env(safe-area-inset-right, 0px)) max(1.5rem, env(safe-area-inset-bottom, 0px)) max(1.5rem, env(safe-area-inset-left, 0px)); background: rgba(37, 26, 21, 0.48); }
        .srw-done-dialog { width: min(100%, 420px); background: #FFFDF9; border: 1px solid #EFE6DC; border-radius: 14px; box-shadow: 0 24px 64px rgba(62, 39, 35, 0.22); padding: 1.25rem 1.35rem; }
        .srw-done-dialog h2 { margin: 0 0 0.4rem; font-size: 1.05rem; font-weight: 700; color: #3D2518; }
        .srw-done-dialog p { margin: 0 0 0.9rem; font-size: 0.85rem; color: #5A4A42; line-height: 1.5; }
        .srw-exclusion-dialog { width: min(100%, 1080px); max-height: calc(100dvh - 3rem - env(safe-area-inset-top, 0px) - env(safe-area-inset-bottom, 0px)); overflow-y: auto; overscroll-behavior: contain; }
        .srw-exclusion-dialog-head { display: flex; justify-content: space-between; align-items: flex-start; gap: 1rem; border-bottom: 1px solid #EFE6DC; margin: -0.2rem 0 1rem; padding-bottom: 0.8rem; }
        .srw-exclusion-dialog-head h2 { margin-top: 0.15rem; }
        .srw-exclusion-dialog-head p { margin-bottom: 0; }
        .srw-exclusion-dialog-head .srw-exclusion-scope { margin-top: 0.3rem; color: #7A6E66; font-size: 0.74rem; }
        .srw-exclusion-review-toolbar { display: flex; align-items: center; justify-content: space-between; gap: 0.65rem; margin-bottom: 0.9rem; border: 1px solid #E2D3C3; border-radius: 10px; background: #FAF6F2; padding: 0.55rem 0.65rem; }
        .srw-exclusion-review-toolbar > span { color: #5A4A42; font-size: 0.74rem; font-weight: 700; }
        .srw-exclusion-items { display: grid; gap: 1rem; }
        .srw-exclusion-group > h3 { margin: 0 0 0.45rem; color: #6B5A50; font-size: 0.76rem; font-weight: 800; letter-spacing: 0.04em; text-transform: uppercase; }
        .srw-exclusion-grid { display: grid; grid-template-columns: repeat(auto-fit, minmax(min(100%, 310px), 1fr)); gap: 0.75rem; align-items: stretch; }
        .srw-exclusion-item { min-width: 0; height: 100%; border: 1px solid #E2D3C3; border-radius: 12px; background: #FFF; padding: 0.8rem; }
        .srw-exclusion-item.is-approved { border-color: #BFD4BB; box-shadow: inset 0 0 0 1px rgba(63, 106, 68, 0.08); }
        .srw-exclusion-item.is-unresolved { border-color: #D8A46F; background: #FFF9F1; }
        .srw-exclusion-item > header { display: flex; align-items: flex-start; justify-content: space-between; gap: 0.75rem; margin-bottom: 0.65rem; }
        .srw-exclusion-item h4 { margin: 0; color: #3D2518; font-size: 0.9rem; }
        .srw-exclusion-item header p { margin: 0.12rem 0 0; color: #7A6E66; font-size: 0.74rem; }
        .srw-exclusion-facts { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); gap: 0.5rem 0.9rem; margin: 0; }
        .srw-exclusion-facts > div { min-width: 0; border-top: 1px solid #F2EBE5; padding-top: 0.4rem; }
        .srw-exclusion-facts dt { color: #8B7B73; font-size: 0.66rem; font-weight: 700; text-transform: uppercase; }
        .srw-exclusion-facts dd { margin: 0.1rem 0 0; color: #3D2518; font-size: 0.76rem; line-height: 1.4; }
        .srw-exclusion-explanation, .srw-exclusion-no-alt p { margin: 0.7rem 0 0 !important; border-left: 3px solid #D5B08C; padding-left: 0.65rem; color: #4E4039 !important; font-size: 0.78rem !important; }
        .srw-exclusion-consequences { display: flex; flex-wrap: wrap; gap: 0.35rem; margin: 0.65rem 0 0; padding: 0; list-style: none; }
        .srw-exclusion-consequences li { border-radius: 999px; background: #FBEEE4; color: #8A5229; padding: 0.22rem 0.5rem; font-size: 0.68rem; font-weight: 700; }
        .srw-exclusion-choice-row { display: flex; align-items: flex-start; flex-wrap: wrap; gap: 0.45rem; margin-top: 0.75rem; }
        .srw-tag-approved { background: #E4EDE2; color: #3F6A44; }
        .srw-next-order-disabled { display: inline-flex; flex-direction: column; gap: 0.18rem; color: #8B7B73; }
        .srw-next-order-disabled small { max-width: 15rem; font-size: 0.62rem; }
        .srw-exclusion-change { margin-top: 0.65rem; }
        .srw-exclusion-change summary { width: max-content; color: #8A5229; font-size: 0.75rem; font-weight: 800; cursor: pointer; }
        .srw-exclusion-change > div { display: flex; flex-wrap: wrap; gap: 0.4rem; margin-top: 0.45rem; }
        .srw-exclusion-item-approval { display: flex; justify-content: flex-end; margin-top: 0.75rem; border-top: 1px solid #F2EBE5; padding-top: 0.6rem; }
        .srw-exclusion-summary { margin-top: 1rem; border-radius: 12px; background: #F5EEE7; padding: 0.8rem 0.9rem; }
        .srw-exclusion-summary h3 { margin: 0 0 0.45rem; font-size: 0.85rem; }
        .srw-exclusion-summary ul { display: grid; gap: 0.25rem; margin: 0; padding-left: 1.1rem; color: #5A4A42; font-size: 0.75rem; line-height: 1.45; }
        .srw-exclusion-apply-actions { position: sticky; bottom: -1.25rem; margin: 1rem -1.35rem -1.25rem; border-top: 1px solid #E2D3C3; background: rgba(255, 253, 249, 0.97); padding: 0.85rem 1.35rem calc(0.85rem + env(safe-area-inset-bottom, 0px)); }
        .srw-exclusion-pending-note { color: #8A5229; font-size: 0.72rem; font-weight: 700; }
        .srw-done-note { color: #a35c2e !important; font-size: 0.78rem !important; }
        .srw-done-actions { display: grid; gap: 0.5rem; }
        .srw-finish { display: flex; align-items: center; justify-content: space-between; gap: 1rem; margin: 0.25rem 1.1rem 0; border-top: 1px solid #E4D9CD; padding: 1rem 0 0; }
        .srw-finish > div { display: flex; flex-direction: column; gap: 0.2rem; }
        .srw-finish > div > span { color: #7a6e66; font-size: 0.76rem; }
        .srw-finish .srw-btn-done { min-width: 9rem; }

        .srw-repair-card { background: #fff; border: 1px solid #EFE6DC; border-radius: 8px; padding: 0.55rem 0.65rem; margin-top: 0.35rem; }
        .srw-repair-head { display: flex; flex-wrap: wrap; align-items: center; gap: 0.4rem; font-size: 0.78rem; color: #3D2518; }
        .srw-repair-sub { color: #8b7b73; font-size: 0.72rem; }
        .srw-repair-facts { display: grid; grid-template-columns: max-content 1fr; gap: 0.1rem 0.6rem; margin: 0.4rem 0 0.2rem; font-size: 0.74rem; }
        .srw-repair-facts dt { color: #8b7b73; }
        .srw-repair-facts dd { margin: 0; color: #3D2518; font-weight: 600; }
        .srw-repair-why { margin: 0.2rem 0 0.4rem; font-size: 0.72rem; color: #a35c2e; }
        .srw-repair-price-warning { margin: 0.25rem 0 0.45rem; padding: 0.4rem 0.5rem; border-left: 3px solid #B84A3A; background: #FDECEA; color: #8A3026; font-size: 0.72rem; line-height: 1.4; }
        .srw-repair-choices { border: none; margin: 0; padding: 0; }
        .srw-repair-choices label { display: flex; align-items: baseline; gap: 0.35rem; font-size: 0.76rem; color: #3D2518; margin-top: 0.2rem; }
        .srw-repair-choices label em { color: #8b7b73; font-style: normal; font-size: 0.68rem; }
        .srw-repair-price { margin-top: 0.45rem; display: flex; flex-direction: column; gap: 0.15rem; font-size: 0.76rem; color: #3D2518; }
        .srw-repair-unit-input { text-align: left; width: 6rem; }
        .srw-repair-unit-hint { color: #8b7b73; font-style: italic; font-size: 0.68rem; margin-left: 0.4rem; }
        .srw-repair-custom { display: inline-flex; align-items: center; gap: 0.3rem; flex-wrap: wrap; }
        .srw-repair-choices legend { font-size: 0.8rem; font-weight: 700; color: #3D2518; padding: 0; margin: 0.35rem 0 0.3rem; line-height: 1.4; }
        .srw-repair-preview { margin: 0.45rem 0 0; font-size: 0.76rem; color: #3f6a44; background: #EDF3EC; border: 1px solid #c9dcc6; border-radius: 6px; padding: 0.4rem 0.55rem; }
        .srw-repair-save { margin-top: 0.5rem; width: auto; }
        .srw-repair-card-quick { border-color: #C9DCC6; background: #FBFDF9; }
        .srw-quick-source { margin: 0.55rem 0 0; color: #6b5a50; font-size: 0.76rem; }
        .srw-quick-reading { margin: 0.35rem 0 0; color: #3D2518; font-size: 0.86rem; line-height: 1.45; }
        .srw-quick-actions { display: flex; gap: 0.45rem; align-items: center; margin-top: 0.55rem; }
        .srw-quick-actions .srw-repair-save { margin-top: 0; }

        @media (max-width: 900px) {
          .srw-layout { flex-direction: column; }
          .srw-side { position: static; flex: 1 1 auto; max-width: none; width: 100%; }
          .srw-vendor-planner-head { flex-direction: column; }
          .srw-vendor-planner-actions { width: 100%; justify-content: flex-start; }
          .srw-vendor-groups { grid-template-columns: 1fr; }
          .srw-excluded-ghost { grid-template-columns: auto minmax(0, 1fr); }
          .srw-excluded-ghost .srw-btn { grid-column: 1 / -1; width: 100%; }
          .srw-finish { align-items: stretch; flex-direction: column; }
          .srw-finish .srw-btn-done { width: 100%; }
          /* Three tracks, money still FIXED — one auto track and the phone
             list goes ragged for the same reason the desktop one did. */
          .srw-row { grid-template-columns: minmax(0, 1fr) 6.5rem 18px; gap: 0.3rem 0.6rem; }
          .srw-row-name    { grid-column: 1; grid-row: 1; }
          .srw-row-cost    { grid-column: 2; grid-row: 1; }
          .srw-row-chevron { grid-column: 3; grid-row: 1; }
          .srw-row-vendor  { grid-column: 1; grid-row: 2; }
          .srw-row-fulfill { grid-column: 2 / -1; grid-row: 2; justify-self: end; }
          .srw-row-buy     { grid-column: 1 / -1; grid-row: 3; }
        }
        @media (max-width: 640px) {
          .srw-head { padding: 0.85rem 0.7rem 0.45rem; }
          .srw-toolbar { padding: 0.6rem 0.7rem; }
          .srw-search { flex: 1 0 100%; width: 100%; min-height: 44px; }
          .srw-chip { min-height: 40px; }
          .srw-toolbar-gap { display: none; }
          .srw-toolbar-label { flex: 1 0 100%; margin: 0.25rem 0 0; }
          .srw-group-toggle { width: 100%; }
          .srw-group-toggle .srw-chip { flex: 1 1 50%; }
          .srw-vendor-planner { margin: 0.65rem 0.7rem 0; border-radius: 14px; }
          .srw-vendor-planner-actions .srw-chip { flex: 1 1 auto; }
          .srw-vendor-tabs { grid-template-columns: minmax(0, 1fr); padding: 0.65rem; }
          .srw-constraints { padding: 0.55rem 0.65rem 0; }
          .srw-vendor-toggle { min-height: 44px; padding-inline: 0.8rem; }
          .srw-exclusion-overlay { align-items: flex-end; padding: max(0.5rem, env(safe-area-inset-top, 0px)) max(0.5rem, env(safe-area-inset-right, 0px)) 0 max(0.5rem, env(safe-area-inset-left, 0px)); }
          .srw-exclusion-dialog { width: 100%; max-height: calc(100dvh - max(0.5rem, env(safe-area-inset-top, 0px))); border-radius: 16px 16px 0 0; }
          .srw-exclusion-dialog-head { position: sticky; top: -1.25rem; z-index: 1; margin-inline: -1.35rem; padding: 1rem 1.35rem 0.8rem; background: rgba(255, 253, 249, 0.98); }
          .srw-exclusion-review-toolbar { align-items: stretch; flex-direction: column; }
          .srw-exclusion-review-toolbar .srw-chip { width: 100%; }
          .srw-exclusion-grid { grid-template-columns: 1fr; }
          .srw-exclusion-facts { grid-template-columns: 1fr; }
          .srw-exclusion-choice-row .srw-chip { min-height: 44px; }
          .srw-vendor-money { grid-template-columns: repeat(2, minmax(0, 1fr)); }
          .srw-vendor-money > div:nth-child(3) { border-left: 0; }
          .srw-vendor-money > div:nth-child(n+3) { border-top: 1px solid #EFE6DC; }
          .srw-vendor-resolution, .srw-scenario-breakdown { margin: 0 0.65rem 0.65rem; }
          .srw-scenario-explanation { margin: 0.65rem 0.65rem 0; }
          .srw-vendor-resolution > div { align-items: stretch; flex-direction: column; }
          .srw-scenario-breakdown > div { grid-template-columns: minmax(0, 1fr) 6.5rem; }
          .srw-scenario-breakdown > div > span:nth-child(2) { grid-column: 1 / -1; grid-row: 2; color: #7A6E66; }
          .srw-scenario-breakdown > div > strong { grid-column: 2; grid-row: 1; }
          .srw-scenario-explanation-meta { align-items: flex-start; flex-direction: column; }
          .srw-scenario-unresolved > div { flex-direction: column; }
          .srw-scenario-unresolved .srw-btn { width: 100%; }
          .srw-vendor-planner-impact { padding: 0.65rem; }
          .srw-vendor-planner-impact dl { gap: 0.4rem 0.8rem; }
          .srw-vendor-planner-impact dd { font-size: 0.72rem; }
          .srw-impact-head { flex-direction: column; }
          .srw-impact-status { text-align: left; }
          .srw-note, .srw-preview { margin-left: 0.7rem; margin-right: 0.7rem; }
          .srw-layout { padding: 0.7rem; }
          .srw-finish { margin: 0.25rem 0.7rem 0; }
        }
      `}</style>
    </div>
  );
};

export default SavingsReviewWorksheet;
